import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { apiService } from '../../services/api';
import CustomDateRangePicker from '../Common/CustomDateRangePicker';
import CustomDateTimePicker from '../Common/CustomDateTimePicker';
import AuctionModal from './AuctionModal';
import DocumentEditor from '../Documents/DocumentEditor';
import { generateDocumentTitle, substituteTemplateVariables } from '../../utils/documentUtils';
import { extractMarginsAndBody } from '../Documents/paginationCore';
import { extractTemplateMeta } from '../../utils/templateMeta';
import { exportToDocx } from '../../utils/docxExporter';
import SearchableSelect from '../Common/SearchableSelect';
import DebtorFormModal from '../Common/DebtorFormModal';
import OrganizerFormModal from '../Common/OrganizerFormModal';
import AccountFormModal from '../Common/AccountFormModal';
import UniversalModal from '../Common/UniversalModal';
import Toast from '../Common/Toast';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import { getDocumentVariablesData } from '../../utils/documentVariables';
import { createAllCases } from '../../utils/declension';
import { applyPhoneMask, formatPhone, normalizePhoneDigits } from '../../utils/phoneMask';
import { parseLocalDateTime, formatLocalDate, formatLocalDateTime, formatInputDateTime } from '../../utils/dateTime';
import { addWorkingDays } from '../../utils/productionCalendar';
import ReminderFormModal from '../Reminders/ReminderFormModal';
import { getDebtorDisplayName, getOrganizerDisplayName } from '../../utils/entityDisplayName';
import './Auctions.css';
import '../Common/PrimaryActionButton.css';
import OwnerBadge from '../Common/OwnerBadge';
import { useAuth } from '../../context/AuthContext';
import SharedLotForm from '../Lots/LotForm';

const formatEtpDisplay = (etp) => {
  if (!etp) return '';
  const name = String(etp.name || '').trim();
  const operator = String(etp.operator || '').trim();
  if (!name) return operator;
  return operator ? `${name} (${operator})` : name;
};

const INTEGRATIONS_JOURNAL_COL_WIDTHS_CACHE_KEY = 'aot_integrations_journal_col_widths_v1';
const DEFAULT_INTEGRATIONS_JOURNAL_COL_WIDTHS = {
  started_at: 260,
  status: 140,
  link: 140,
  error: 420,
};

const formatBankAccountDisplay = (account) => {
  if (!account) return '';
  return `${account.bank_name} - ${account.account_number}`;
};

const getAuctionDisplayNumber = (auctionLike) => {
  if (!auctionLike) return '';
  return auctionLike.owner_sequence || auctionLike.auction_owner_sequence || auctionLike.id || auctionLike.auction_id || '';
};

const getAuctionTitleLabel = (auctionLike) => {
  if (!auctionLike) return '';
  const title = String(auctionLike.title || '').trim();
  if (title) return title;
  const auctionNumber = getAuctionDisplayNumber(auctionLike);
  return auctionNumber ? `Торги #${auctionNumber}` : 'Торги';
};

const buildDebtorAuctionTitle = (debtorLike) => {
  if (!debtorLike) return '';
  const debtorType = String(debtorLike?.type || '').trim().toLowerCase();
  const rawName = String(getDebtorDisplayName(debtorLike) || debtorLike?.name || '').trim();
  if (!rawName) return '';

  if (debtorType === 'legal') {
    return `Торги по реализации имущества ${rawName}`;
  }

  if (debtorType === 'entrepreneur') {
    const withoutPrefix = rawName.replace(/^\s*(?:ИП|Индивидуальный\s+предприниматель)\s+/iu, '').trim();
    const baseName = withoutPrefix || rawName;
    const cases = createAllCases(baseName);
    const genitive = String(cases?.r || baseName).trim();
    return `Торги по реализации имущества ИП ${genitive}`;
  }

  const cases = createAllCases(rawName);
  const genitive = String(cases?.r || rawName).trim();
  return `Торги по реализации имущества ${genitive}`;
};

const getApiErrorMessage = (error, fallback = 'Ошибка сохранения данных') => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (!data) {
    const base = error?.message || fallback;
    return status ? `${fallback}: HTTP ${status}. ${base}` : base;
  }
  const withStatus = (message) => status ? `${message} (HTTP ${status})` : message;
  if (typeof data === 'string') {
    const plain = String(data).trim();
    const plainLower = plain.toLowerCase();
    if (plainLower.includes('504 gateway time-out') || plainLower.includes('504 gateway timeout')) {
      return 'Сервер обрабатывает запрос дольше обычного. Проверьте статус в истории через несколько секунд.';
    }
    if (plainLower.includes('<html') && plainLower.includes('<title>')) {
      // Не показываем пользователю сырой HTML страницы ошибки nginx/proxy.
      return withStatus(fallback);
    }
    return withStatus(data);
  }
  if (typeof data === 'object') {
    const accountErrors = data.account_number;
    const accountMessages = Array.isArray(accountErrors) ? accountErrors : (accountErrors ? [accountErrors] : []);
    const hasDuplicateAccountError = accountMessages.some((item) => String(item || '').toLowerCase().includes('уже существует'));
    if (hasDuplicateAccountError) {
      return 'Такой счет уже существует в системе.';
    }
    if (data.detail) return withStatus(String(data.detail));
    if (data.message) return withStatus(String(data.message));
    const flattened = Object.values(data).flat().map((item) => String(item || '').trim()).filter(Boolean);
    if (flattened.length) return withStatus(flattened.join(', '));
  }
  const base = error?.message || fallback;
  return status ? `${fallback}: HTTP ${status}. ${base}` : base;
};

const isGatewayTimeoutError = (error) => {
  const status = Number(error?.response?.status || 0);
  if (status === 502 || status === 503 || status === 504) return true;
  const raw = String(error?.response?.data || error?.message || '').toLowerCase();
  return raw.includes('gateway time-out') || raw.includes('gateway timeout') || raw.includes('504');
};

const truncateForToast = (value, maxLen = 220) => {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '';
  if (normalized.length <= maxLen) return normalized;
  return `${normalized.slice(0, maxLen - 1)}…`;
};

const buildFedresursDiagnosticMessage = (history) => {
  const jobs = Array.isArray(history) ? history : [];
  if (!jobs.length) return '';
  const latestJob = jobs[0] || null;
  if (!latestJob) return '';
  const status = String(latestJob?.status || '').toLowerCase();
  if (['queued', 'running'].includes(status)) return '';
  const steps = Array.isArray(latestJob?.steps) ? latestJob.steps : [];
  const lastStep = steps.length ? steps[steps.length - 1] : null;
  const stepName = truncateForToast(lastStep?.step_name || latestJob?.error_short || '');
  const stepDetails = truncateForToast(lastStep?.details || '', 260);
  if (stepName && stepDetails) {
    return `${stepName}: ${stepDetails}`;
  }
  return stepName || stepDetails || '';
};

const formatPercentForUi = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const source = String(value).trim().replace(',', '.');
  if (!source) return '';
  if (!/^-?\d+(?:\.\d+)?$/.test(source)) return String(value);
  const [intPart, fracPart = ''] = source.split('.');
  const trimmedFrac = fracPart.replace(/0+$/, '');
  return trimmedFrac ? `${intPart},${trimmedFrac}` : intPart;
};

const normalizePercentForApi = (value) => {
  if (value === null || value === undefined) return value;
  const source = String(value).trim();
  if (!source) return '';
  return source.replace(',', '.');
};

const formatDeadlineFromDateTime = (value) => {
  const date = parseLocalDateTime(value);
  if (!date) return '';
  const pad = (v) => String(v).padStart(2, '0');
  return `до ${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const buildDefaultDepositDeadline = (auctionType, applicationsEnd) => {
  const normalizedType = String(auctionType || '').trim();
  if (normalizedType === 'public_offer') {
    return 'не позднее даты и времени окончания периода, на котором претендент подает заявку на участие в торгах';
  }
  if (normalizedType === 'auction' || normalizedType === 'contest') {
    return formatDeadlineFromDateTime(applicationsEnd);
  }
  return '';
};

const resolveDocumentTemplateIncludes = async (html, stack = new Set()) => {
  if (!html) return html;
  const includeRe = /\{\{\s*include_template\s*:\s*([^}]+?)\s*\}\}/gi;
  const ids = new Set();
  const esc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let match;
  while ((match = includeRe.exec(html)) !== null) {
    const raw = String(match[1] || '').trim();
    if (/^\d+$/.test(raw)) ids.add(raw);
  }
  if (ids.size === 0) return html;

  let resolved = html;
  for (const id of ids) {
    const inlineRe = new RegExp(`\\{\\{\\s*include_template\\s*:\\s*${esc(id)}\\s*\\}\\}`, 'gi');
    const paragraphRe = new RegExp(
      `<p\\b[^>]*>(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*(?:<(?:span|strong|b|em|i|u|mark)\\b[^>]*>(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*)*${inlineRe.source}(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*(?:<\\/(?:span|strong|b|em|i|u|mark)>(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*)*<\\/p>`,
      'gi'
    );
    if (stack.has(id)) {
      resolved = resolved.replace(paragraphRe, '');
      resolved = resolved.replace(inlineRe, '');
      continue;
    }
    try {
      const resp = await apiService.getDocumentTemplate(id);
      const tplContent = resp.data?.content || '';
      const nested = await resolveDocumentTemplateIncludes(tplContent, new Set([...stack, id]));
      resolved = resolved.replace(paragraphRe, nested);
      resolved = resolved.replace(inlineRe, nested);
    } catch (err) {
      console.error('Include template load error:', err);
    }
  }
  return resolved;
};

const buildFedresursNoticeHtmlFromTemplate = async ({
  auction,
  debtors,
  organizers,
  lots,
  etps,
  templates,
  variablesData,
}) => {
  let templatesList = Array.isArray(templates) ? templates : [];
  if (templatesList.length === 0) {
    const response = await apiService.getDocumentTemplates();
    templatesList = response.data?.results || [];
  }

  const noticeTemplate =
    templatesList.find((item) => item?.is_standard && String(item?.name || '') === 'Сообщение о торгах')
    || templatesList.find((item) => String(item?.name || '') === 'Сообщение о торгах');

  if (!noticeTemplate?.id) {
    throw new Error('Не найден шаблон «Сообщение о торгах».');
  }

  const fullTemplateResponse = await apiService.getDocumentTemplate(noticeTemplate.id);
  const parsedTemplate = extractTemplateMeta(fullTemplateResponse.data?.content || '');
  const templateContent = parsedTemplate.content || fullTemplateResponse.data?.content || '';
  if (!templateContent) {
    throw new Error('Шаблон «Сообщение о торгах» пуст.');
  }

  const resolvedTemplateContent = await resolveDocumentTemplateIncludes(templateContent);
  return substituteTemplateVariables(resolvedTemplateContent, {
    auction,
    debtors,
    organizers,
    lots,
    etps,
    variablesData,
  });
};

const mapDebtorOption = (debtor) => ({
  ...(debtor || {}),
  display_name: getDebtorDisplayName(debtor) || debtor?.name || '',
});

const mapOrganizerOption = (organizer) => ({
  ...(organizer || {}),
  display_name: getOrganizerDisplayName(organizer) || organizer?.name || '',
});

const parseLotNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const sortLotsByNumber = (lots) => {
  return [...(Array.isArray(lots) ? lots : [])].sort((a, b) => {
    const aNumber = parseLotNumber(a?.lot_number);
    const bNumber = parseLotNumber(b?.lot_number);
    if (aNumber !== null && bNumber !== null && aNumber !== bNumber) return aNumber - bNumber;
    if (aNumber !== null && bNumber === null) return -1;
    if (aNumber === null && bNumber !== null) return 1;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

const normalizeLotsWithFallbackNumbers = (lots) => {
  const sorted = sortLotsByNumber(lots);
  const used = new Set(
    sorted
      .map((lot) => parseLotNumber(lot?.lot_number))
      .filter((value) => value !== null)
  );
  let nextCandidate = 1;
  return sorted.map((lot) => {
    const parsed = parseLotNumber(lot?.lot_number);
    if (parsed !== null) {
      return { ...lot, lot_number: parsed };
    }
    while (used.has(nextCandidate)) {
      nextCandidate += 1;
    }
    used.add(nextCandidate);
    return { ...lot, lot_number: nextCandidate };
  });
};

const createDefaultPublicOfferPeriod = (startDateTime = '') => ({
  start_datetime: startDateTime || '',
  start_datetime_manual: false,
  applications_end_datetime: '',
  period_end_datetime: '',
  period_price: '',
  period_end_custom: false,
  period_end_diff_value: '0',
  period_end_diff_unit: 'days',
  period_end_diff_sign: 'plus',
});

const toInputDateTimeString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return '';

    const dtLocalMatch = raw.match(/^(\d{4,})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (dtLocalMatch) {
      const [, yearRaw, month, day, hour, minute] = dtLocalMatch;
      return `${yearRaw.slice(0, 4)}-${month}-${day}T${hour}:${minute}`;
    }

    const strictIsoLike = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
    const strictRuLike = /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/;
    if (strictIsoLike.test(raw) || strictRuLike.test(raw)) {
      return formatInputDateTime(raw);
    }

    // Не преобразуем частично введенные/нестандартные значения, чтобы не получать артефакты вида 190...
    return raw;
  }
  return formatInputDateTime(value);
};

const sanitizeDateTimeLocalInput = (value, { allowEmpty = true } = {}) => {
  const raw = String(value ?? '').trim();
  if (!raw) return allowEmpty ? '' : '';

  const fullMatch = raw.match(/^(\d{4,})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!fullMatch) {
    return raw;
  }

  const [, yearRaw, month, day, hour, minute] = fullMatch;
  const year = yearRaw.slice(0, 4);
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const calculatePeriodEndWithDiff = (applicationsEndDateTime, period) => {
  if (!applicationsEndDateTime) return '';
  if (!period?.period_end_custom) return applicationsEndDateTime;
  const base = parseLocalDateTime(applicationsEndDateTime);
  if (!base) return applicationsEndDateTime;
  const rawDiff = Number.parseInt(String(period?.period_end_diff_value || '0'), 10);
  const diffValue = Number.isFinite(rawDiff) && rawDiff >= 0 ? rawDiff : 0;
  const sign = String(period?.period_end_diff_sign || 'plus') === 'minus' ? -1 : 1;
  const unit = String(period?.period_end_diff_unit || 'days');

  let deltaMinutes = diffValue;
  if (unit === 'hours') deltaMinutes = diffValue * 60;
  if (unit === 'days') deltaMinutes = diffValue * 60 * 24;

  const result = new Date(base.getTime() + sign * deltaMinutes * 60 * 1000);
  return formatInputDateTime(result);
};

const normalizePublicOfferPeriods = (rawPeriods, applicationsStart = '') => {
  const source = Array.isArray(rawPeriods) ? rawPeriods : [];
  const normalizedSource = source.map((period) => ({
    ...(period || {}),
    start_datetime: toInputDateTimeString(period?.start_datetime),
    start_datetime_manual: !!period?.start_datetime_manual,
    applications_end_datetime: toInputDateTimeString(period?.applications_end_datetime),
    period_end_datetime: toInputDateTimeString(period?.period_end_datetime),
    period_price: String(period?.period_price ?? '').trim(),
    period_end_custom: !!period?.period_end_custom,
    period_end_diff_value: String(period?.period_end_diff_value ?? '0').replace(/\D/g, ''),
    period_end_diff_unit: (() => {
      const unit = String(period?.period_end_diff_unit || '').trim();
      const diff = String(period?.period_end_diff_value ?? '0').replace(/\D/g, '');
      if (!['minutes', 'hours', 'days'].includes(unit)) return 'days';
      // Старые строки со значением по умолчанию 0 минут считаем "не выбранными"
      if (unit === 'minutes' && (!diff || diff === '0')) return 'days';
      return unit;
    })(),
    period_end_diff_sign: String(period?.period_end_diff_sign) === 'minus' ? 'minus' : 'plus',
  }));

  const result = normalizedSource.length > 0
    ? normalizedSource
    : [createDefaultPublicOfferPeriod(toInputDateTimeString(applicationsStart))];

  for (let i = 0; i < result.length; i += 1) {
    const prev = result[i - 1];
    if (i === 0) {
      result[i].start_datetime = toInputDateTimeString(applicationsStart || result[i].start_datetime);
      result[i].start_datetime_manual = true;
    } else if (!result[i].start_datetime) {
      result[i].start_datetime = prev?.period_end_datetime || prev?.applications_end_datetime || '';
      result[i].start_datetime_manual = false;
    }
    if (!result[i].period_end_custom) {
      result[i].period_end_datetime = result[i].applications_end_datetime || '';
    } else if (!result[i].period_end_datetime && result[i].applications_end_datetime) {
      result[i].period_end_datetime = calculatePeriodEndWithDiff(result[i].applications_end_datetime, result[i]);
    }
  }

  return result;
};

const PUBLIC_OFFER_REDUCTION_TYPES = [
  { value: 'prev_minus_percent_of_initial', label: 'Цена на интервале = цена предыдущего интервала − % от начальной цены' },
  { value: 'prev_minus_percent_of_previous', label: 'Цена на интервале = цена предыдущего интервала − % от предыдущей цены' },
  { value: 'prev_minus_rub', label: 'Цена на интервале = снижение в рублях от цены предыдущего интервала' },
  { value: 'percent_of_initial', label: 'Цена на интервале = снижение в процентах от начальной цены' },
];

const PUBLIC_OFFER_DEPOSIT_METHODS = [
  { value: 'percent_of_initial', label: 'Процент от начальной цены лота' },
  { value: 'percent_of_interval', label: 'Процент от цены на интервале' },
  { value: 'fixed_rub', label: 'Фиксированная сумма' },
];

const PUBLIC_OFFER_GENERATION_MODES = [
  { value: 'to_cutoff_price', label: 'До цены отсечения' },
  { value: 'by_count', label: 'В количестве интервалов' },
];

const toTwoDecimals = (value) => {
  const num = Number.parseFloat(String(value ?? '').replace(',', '.'));
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2);
};

const sanitizeDecimalInput = (value, { allowEmpty = true } = {}) => {
  const raw = String(value ?? '').replace(',', '.');
  if (!raw) return allowEmpty ? '' : '0';
  const cleaned = raw.replace(/[^\d.]/g, '');
  const [wholeRaw = '', ...fractionParts] = cleaned.split('.');
  const whole = wholeRaw.replace(/^0+(?=\d)/, '');
  const fraction = fractionParts.join('').slice(0, 2);
  if (cleaned.includes('.')) return `${whole || '0'}.${fraction}`;
  return whole;
};

const isPercentReductionType = (reductionType) => String(reductionType || '') !== 'prev_minus_rub';

const sanitizeReductionInput = (value, reductionType, { allowEmpty = true } = {}) => {
  const sanitized = sanitizeDecimalInput(value, { allowEmpty });
  if (!sanitized) return sanitized;
  if (!isPercentReductionType(reductionType)) return sanitized;

  const hasTrailingDot = String(sanitized).endsWith('.');
  const parsed = Number.parseFloat(String(sanitized).replace(',', '.'));
  if (!Number.isFinite(parsed)) return allowEmpty ? '' : '0';

  const clamped = Math.min(100, Math.max(0, parsed));
  if (hasTrailingDot) {
    return `${Math.trunc(clamped)}.`;
  }

  return sanitizeDecimalInput(String(clamped), { allowEmpty: false });
};

const formatFixedTwoDecimals = (value) => {
  const parsed = Number.parseFloat(String(value ?? '').replace(/\s+/g, '').replace(',', '.'));
  if (!Number.isFinite(parsed)) return '';
  return parsed.toFixed(2);
};

const formatGroupedAmountInput = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const normalized = raw.replace(/\s+/g, '').replace(',', '.');
  const [intPartRaw = '', fracPartRaw = ''] = normalized.split('.');
  const intPart = intPartRaw.replace(/\D/g, '');
  const fracPart = fracPartRaw.replace(/\D/g, '').slice(0, 2);
  if (!intPart && !fracPart) return '';
  const groupedInt = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const hasSeparator = /[.,]/.test(raw);
  return hasSeparator ? `${groupedInt}.${fracPart}` : groupedInt;
};

const getReductionColumnTitle = (reductionType) => {
  switch (reductionType) {
    case 'prev_minus_percent_of_initial':
      return 'Снижение цены предыдущего интервала на % от начальной цены';
    case 'prev_minus_percent_of_previous':
      return 'Снижение цены предыдущего интервала на % от предыдущей цены';
    case 'prev_minus_rub':
      return 'Снижение в рублях от цены предыдущего интервала';
    case 'percent_of_initial':
      return 'Снижение в процентах от начальной цены';
    default:
      return 'Снижение цены';
  }
};

const getReductionValueSuffix = (reductionType) => (
  reductionType === 'prev_minus_rub' ? '₽' : '%'
);

const getDepositValueSuffix = (depositMethod) => (
  depositMethod === 'fixed_rub' ? '₽' : '%'
);

const getDefaultLotPublicOfferSettings = () => ({
  interval_value: '1',
  interval_unit: 'days_working',
  reduction_type: 'prev_minus_percent_of_initial',
  reduction_value: '5.00',
  applications_start_matches_interval: true,
  applications_end_matches_interval: true,
  applications_start_diff_value: '0',
  applications_start_diff_unit: 'hours',
  applications_end_diff_value: '0',
  applications_end_diff_unit: 'hours',
  generation_mode: 'by_count',
  cutoff_price: '',
  intervals_count: '10',
  deposit_method: 'percent_of_initial',
  deposit_value: '10.00',
});

const getDefaultLotPublicOfferRow = (startDate = '', endDate = '', price = '', deposit = '') => ({
  interval_start_datetime: startDate || '',
  applications_start_datetime: startDate || '',
  applications_end_datetime: endDate || '',
  interval_end_datetime: endDate || '',
  reduction_value: '',
  deposit_amount: deposit || '',
  interval_price: price || '',
  comment: '',
});

const normalizeLotPublicOfferSettings = (settings) => {
  const source = (settings && typeof settings === 'object') ? settings : {};
  const defaults = getDefaultLotPublicOfferSettings();
  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(source, key);
  return {
    ...defaults,
    ...source,
    interval_value: hasOwn('interval_value')
      ? String(source.interval_value ?? '').replace(/\D/g, '')
      : defaults.interval_value,
    interval_unit: (() => {
      const rawUnit = String(source.interval_unit ?? (defaults.interval_unit || '')).trim();
      if (rawUnit === 'days') return 'days_calendar'; // legacy value
      if (['days_working', 'days_calendar', 'hours', 'minutes'].includes(rawUnit)) return rawUnit;
      return defaults.interval_unit;
    })(),
    reduction_value: sanitizeDecimalInput(source.reduction_value ?? defaults.reduction_value, { allowEmpty: false }) || defaults.reduction_value,
    cutoff_price: sanitizeDecimalInput(source.cutoff_price ?? defaults.cutoff_price, { allowEmpty: true }),
    intervals_count: String(source.intervals_count ?? defaults.intervals_count).replace(/\D/g, '') || defaults.intervals_count,
    deposit_value: sanitizeDecimalInput(source.deposit_value ?? defaults.deposit_value, { allowEmpty: false }) || defaults.deposit_value,
    applications_start_matches_interval: source.applications_start_matches_interval !== false,
    applications_end_matches_interval: source.applications_end_matches_interval !== false,
    applications_start_diff_value: String(source.applications_start_diff_value ?? defaults.applications_start_diff_value).replace(/\D/g, ''),
    applications_start_diff_unit: ['days', 'hours', 'minutes'].includes(String(source.applications_start_diff_unit || ''))
      ? String(source.applications_start_diff_unit)
      : defaults.applications_start_diff_unit,
    applications_end_diff_value: String(source.applications_end_diff_value ?? defaults.applications_end_diff_value).replace(/\D/g, ''),
    applications_end_diff_unit: ['days', 'hours', 'minutes'].includes(String(source.applications_end_diff_unit || ''))
      ? String(source.applications_end_diff_unit)
      : defaults.applications_end_diff_unit,
    generation_mode: (() => {
      const rawMode = String(source.generation_mode ?? defaults.generation_mode);
      if (rawMode === 'full_period') return 'by_count';
      if (rawMode === 'to_cutoff_price' || rawMode === 'by_count') return rawMode;
      return defaults.generation_mode;
    })(),
  };
};

const normalizeLotPublicOfferPeriods = (rows, fallbackStart = '', ensureOneRow = true) => {
  const source = Array.isArray(rows) ? rows : [];
  const normalized = source
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const start = toInputDateTimeString(row.interval_start_datetime || row.start_datetime || '');
      const applicationsStart = toInputDateTimeString(row.applications_start_datetime || start || '');
      const applicationsEnd = toInputDateTimeString(row.applications_end_datetime || row.applications_end_datetime || '');
      const intervalEnd = toInputDateTimeString(row.interval_end_datetime || row.period_end_datetime || applicationsEnd || '');
      return {
        interval_start_datetime: start,
        applications_start_datetime: applicationsStart,
        applications_end_datetime: applicationsEnd,
        interval_end_datetime: intervalEnd,
        reduction_value: sanitizeDecimalInput(row.reduction_value ?? '', { allowEmpty: true }),
        deposit_amount: sanitizeDecimalInput(row.deposit_amount ?? '', { allowEmpty: true }),
        interval_price: sanitizeDecimalInput(row.interval_price ?? row.period_price ?? '', { allowEmpty: true }),
        comment: String(row.comment ?? ''),
      };
    });

  if (normalized.length > 0 || !ensureOneRow) return normalized;

  const start = toInputDateTimeString(fallbackStart || '');
  return [getDefaultLotPublicOfferRow(start, '', '', '')];
};

const getMinPublicOfferIntervalStartFromLots = (lots) => {
  const sourceLots = Array.isArray(lots) ? lots : [];
  let minDate = null;

  sourceLots.forEach((lot) => {
    const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, '', false);
    rows.forEach((row) => {
      const startDate = parseLocalDateTime(row?.interval_start_datetime || '');
      if (!startDate) return;
      if (!minDate || startDate.getTime() < minDate.getTime()) {
        minDate = startDate;
      }
    });
  });

  return minDate ? formatInputDateTime(minDate) : '';
};

const getLotLastIntervalEndsMap = (lots) => {
  const sourceLots = Array.isArray(lots) ? lots : [];
  const result = {};

  sourceLots.forEach((lot) => {
    const lotId = String(lot?.id ?? '');
    if (!lotId) return;
    const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, '', false);
    const lastRow = rows.length ? rows[rows.length - 1] : null;
    const intervalEnd = String(lastRow?.interval_end_datetime || '');
    const applicationsEnd = String(lastRow?.applications_end_datetime || '');
    result[lotId] = `${intervalEnd}||${applicationsEnd}`;
  });

  return result;
};

const getFirstLotFirstApplicationsStartFromLots = (lots) => {
  const sourceLots = Array.isArray(lots) ? lots : [];
  if (!sourceLots.length) return '';

  const orderedLots = [...sourceLots].sort((a, b) => {
    const aNum = Number.parseInt(String(a?.lot_number ?? ''), 10);
    const bNum = Number.parseInt(String(b?.lot_number ?? ''), 10);
    if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) {
      return aNum - bNum;
    }
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  });

  const firstLot = orderedLots[0];
  if (!firstLot) return '';

  const rows = normalizeLotPublicOfferPeriods(firstLot?.public_offer_periods, '', false);
  if (!rows.length) return '';

  return toInputDateTimeString(rows[0]?.applications_start_datetime || '');
};

const getPublicOfferAutoEndDatesFromLots = (lots) => {
  const sourceLots = Array.isArray(lots) ? lots : [];
  let target = null;

  sourceLots.forEach((lot) => {
    const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, '', false);
    if (!rows.length) return;
    const lastRow = rows[rows.length - 1] || {};
    const lastIntervalEndRaw = toInputDateTimeString(lastRow?.interval_end_datetime || '');
    const lastIntervalEnd = parseLocalDateTime(lastIntervalEndRaw || '');
    if (!lastIntervalEnd) return;

    if (!target || lastIntervalEnd.getTime() > target.intervalEndDate.getTime()) {
      target = {
        intervalEndDate: lastIntervalEnd,
        resultsDatetime: lastIntervalEndRaw,
        applicationsEnd: toInputDateTimeString(lastRow?.applications_end_datetime || ''),
      };
    }
  });

  if (!target) {
    return { resultsDatetime: '', applicationsEnd: '' };
  }

  return {
    resultsDatetime: target.resultsDatetime || '',
    applicationsEnd: target.applicationsEnd || '',
  };
};

const compareInputDateTime = (a, b) => {
  const da = parseLocalDateTime(a);
  const db = parseLocalDateTime(b);
  if (!da || !db) return 0;
  return da.getTime() - db.getTime();
};

const hasPublicOfferDateOrderViolations = (rows) => {
  const source = Array.isArray(rows) ? rows : [];
  for (let i = 0; i < source.length; i += 1) {
    const row = source[i] || {};
    if (row.interval_start_datetime && row.applications_start_datetime && compareInputDateTime(row.interval_start_datetime, row.applications_start_datetime) > 0) {
      return true;
    }
    if (row.applications_end_datetime && row.interval_end_datetime && compareInputDateTime(row.applications_end_datetime, row.interval_end_datetime) > 0) {
      return true;
    }
    if (i < source.length - 1) {
      const next = source[i + 1] || {};
      if (row.interval_end_datetime && next.interval_start_datetime && compareInputDateTime(row.interval_end_datetime, next.interval_start_datetime) > 0) {
        return true;
      }
    }
  }
  return false;
};

const recalculatePublicOfferRows = (rows, settings, changedRowIndex = 0) => {
  const source = Array.isArray(rows) ? rows.map((row) => ({ ...(row || {}) })) : [];
  if (!source.length) return source;
  const changedIndex = Math.max(0, Number(changedRowIndex || 0));
  const startIndex = Math.max(0, changedIndex - 1);
  const toDate = (value) => parseLocalDateTime(value);
  const formatDate = (date) => (date ? formatInputDateTime(date) : '');
  const toMinutesDiff = (fromDate, toDateValue) => {
    if (!fromDate || !toDateValue) return null;
    const ms = toDateValue.getTime() - fromDate.getTime();
    if (!Number.isFinite(ms)) return null;
    return Math.round(ms / (60 * 1000));
  };
  const maxDate = (a, b) => {
    if (!a) return b || null;
    if (!b) return a || null;
    return a.getTime() >= b.getTime() ? a : b;
  };

  // Опорные смещения берем из фактически отредактированного интервала,
  // а не из настроек блока автогенерации.
  const changedRow = source[changedIndex] || {};
  const changedStartDate = toDate(changedRow.interval_start_datetime || '');
  const changedApplicationsStartDate = toDate(changedRow.applications_start_datetime || '');
  const changedApplicationsEndDate = toDate(changedRow.applications_end_datetime || '');
  const changedEndDate = toDate(changedRow.interval_end_datetime || '');
  const changedDurationMs = (
    changedStartDate &&
    changedEndDate &&
    changedEndDate.getTime() >= changedStartDate.getTime()
  ) ? (changedEndDate.getTime() - changedStartDate.getTime()) : null;
  const startOffsetMinutes = toMinutesDiff(changedStartDate, changedApplicationsStartDate);
  const endOffsetMinutes = toMinutesDiff(changedApplicationsEndDate, changedEndDate);

  for (let i = startIndex; i < source.length; i += 1) {
    const row = source[i];
    const prev = i > 0 ? source[i - 1] : null;
    const originalStart = toDate(row.interval_start_datetime || '');
    const originalEnd = toDate(row.interval_end_datetime || '');
    const originalDurationMs = (originalStart && originalEnd && originalEnd.getTime() >= originalStart.getTime())
      ? (originalEnd.getTime() - originalStart.getTime())
      : null;

    if (prev?.interval_end_datetime) {
      // Каскадно "сшиваем" интервалы: старт каждого следующего = окончанию предыдущего.
      row.interval_start_datetime = prev.interval_end_datetime;
    }

    // На измененном интервале оставляем введенные пользователем значения.
    // Для всех следующих интервалов используем смещение, вычисленное из измененного.
    if (i > changedIndex && row.interval_start_datetime) {
      if (startOffsetMinutes !== null) {
        row.applications_start_datetime = addMinutesToInputDate(row.interval_start_datetime, startOffsetMinutes);
      } else if (settings?.applications_start_matches_interval) {
        row.applications_start_datetime = row.interval_start_datetime || '';
      } else if (
        row.interval_start_datetime &&
        row.applications_start_datetime &&
        compareInputDateTime(row.interval_start_datetime, row.applications_start_datetime) > 0
      ) {
        row.applications_start_datetime = row.interval_start_datetime;
      }
    } else if (settings?.applications_start_matches_interval) {
      row.applications_start_datetime = row.interval_start_datetime || '';
    } else if (
      row.interval_start_datetime &&
      row.applications_start_datetime &&
      compareInputDateTime(row.interval_start_datetime, row.applications_start_datetime) > 0
    ) {
      row.applications_start_datetime = row.interval_start_datetime;
    }

    // Интервал не может заканчиваться раньше начала интервала, начала/окончания приема заявок.
    const startDate = toDate(row.interval_start_datetime || '');
    const appStartDate = toDate(row.applications_start_datetime || '');
    const appEndDate = toDate(row.applications_end_datetime || '');
    let minEndDate = maxDate(startDate, appStartDate);
    if (!settings?.applications_end_matches_interval) {
      minEndDate = maxDate(minEndDate, appEndDate);
    }
    let endDate = toDate(row.interval_end_datetime || '');
    // Для всех последующих интервалов каскадно переносим окончание,
    // сохраняя исходную длительность интервала, а не только при конфликте.
    if (i > changedIndex && startDate && changedDurationMs !== null && changedDurationMs >= 0) {
      endDate = new Date(startDate.getTime() + changedDurationMs);
    }
    if (!endDate || (minEndDate && endDate.getTime() < minEndDate.getTime())) {
      if (startDate && originalDurationMs !== null && originalDurationMs > 0) {
        const durationBasedEnd = new Date(startDate.getTime() + originalDurationMs);
        endDate = minEndDate ? maxDate(durationBasedEnd, minEndDate) : durationBasedEnd;
      } else {
        endDate = minEndDate;
      }
    }
    row.interval_end_datetime = formatDate(endDate);

    if (i > changedIndex && row.interval_end_datetime) {
      if (endOffsetMinutes !== null) {
        row.applications_end_datetime = addMinutesToInputDate(row.interval_end_datetime, -endOffsetMinutes);
      } else if (settings?.applications_end_matches_interval) {
        row.applications_end_datetime = row.interval_end_datetime || '';
      }
    } else if (settings?.applications_end_matches_interval) {
      row.applications_end_datetime = row.interval_end_datetime || '';
    }

    // Базовые ограничения всегда должны выполняться.
    if (
      row.applications_end_datetime &&
      row.interval_end_datetime &&
      compareInputDateTime(row.applications_end_datetime, row.interval_end_datetime) > 0
    ) {
      row.applications_end_datetime = row.interval_end_datetime;
    }
    if (
      row.applications_start_datetime &&
      row.applications_end_datetime &&
      compareInputDateTime(row.applications_start_datetime, row.applications_end_datetime) > 0
    ) {
      row.applications_end_datetime = row.applications_start_datetime;
    }
  }

  return source;
};

const addIntervalBySettings = async (dateInput, settings) => {
  const value = Number.parseInt(String(settings?.interval_value || '0'), 10);
  if (!Number.isFinite(value) || value <= 0) return '';

  const unit = String(settings?.interval_unit || 'days_working');
  if (unit === 'days_working') {
    const startDate = parseLocalDateTime(dateInput);
    if (!startDate) return '';
    try {
      const nextDate = await addWorkingDays(startDate, value);
      return nextDate ? formatInputDateTime(nextDate) : '';
    } catch (error) {
      console.warn('Ошибка расчета рабочих дней для генерации этапов:', error);
      return '';
    }
  }

  const minutes = unit === 'minutes' ? value : (unit === 'hours' ? value * 60 : value * 24 * 60);
  return addMinutesToInputDate(dateInput, minutes);
};

const durationMinutesFromValueUnit = (valueRaw, unitRaw) => {
  const value = Number.parseInt(String(valueRaw || '0'), 10);
  if (!Number.isFinite(value) || value <= 0) return 0;
  const unit = String(unitRaw || 'hours');
  if (unit === 'minutes') return value;
  if (unit === 'hours') return value * 60;
  return value * 24 * 60;
};

const addMinutesToInputDate = (dateInput, minutes) => {
  const base = parseLocalDateTime(dateInput);
  if (!base || !Number.isFinite(minutes)) return '';
  const next = new Date(base.getTime() + minutes * 60 * 1000);
  return formatInputDateTime(next);
};

const computeNextPrice = ({ previousPrice, initialPrice, reductionType, reductionValue, index }) => {
  if (!Number.isFinite(previousPrice) || !Number.isFinite(initialPrice)) return null;
  if (!Number.isFinite(reductionValue)) return null;

  if (index === 0) return initialPrice;

  if (reductionType === 'prev_minus_percent_of_previous') {
    return previousPrice - (previousPrice * reductionValue / 100);
  }
  if (reductionType === 'prev_minus_rub') {
    return previousPrice - reductionValue;
  }
  if (reductionType === 'percent_of_initial') {
    return initialPrice * (1 - (reductionValue * index / 100));
  }
  return previousPrice - (initialPrice * reductionValue / 100);
};

const computeDeposit = ({ depositMethod, depositValue, initialPrice, intervalPrice }) => {
  if (!Number.isFinite(depositValue)) return null;
  if (depositMethod === 'fixed_rub') return depositValue;
  if (depositMethod === 'percent_of_interval') return intervalPrice * depositValue / 100;
  return initialPrice * depositValue / 100;
};

const recalculateLotRowsByReductionFromIndex = (rows, settings, initialPrice, fromIndex) => {
  const source = Array.isArray(rows) ? rows.map((r) => ({ ...(r || {}) })) : [];
  const parseDecimal = (value) => {
    const prepared = String(value ?? '').replace(/\s+/g, '').replace(',', '.');
    const parsed = Number.parseFloat(prepared);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const initialParsed = parseDecimal(initialPrice);
  const firstIntervalParsed = parseDecimal(source?.[0]?.interval_price);
  const startPrice = (initialParsed && initialParsed > 0)
    ? initialParsed
    : ((firstIntervalParsed && firstIntervalParsed > 0) ? firstIntervalParsed : null);
  if (!Number.isFinite(startPrice) || startPrice <= 0) return source;

  const safeFrom = Math.max(0, Number.isFinite(Number(fromIndex)) ? Number(fromIndex) : 0);
  if (safeFrom >= source.length) return source;

  const reductionType = String(settings?.reduction_type || 'prev_minus_percent_of_initial');
  const depositMethod = String(settings?.deposit_method || 'percent_of_initial');
  const depositValue = Number.parseFloat(String(settings?.deposit_value || '0').replace(',', '.'));

  let previousPrice = startPrice;
  if (safeFrom > 0) {
    const prevParsed = parseDecimal(source?.[safeFrom - 1]?.interval_price);
    if (Number.isFinite(prevParsed) && prevParsed > 0) {
      previousPrice = prevParsed;
    }
  }

  for (let idx = safeFrom; idx < source.length; idx += 1) {
    const row = source[idx] || {};
    const reductionRaw = Number.parseFloat(String(row?.reduction_value || settings?.reduction_value || '0').replace(',', '.'));
    const reductionValue = Number.isFinite(reductionRaw) ? reductionRaw : 0;
    const nextPrice = computeNextPrice({
      previousPrice,
      initialPrice: startPrice,
      reductionType,
      reductionValue,
      index: idx,
    });
    const safePrice = Number.isFinite(nextPrice) ? Math.max(0, nextPrice) : previousPrice;
    const deposit = computeDeposit({
      depositMethod,
      depositValue,
      initialPrice: startPrice,
      intervalPrice: safePrice,
    });
    source[idx] = {
      ...row,
      interval_price: toTwoDecimals(safePrice),
      deposit_amount: toTwoDecimals(deposit),
    };
    previousPrice = safePrice;
  }

  return source;
};

const AuctionsList = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const [auctions, setAuctions] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [etps, setEtps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Табличный вид: сортировка/фильтры/пагинация храним локально
  const AUCTIONS_TABLE_STATE_KEY = 'aot_auctions_table_state_v1';
  const AUCTIONS_TABLE_WIDTHS_CACHE_KEY = 'aot_auctions_table_widths_cache_v1';
  const AUCTIONS_FILTERS_COLLAPSED_KEY = 'aot_auctions_filters_collapsed_v1';
  const AUCTION_CARD_LOTS_TABLE_WIDTHS_CACHE_KEY = 'aot_auction_card_lots_table_widths_cache_v1';
  const PUBLIC_OFFER_PERIODS_TABLE_WIDTHS_CACHE_KEY = 'aot_public_offer_periods_table_widths_cache_v1';
  const DEFAULT_TABLE_STATE = {
    preset: 'all',
    search: '',
    debtor: '',
    organizer: '',
    debtorTypes: [],
    organizerKinds: [],
    statuses: [],
    auctionTypes: [],
    offerForms: [],
    auctionDateFrom: '',
    auctionDateTo: '',
    applicationsRangeFrom: '',
    applicationsRangeTo: '',
    hasLots: 'any',
    hasDocs: 'any',
    hasAccounts: 'any',
    sortField: 'id',
    sortDir: 'desc',
    pageSize: 24,
    currentPage: 1,
  };
  const loadSavedTableState = () => {
    try {
      const raw = localStorage.getItem(AUCTIONS_TABLE_STATE_KEY);
      if (!raw) return DEFAULT_TABLE_STATE;
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_TABLE_STATE, ...(parsed || {}) };
    } catch (_) {
      return DEFAULT_TABLE_STATE;
    }
  };
  const [tableState, setTableState] = useState(loadSavedTableState);

  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(AUCTIONS_FILTERS_COLLAPSED_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return !!parsed;
    } catch (_) {
      return false;
    }
  });

  const DEFAULT_COLUMN_WIDTHS = useMemo(() => ({
    id: 86,
    status: 120,
    auction_type: 120,
    auction_form: 120,
    price_offer_form: 120,
    debtor_name: 280,
    organizer_name: 280,
    applications_start: 130,
    applications_end: 130,
    results_datetime: 120,
    auction_date: 120,
    total_start_price: 140,
    created_date: 120,
    updated_date: 120,
    actions: 88,
  }), []);

  const readCachedColumnWidths = () => {
    try {
      const raw = localStorage.getItem(AUCTIONS_TABLE_WIDTHS_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  };

  const [columnWidths, setColumnWidths] = useState(readCachedColumnWidths);
  const columnWidthsRef = useRef(columnWidths);
  const resizeStateRef = useRef(null);
  const resizeRafRef = useRef(null);
  const readCachedIntegrationsJournalColWidths = () => {
    try {
      const raw = localStorage.getItem(INTEGRATIONS_JOURNAL_COL_WIDTHS_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  };
  const [integrationsJournalColWidths, setIntegrationsJournalColWidths] = useState(readCachedIntegrationsJournalColWidths);
  const integrationsJournalColWidthsRef = useRef(integrationsJournalColWidths);
  const integrationsJournalResizeStateRef = useRef(null);
  const integrationsJournalResizeRafRef = useRef(null);

  useEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);
  useEffect(() => {
    integrationsJournalColWidthsRef.current = integrationsJournalColWidths;
  }, [integrationsJournalColWidths]);

  const DEFAULT_PUBLIC_OFFER_PERIODS_COLUMN_WIDTHS = useMemo(() => ({
    interval_start_datetime: 190,
    applications_start_datetime: 190,
    applications_end_datetime: 190,
    interval_end_datetime: 190,
    reduction_value: 220,
    deposit_amount: 140,
    interval_price: 140,
    comment: 220,
    actions: 92,
  }), []);

  const readCachedPublicOfferPeriodsColumnWidths = () => {
    try {
      const raw = localStorage.getItem(PUBLIC_OFFER_PERIODS_TABLE_WIDTHS_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  };

  const [publicOfferPeriodsColumnWidths, setPublicOfferPeriodsColumnWidths] = useState(readCachedPublicOfferPeriodsColumnWidths);
  const publicOfferPeriodsColumnWidthsRef = useRef(publicOfferPeriodsColumnWidths);
  const publicOfferPeriodsResizeStateRef = useRef(null);
  const publicOfferPeriodsResizeRafRef = useRef(null);

  useEffect(() => {
    publicOfferPeriodsColumnWidthsRef.current = publicOfferPeriodsColumnWidths;
  }, [publicOfferPeriodsColumnWidths]);

  // Состояние для пагинации
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'create', 'view', 'edit'
    auction: null
  });
  const [modalTitleDraft, setModalTitleDraft] = useState('');
  const modalTitleDraftRef = useRef('');
  const [isModalTitleManuallyEdited, setIsModalTitleManuallyEdited] = useState(false);
  const [auctionFormResetToken, setAuctionFormResetToken] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [lots, setLots] = useState([]);
  const [showLotForm, setShowLotForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [repeatCreatingIds, setRepeatCreatingIds] = useState({});
  const [openAuctionActionsMenuId, setOpenAuctionActionsMenuId] = useState(null);
  const [auctionActionsMenuPos, setAuctionActionsMenuPos] = useState({
    top: 0,
    left: 0,
    maxHeight: 320,
    placement: 'bottom',
  });

  const setModalTitleProgrammatically = useCallback((value) => {
    const next = String(value || '').trim();
    setModalTitleDraft(next);
    modalTitleDraftRef.current = next;
  }, []);

  // Состояния для модального окна документов из карточек
  const [showDocumentsListModal, setShowDocumentsListModal] = useState(false);
  const [documentsForModal, setDocumentsForModal] = useState([]);
  const [currentAuctionForModal, setCurrentAuctionForModal] = useState(null);
  const [lotsForModal, setLotsForModal] = useState([]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event?.target;
      if (!target || !(target instanceof Element)) return;
      if (target.closest('.auction-actions-menu')) return;
      if (target.closest('.auction-actions-dropdown-portal')) return;
      setOpenAuctionActionsMenuId(null);
    };
    const handleEscClose = (event) => {
      if (event?.key === 'Escape') {
        setOpenAuctionActionsMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscClose);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscClose);
    };
  }, []);

  const openAuctionActionsMenu = useCallback((auctionId, triggerElement) => {
    const rect = triggerElement?.getBoundingClientRect?.();
    if (!rect) {
      setOpenAuctionActionsMenuId(auctionId);
      return;
    }
    const MENU_WIDTH = 210;
    const MENU_PREFERRED_HEIGHT = 280;
    const GAP = 6;
    const VIEWPORT_MARGIN = 8;
    const viewportWidth = window.innerWidth || 1280;
    const viewportHeight = window.innerHeight || 720;
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, viewportWidth - MENU_WIDTH - 8));
    const availableBelow = viewportHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
    const availableAbove = rect.top - GAP - VIEWPORT_MARGIN;
    const shouldOpenUp = availableBelow < 180 && availableAbove > availableBelow;
    const placement = shouldOpenUp ? 'top' : 'bottom';
    const maxHeight = Math.max(140, Math.floor((shouldOpenUp ? availableAbove : availableBelow)));
    const top = shouldOpenUp ? (rect.top - GAP) : (rect.bottom + GAP);
    setAuctionActionsMenuPos({ top, left, maxHeight: Math.min(maxHeight, MENU_PREFERRED_HEIGHT), placement });
    setOpenAuctionActionsMenuId(auctionId);
  }, []);

  const openedAuctionForActionsMenu = useMemo(() => {
    if (!openAuctionActionsMenuId) return null;
    return (auctions || []).find((item) => item.id === openAuctionActionsMenuId) || null;
  }, [openAuctionActionsMenuId, auctions]);

  useEffect(() => {
    if (!modalState?.isOpen) {
      setModalTitleProgrammatically('');
      setIsModalTitleManuallyEdited(false);
      return;
    }
    const auction = modalState?.auction;
    if (!auction) {
      const ownAuctions = (auctions || []).filter((a) => {
        const ownerEmail = String(a?.owner_email || '').trim().toLowerCase();
        const currentEmail = String(user?.email || '').trim().toLowerCase();
        return !ownerEmail || !currentEmail || ownerEmail === currentEmail;
      });
      const maxOwnerSeq = ownAuctions.reduce((maxSeq, item) => {
        const n = Number.parseInt(String(item?.owner_sequence || item?.auction_owner_sequence || ''), 10);
        return Number.isFinite(n) ? Math.max(maxSeq, n) : maxSeq;
      }, 0);
      const nextOwnerSeq = maxOwnerSeq + 1;
      setModalTitleProgrammatically(nextOwnerSeq > 0 ? `Торги #${nextOwnerSeq}` : '');
      setIsModalTitleManuallyEdited(false);
      return;
    }
    const initialTitle = String(auction.title || '').trim() || getAuctionTitleLabel(auction);
    setModalTitleProgrammatically(initialTitle);
    setIsModalTitleManuallyEdited(false);
  }, [modalState, auctions, user?.email, setModalTitleProgrammatically]);

  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  // Состояния для модальных окон создания записей
  const [showDebtorForm, setShowDebtorForm] = useState(false);
  const [createdDebtorDraft, setCreatedDebtorDraft] = useState(null);
  const [showOrganizerForm, setShowOrganizerForm] = useState(false);
  const [autoSelectDebtorId, setAutoSelectDebtorId] = useState('');
  const [autoSelectOrganizerId, setAutoSelectOrganizerId] = useState('');
  const [autoSelectEtpId, setAutoSelectEtpId] = useState('');
  const [autoSelectAccount, setAutoSelectAccount] = useState(null);
  const [organizerFormFixedType, setOrganizerFormFixedType] = useState('specialized_org'); // specialized_org | arbitrage_manager
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [createdAccountDraft, setCreatedAccountDraft] = useState(null);
  const [accountOwnerContext, setAccountOwnerContext] = useState({
    debtorId: '',
    organizerId: '',
    etpId: '',
    backfillDebtorToAuction: false,
    backfillOrganizerToAuction: false,
    backfillEtpToAuction: false,
  });
  const [showAccountOrganizerTypeModal, setShowAccountOrganizerTypeModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Состояния для модальных окон редактирования записей
  const [editDebtorModal, setEditDebtorModal] = useState({ isOpen: false, debtor: null });
  const [editOrganizerModal, setEditOrganizerModal] = useState({ isOpen: false, organizer: null, fixedType: null });
  const [editAccountModal, setEditAccountModal] = useState({
    isOpen: false,
    account: null,
    type: null,
    debtorId: '',
    organizerId: '',
    etpId: '',
  });

  const organizersById = useMemo(() => {
    return new Map((organizers || []).map((o) => [o.id, o]));
  }, [organizers]);

  const debtorsById = useMemo(() => {
    return new Map((debtors || []).map((d) => [d.id, d]));
  }, [debtors]);

  // Функция для сброса состояния
  const resetState = () => {
    setModalState({ isOpen: false, mode: null, auction: null });
    setHasUnsavedChanges(false);
    setSelectedAuction(null);
    setLots([]);
    setShowLotForm(false);
    setToast(null);
    setAutoSelectEtpId('');
    setEditDebtorModal({ isOpen: false, debtor: null });
    setEditOrganizerModal({ isOpen: false, organizer: null, fixedType: null });
    setEditAccountModal({ isOpen: false, account: null, type: null, debtorId: '', organizerId: '', etpId: '' });
  };

  const getDebtorLabelForTable = useCallback((auction) => {
    const immediateLabel = String(auction?.debtor_display_name || '').trim();
    if (immediateLabel) return immediateLabel;

    const debtorId = auction?.debtor;
    if (debtorId) {
      const debtor = debtorsById.get(debtorId);
      const debtorDisplayName = getDebtorDisplayName(debtor);
      if (debtorDisplayName) return debtorDisplayName;
    }
    const fallback = String(auction?.debtor_name || '').trim();
    return fallback || '—';
  }, [debtorsById]);

  const getOrganizerLabelForTable = useCallback((auction) => {
    const immediateLabel = String(auction?.organizer_display_name || '').trim();
    if (immediateLabel) {
      if (/\((АУ|ОТ)\)\s*$/u.test(immediateLabel)) return immediateLabel;
      const typeSuffix = auction?.organizer_type === 'arbitrage_manager'
        ? 'АУ'
        : auction?.organizer_type === 'specialized_org'
          ? 'ОТ'
          : null;
      return typeSuffix ? `${immediateLabel} (${typeSuffix})` : immediateLabel;
    }

    const organizerId = auction?.organizer;
    const organizer = organizerId ? organizersById.get(organizerId) : null;
    const computedLabel = getOrganizerDisplayName(organizer);
    const baseLabel = String(computedLabel || auction?.organizer_name || '').trim();
    if (!baseLabel) return '—';
    if (/\((АУ|ОТ)\)\s*$/u.test(baseLabel)) return baseLabel;

    if (!organizerId) return baseLabel;

    const suffix = organizer?.type === 'arbitrage_manager'
      ? 'АУ'
      : organizer?.type === 'specialized_org'
        ? 'ОТ'
        : null;

    return suffix ? `${baseLabel} (${suffix})` : baseLabel;
  }, [organizersById]);

  const effectiveColumnWidths = useMemo(() => {
    return { ...DEFAULT_COLUMN_WIDTHS, ...(columnWidths || {}) };
  }, [DEFAULT_COLUMN_WIDTHS, columnWidths]);

  const persistColumnWidthsCache = useCallback((next) => {
    try {
      localStorage.setItem(AUCTIONS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, [AUCTIONS_TABLE_WIDTHS_CACHE_KEY]);

  const DEFAULT_AUCTION_CARD_LOT_COLUMN_WIDTHS = useMemo(() => ({
    index: 70,
    name: 280,
    description: 320,
    start_price: 170,
    category: 170,
    status: 260,
    actions: 190,
  }), []);

  const readCachedAuctionCardLotColumnWidths = () => {
    try {
      const raw = localStorage.getItem(AUCTION_CARD_LOTS_TABLE_WIDTHS_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  };

  const [auctionCardLotColumnWidths, setAuctionCardLotColumnWidths] = useState(readCachedAuctionCardLotColumnWidths);
  const auctionCardLotColumnWidthsRef = useRef(auctionCardLotColumnWidths);
  const lotResizeStateRef = useRef(null);
  const lotResizeRafRef = useRef(null);

  useEffect(() => {
    auctionCardLotColumnWidthsRef.current = auctionCardLotColumnWidths;
  }, [auctionCardLotColumnWidths]);

  const effectiveAuctionCardLotColumnWidths = useMemo(() => {
    return { ...DEFAULT_AUCTION_CARD_LOT_COLUMN_WIDTHS, ...(auctionCardLotColumnWidths || {}) };
  }, [DEFAULT_AUCTION_CARD_LOT_COLUMN_WIDTHS, auctionCardLotColumnWidths]);

  const effectivePublicOfferPeriodsColumnWidths = useMemo(() => {
    return { ...DEFAULT_PUBLIC_OFFER_PERIODS_COLUMN_WIDTHS, ...(publicOfferPeriodsColumnWidths || {}) };
  }, [DEFAULT_PUBLIC_OFFER_PERIODS_COLUMN_WIDTHS, publicOfferPeriodsColumnWidths]);
  const effectiveIntegrationsJournalColWidths = useMemo(() => {
    return { ...DEFAULT_INTEGRATIONS_JOURNAL_COL_WIDTHS, ...(integrationsJournalColWidths || {}) };
  }, [integrationsJournalColWidths]);

  const persistAuctionCardLotColumnWidthsCache = useCallback((next) => {
    try {
      localStorage.setItem(AUCTION_CARD_LOTS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, [AUCTION_CARD_LOTS_TABLE_WIDTHS_CACHE_KEY]);

  const persistPublicOfferPeriodsColumnWidthsCache = useCallback((next) => {
    try {
      localStorage.setItem(PUBLIC_OFFER_PERIODS_TABLE_WIDTHS_CACHE_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, [PUBLIC_OFFER_PERIODS_TABLE_WIDTHS_CACHE_KEY]);
  const persistIntegrationsJournalColWidthsCache = useCallback((next) => {
    try {
      localStorage.setItem(INTEGRATIONS_JOURNAL_COL_WIDTHS_CACHE_KEY, JSON.stringify(next || {}));
    } catch (_) {}
  }, []);

  const loadSystemUiSettings = useCallback(async () => {
    try {
      const resp = await apiService.getUiSettings();
      const widths = resp?.data?.auctions_table_column_widths;
      if (widths && typeof widths === 'object') {
        setColumnWidths(widths);
        persistColumnWidthsCache(widths);
      }
      const lotWidths = resp?.data?.auction_card_lots_table_column_widths;
      if (lotWidths && typeof lotWidths === 'object') {
        setAuctionCardLotColumnWidths(lotWidths);
        persistAuctionCardLotColumnWidthsCache(lotWidths);
      }
      const publicOfferWidths = resp?.data?.public_offer_periods_table_column_widths;
      if (publicOfferWidths && typeof publicOfferWidths === 'object') {
        setPublicOfferPeriodsColumnWidths(publicOfferWidths);
        persistPublicOfferPeriodsColumnWidthsCache(publicOfferWidths);
      }
      const integrationWidths = resp?.data?.integrations_journal_column_widths;
      if (integrationWidths && typeof integrationWidths === 'object') {
        setIntegrationsJournalColWidths(integrationWidths);
        persistIntegrationsJournalColWidthsCache(integrationWidths);
      }
      const v = Number(resp?.data?.table_page_size);
      if ([12, 24, 48, 96].includes(v)) {
        setTableState((prev) => ({ ...(prev || {}), pageSize: v, currentPage: 1 }));
      }
    } catch (_) {
      // Если сервер недоступен — работаем с кэшем.
    }
  }, [persistAuctionCardLotColumnWidthsCache, persistColumnWidthsCache, persistIntegrationsJournalColWidthsCache, persistPublicOfferPeriodsColumnWidthsCache]);

  const saveSystemUiSettings = useCallback(async (nextWidths) => {
    if (!isAdmin) return;
    try {
      await apiService.saveUiSettings({ auctions_table_column_widths: nextWidths || {} });
    } catch (e) {
      console.error('Не удалось сохранить ширины столбцов:', e);
    }
  }, [isAdmin]);

  const saveSystemAuctionCardLotColumnWidths = useCallback(async (nextWidths) => {
    if (!isAdmin) return;
    try {
      await apiService.saveUiSettings({ auction_card_lots_table_column_widths: nextWidths || {} });
    } catch (e) {
      console.error('Не удалось сохранить ширины столбцов лотов (карточка торга):', e);
    }
  }, [isAdmin]);

  const saveSystemPublicOfferPeriodsColumnWidths = useCallback(async (nextWidths) => {
    if (!isAdmin) return;
    try {
      await apiService.saveUiSettings({ public_offer_periods_table_column_widths: nextWidths || {} });
    } catch (e) {
      console.error('Не удалось сохранить ширины столбцов таблицы периодов публичного предложения:', e);
    }
  }, [isAdmin]);
  const saveSystemIntegrationsJournalColWidths = useCallback(async (nextWidths) => {
    if (!isAdmin) return;
    try {
      await apiService.saveUiSettings({ integrations_journal_column_widths: nextWidths || {} });
    } catch (e) {
      console.error('Не удалось сохранить ширины колонок блоков Федресурс / ЭТП:', e);
    }
  }, [isAdmin]);

  const resetColumnWidths = useCallback(() => {
    const next = {};
    setColumnWidths(next);
    persistColumnWidthsCache(next);
    saveSystemUiSettings(next);
  }, [persistColumnWidthsCache, saveSystemUiSettings]);

  const startResizeColumn = useCallback((e, key) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();

    resizeStateRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(effectiveColumnWidths[key] || 100),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [effectiveColumnWidths, isAdmin]);

  useEffect(() => {
    const onMove = (e) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(60, Math.min(900, state.startWidth + delta));

      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        setColumnWidths((prev) => {
          const next = { ...(prev || {}), [state.key]: Math.round(nextWidth) };
          persistColumnWidthsCache(next);
          return next;
        });
      });
    };

    const onUp = () => {
      const state = resizeStateRef.current;
      if (!state) return;
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Сохраняем итоговые значения в БД только после отпускания мыши
      saveSystemUiSettings({ ...(columnWidthsRef.current || {}) });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [columnWidths, persistColumnWidthsCache, saveSystemUiSettings]);

  const startResizeIntegrationsJournalColumn = useCallback((e, key) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    integrationsJournalResizeStateRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(effectiveIntegrationsJournalColWidths[key] || 100),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [effectiveIntegrationsJournalColWidths, isAdmin]);

  useEffect(() => {
    const onMove = (e) => {
      const state = integrationsJournalResizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(120, Math.min(480, state.startWidth + delta));
      if (integrationsJournalResizeRafRef.current) cancelAnimationFrame(integrationsJournalResizeRafRef.current);
      integrationsJournalResizeRafRef.current = requestAnimationFrame(() => {
        setIntegrationsJournalColWidths((prev) => {
          const next = { ...(prev || {}), [state.key]: Math.round(nextWidth) };
          persistIntegrationsJournalColWidthsCache(next);
          return next;
        });
      });
    };
    const onUp = () => {
      const state = integrationsJournalResizeStateRef.current;
      if (!state) return;
      integrationsJournalResizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      saveSystemIntegrationsJournalColWidths({ ...(integrationsJournalColWidthsRef.current || {}) });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [persistIntegrationsJournalColWidthsCache, saveSystemIntegrationsJournalColWidths]);

  const startResizeAuctionCardLotColumn = useCallback((e, key) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();

    lotResizeStateRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(effectiveAuctionCardLotColumnWidths[key] || 100),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [effectiveAuctionCardLotColumnWidths, isAdmin]);

  useEffect(() => {
    const onMove = (e) => {
      const state = lotResizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(60, Math.min(900, state.startWidth + delta));

      if (lotResizeRafRef.current) cancelAnimationFrame(lotResizeRafRef.current);
      lotResizeRafRef.current = requestAnimationFrame(() => {
        setAuctionCardLotColumnWidths((prev) => {
          const next = { ...(prev || {}), [state.key]: Math.round(nextWidth) };
          persistAuctionCardLotColumnWidthsCache(next);
          return next;
        });
      });
    };

    const onUp = () => {
      const state = lotResizeStateRef.current;
      if (!state) return;
      lotResizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      saveSystemAuctionCardLotColumnWidths({ ...(auctionCardLotColumnWidthsRef.current || {}) });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [persistAuctionCardLotColumnWidthsCache, saveSystemAuctionCardLotColumnWidths]);

  const startResizePublicOfferPeriodsColumn = useCallback((e, key) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();

    publicOfferPeriodsResizeStateRef.current = {
      key,
      startX: e.clientX,
      startWidth: Number(effectivePublicOfferPeriodsColumnWidths[key] || 120),
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [effectivePublicOfferPeriodsColumnWidths, isAdmin]);

  useEffect(() => {
    const onMove = (e) => {
      const state = publicOfferPeriodsResizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(80, Math.min(900, state.startWidth + delta));

      if (publicOfferPeriodsResizeRafRef.current) cancelAnimationFrame(publicOfferPeriodsResizeRafRef.current);
      publicOfferPeriodsResizeRafRef.current = requestAnimationFrame(() => {
        setPublicOfferPeriodsColumnWidths((prev) => {
          const next = { ...(prev || {}), [state.key]: Math.round(nextWidth) };
          persistPublicOfferPeriodsColumnWidthsCache(next);
          return next;
        });
      });
    };

    const onUp = () => {
      const state = publicOfferPeriodsResizeStateRef.current;
      if (!state) return;
      publicOfferPeriodsResizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      saveSystemPublicOfferPeriodsColumnWidths({ ...(publicOfferPeriodsColumnWidthsRef.current || {}) });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [persistPublicOfferPeriodsColumnWidthsCache, saveSystemPublicOfferPeriodsColumnWidths]);

  // Обработчики для редактирования записей
  const onEditDebtor = (debtorId) => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (debtor) {
      setEditDebtorModal({ isOpen: true, debtor });
    }
  };

  const onEditOrganizer = (organizerId) => {
    const organizer = organizers.find((o) => String(o?.id) === String(organizerId));
    if (organizer) {
      setEditOrganizerModal({ isOpen: true, organizer, fixedType: organizer?.type || null });
    }
  };

  const onEditAccount = (accountId, type, context = '') => {
    if (!accountId) return;
    const account = bankAccounts.find(a => a.id === accountId);
    if (account) {
      const nextDebtorId =
        context && typeof context === 'object'
          ? (context?.debtorId || '')
          : (context || '');
      const nextOrganizerId = context && typeof context === 'object' ? (context?.organizerId || '') : '';
      const nextEtpId = context && typeof context === 'object' ? (context?.etpId || '') : '';
      setEditAccountModal({
        isOpen: true,
        account,
        type,
        debtorId: nextDebtorId,
        organizerId: nextOrganizerId,
        etpId: nextEtpId,
      });
    }
  };

  const handleAccountOwnerCreate = (ownerType) => {
    if (ownerType === 'debtor') {
      setCreatedDebtorDraft(null);
      setShowDebtorForm(true);
      return;
    }
    if (ownerType === 'organizer') {
      setShowAccountOrganizerTypeModal(true);
    }
  };

  const handleAccountOwnerEdit = (ownerType, ownerId) => {
    if (ownerType === 'debtor') {
      onEditDebtor(ownerId);
    } else if (ownerType === 'organizer') {
      onEditOrganizer(ownerId);
    }
  };

  useEffect(() => {
    // Сбрасываем состояние при первой загрузке компонента
    resetState();
    loadData();
    loadSystemUiSettings();
  }, []);

  // Сбрасываем состояние при переходе на страницу торгов
  useEffect(() => {
    resetState();
  }, [location.pathname]);

  // Обрабатываем открытие модального окна создания торгов отдельно
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setModalState({ isOpen: true, mode: 'create', auction: null });
      // Очищаем state, чтобы модальное окно не открывалось повторно при обновлении
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.openCreateModal]);

  // Обработчик кастомного события для сброса состояния при повторном клике на "Торги"
  useEffect(() => {
    const handleResetState = () => {
      resetState();
    };

    window.addEventListener('resetAuctionsState', handleResetState);

    return () => {
      window.removeEventListener('resetAuctionsState', handleResetState);
    };
  }, []);

  const fetchAllAuctions = async () => {
    const all = [];
    let page = 1;
    const pageSize = 100;
    while (page <= 500) {
      try {
        const resp = await apiService.getAuctions(`?page=${page}&page_size=${pageSize}`);
        const data = resp?.data;
        const chunk = Array.isArray(data?.results)
          ? data.results
          : (Array.isArray(data) ? data : []);
        all.push(...chunk);
        const hasNext = Boolean(data?.next);
        if (!hasNext) break;
        page += 1;
      } catch (error) {
        // Не валим полностью раздел "Торги", если упала одна из страниц пагинации.
        if (all.length > 0) {
          console.error(`Error loading auctions page ${page}, using partial result:`, error);
          break;
        }
        throw error;
      }
    }
    return all;
  };

  const fetchAllEtps = async () => {
    const all = [];
    let page = 1;
    while (true) {
      const resp = await apiService.getEtps(`?page=${page}`);
      const chunk = resp.data.results || [];
      all.push(...chunk);
      if (!resp.data.next) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  };

  const fetchAllDebtors = async () => {
    const all = [];
    let page = 1;
    while (true) {
      const resp = await apiService.getDebtors(`?page=${page}&page_size=200`);
      const chunk = resp.data?.results || [];
      all.push(...chunk);
      if (!resp.data?.next) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  };

  const fetchAllOrganizers = async () => {
    const all = [];
    let page = 1;
    while (true) {
      const resp = await apiService.getOrganizers(`?page=${page}&page_size=200`);
      const chunk = resp.data?.results || [];
      all.push(...chunk);
      if (!resp.data?.next) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  };

  const loadAuxiliaryDictionaries = async () => {
    const [debtorsData, organizersData, bankAccountsResponse, etpsData] = await Promise.all([
      fetchAllDebtors(),
      fetchAllOrganizers(),
      apiService.getBankAccountsForAuctions(),
      fetchAllEtps(),
    ]);

    setDebtors((debtorsData || []).map(mapDebtorOption));
    setOrganizers((organizersData || []).map(mapOrganizerOption));
    setBankAccounts(bankAccountsResponse.data || []);
    setEtps(etpsData || []);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const auctionsData = await fetchAllAuctions();
      setAuctions(auctionsData || []);
      setError('');
      setLoading(false);

      // Догружаем справочники асинхронно: список торгов отображаем сразу.
      loadAuxiliaryDictionaries().catch((error) => {
        console.error('Error loading auxiliary dictionaries:', error);
      });
    } catch (error) {
      const detailed = getApiErrorMessage(error, 'Ошибка загрузки данных');
      setError(detailed);
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Функции для пагинации
  const handlePageChange = (page) => {
    setTableState(prev => ({ ...prev, currentPage: page }));
  };

  const prepareAuctionPayload = (formValues) => {
    const payload = { ...formValues };
    payload.title = String(payload.title || '').trim();
    payload.efrsb_message_id = String(payload.efrsb_message_id || '').trim();
    payload.inspection_phone = String(payload.inspection_phone || '').trim();
    payload.inspection_email = String(payload.inspection_email || '').trim();
    payload.is_repeat_trade = !!payload.is_repeat_trade;
    delete payload.auction_start_datetime;
    payload.auction_step = normalizePercentForApi(payload.auction_step);
    payload.deposit_amount = normalizePercentForApi(payload.deposit_amount);

    const nullableFields = [
      'applications_start',
      'applications_end',
      'results_datetime',
      'auction_date',
      'auction_step',
      'contest_step',
      'price_decrease_rules',
      'contest_conditions',
      'deposit_amount',
      'deposit_deadline',
      'deposit_account',
      'payment_account',
      'etp',
      'efrsb_message_id',
      'inspection_phone',
      'inspection_email',
    ];

    nullableFields.forEach((field) => {
      if (payload[field] === '') {
        payload[field] = null;
      }
    });

    payload.public_offer_periods = [];

    return payload;
  };

  const buildLotPublicOfferPayload = (lot) => {
    const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, '', false);
    return {
      public_offer_settings: normalizeLotPublicOfferSettings(lot?.public_offer_settings),
      public_offer_periods: rows,
      public_offer_periods_filled_once: !!lot?.public_offer_periods_filled_once
        || rows.some((row) => !!String(row?.interval_price || row?.comment || row?.applications_end_datetime || '').trim()),
    };
  };

  const handleCreateAuction = async (formData, lots = {}, options = {}) => {
    const { exit = false } = options;
    try {
      setError('');
      const payload = prepareAuctionPayload(formData);
      const response = await apiService.createAuction(payload);
      const newAuction = {
        ...(response.data || {}),
        title: String(payload.title || response.data?.title || '').trim(),
      };

      const { tempLots = [], attachedLots = [] } = lots;
      let totalLots = 0;

      if (tempLots.length > 0) {
        for (const tempLot of tempLots) {
          try {
            const lotData = {
              lot_number: parseLotNumber(tempLot.lot_number),
              name: tempLot.name,
              description: tempLot.description,
              start_price: tempLot.start_price,
              cadastral_number: tempLot.cadastral_number,
              location: tempLot.location,
              category: tempLot.category,
              efrsb_classifiers: Array.isArray(tempLot.efrsb_classifiers)
                ? tempLot.efrsb_classifiers.map((id) => Number(id)).filter((id) => Number.isFinite(id))
                : [],
              auction: newAuction.id,
              ...buildLotPublicOfferPayload(tempLot),
            };
            await apiService.createLot(lotData);
            totalLots++;
          } catch (lotError) {
            console.error('Error creating lot:', lotError);
          }
        }
      }

      if (attachedLots.length > 0) {
        for (const attachedLot of attachedLots) {
          try {
            const requestData = { lot_id: attachedLot.id };
            await apiService.addLotToAuction(newAuction.id, requestData);
            await apiService.patchLot(attachedLot.id, {
              ...buildLotPublicOfferPayload(attachedLot),
              auction: newAuction.id,
            });
            totalLots++;
          } catch (lotError) {
            console.error('Error attaching lot:', lotError);
          }
        }
      }

      const auctionsData = await fetchAllAuctions();
      setAuctions(auctionsData || []);

      setHasUnsavedChanges(false);

      const lotMessage = totalLots > 0 ? ` и ${totalLots} лот(ов)` : '';
      showToast(`Торги${lotMessage} успешно созданы`, 'success');

      if (exit) {
        setModalState({ isOpen: false, mode: null, auction: null });
      } else {
        setModalState(prev => ({
          ...prev,
          isOpen: true,
          mode: 'edit',
          auction: {
            ...(prev.auction || {}),
            ...payload,
            ...newAuction,
          },
        }));
      }

      return newAuction;
    } catch (error) {
      setError('Ошибка создания торгов');
      console.error('Error creating auction:', error);
      const data = error?.response?.data;
      let message = error?.message || 'Неизвестная ошибка';
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (typeof data === 'object') {
          const entries = Object.entries(data);
          if (entries.length) {
            const [key, value] = entries[0];
            if (Array.isArray(value) && value.length) {
              message = `${key}: ${value[0]}`;
            } else if (typeof value === 'string') {
              message = `${key}: ${value}`;
            } else {
              message = `${key}: ${JSON.stringify(value)}`;
            }
          } else {
            message = JSON.stringify(data);
          }
        }
      }
      const organizerRequiredError =
        message === 'organizer: Это поле не может быть пустым.'
        || message === 'organizer: This field may not be null.'
        || message === 'organizer: This field is required.';
      if (organizerRequiredError) {
        message = 'поле Организатор торгов не может быть пустым.';
      }
      showToast(`Ошибка при создании торгов: ${message}`, 'error');
      return false;
    }
  };

  const handleUpdateAuction = async (auctionId, formData, lots = {}, options = {}) => {
    const { exit = false } = options;
    try {
      setError('');
      const payload = prepareAuctionPayload(formData);
      const response = await apiService.updateAuction(auctionId, payload);
      let updatedAuction = {
        ...(response.data || {}),
        title: String(payload.title || response.data?.title || '').trim(),
        inspection_phone: String(
          (response.data?.inspection_phone !== undefined ? response.data.inspection_phone : payload.inspection_phone) || ''
        ),
        inspection_email: String(
          (response.data?.inspection_email !== undefined ? response.data.inspection_email : payload.inspection_email) || ''
        ),
      };
      try {
        const freshResponse = await apiService.getAuction(auctionId);
        if (freshResponse?.data) {
          updatedAuction = {
            ...freshResponse.data,
            title: String(freshResponse.data?.title || '').trim(),
            inspection_phone: String(
              (freshResponse.data?.inspection_phone !== undefined ? freshResponse.data.inspection_phone : payload.inspection_phone) || ''
            ),
            inspection_email: String(
              (freshResponse.data?.inspection_email !== undefined ? freshResponse.data.inspection_email : payload.inspection_email) || ''
            ),
          };
        }
      } catch (freshError) {
        console.error('Error loading fresh auction after update:', freshError);
      }

      const allLots = Array.isArray(lots?.lots) ? lots.lots : [];
      let existingLotIds = null;
      try {
        const lotsResponse = await apiService.getAuctionLots(auctionId);
        const persistedLots = Array.isArray(lotsResponse?.data) ? lotsResponse.data : [];
        existingLotIds = new Set(
          persistedLots
            .map((item) => Number(item?.id))
            .filter((id) => Number.isFinite(id))
        );
      } catch (lotsLoadError) {
        console.warn('Не удалось получить актуальный список лотов перед синхронизацией периодов:', lotsLoadError);
      }
      let lotSyncErrors = 0;
      for (const lot of allLots) {
        if (!lot?.id || lot?.isTemporary) continue;
        const lotId = Number(lot.id);
        if (existingLotIds && !existingLotIds.has(lotId)) {
          continue;
        }
        try {
          await apiService.patchLot(lot.id, {
            ...buildLotPublicOfferPayload(lot),
            auction: auctionId,
          });
        } catch (lotError) {
          if (Number(lotError?.response?.status) === 404) {
            continue;
          }
          console.error('Error updating lot public offer settings:', lotError);
          lotSyncErrors += 1;
        }
      }
      if (lotSyncErrors > 0) {
        throw new Error(`Не удалось сохранить таблицы периодов для ${lotSyncErrors} лот(ов).`);
      }

      const updatedDebtor = updatedAuction.debtor_details
        || (debtors || []).find((d) => String(d?.id) === String(updatedAuction.debtor || payload.debtor));
      const updatedOrganizer = updatedAuction.organizer_details
        || (organizers || []).find((o) => String(o?.id) === String(updatedAuction.organizer || payload.organizer));
      updatedAuction = {
        ...updatedAuction,
        debtor_display_name: getDebtorDisplayName(updatedDebtor) || updatedAuction.debtor_name || '',
        organizer_display_name: getOrganizerDisplayName(updatedOrganizer) || updatedAuction.organizer_name || '',
        organizer_type: updatedOrganizer?.type || updatedAuction.organizer_type,
      };

      // Не дергаем loadData(), чтобы не было "мигания" карточки из-за loading=true
      setAuctions(prev => (prev || []).map(a => (a.id === auctionId ? { ...a, ...updatedAuction } : a)));

      setHasUnsavedChanges(false);
      showToast('Изменения сохранены', 'success');

      if (exit) {
        setModalState({ isOpen: false, mode: null, auction: null });
      } else {
        setModalState(prev => ({
          ...prev,
          isOpen: true,
          mode: 'edit',
          auction: {
            ...(prev.auction || {}),
            ...payload,
            ...updatedAuction,
            id: auctionId,
          },
        }));
      }

      return updatedAuction;
    } catch (error) {
      setError('Ошибка обновления торгов');
      console.error('Error updating auction:', error);
      const data = error?.response?.data;
      let message = error?.message || 'Неизвестная ошибка';
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (typeof data === 'object') {
          const entries = Object.entries(data);
          if (entries.length) {
            const [key, value] = entries[0];
            if (Array.isArray(value) && value.length) {
              message = `${key}: ${value[0]}`;
            } else if (typeof value === 'string') {
              message = `${key}: ${value}`;
            } else {
              message = `${key}: ${JSON.stringify(value)}`;
            }
          } else {
            message = JSON.stringify(data);
          }
        }
      }
      showToast(`Ошибка при обновлении торгов: ${message}`, 'error');
      return false;
    }
  };

  const handleViewAuction = (auction) => {
    const openView = async () => {
      let modalAuction = auction;
      if (auction?.id) {
        try {
          const response = await apiService.getAuction(auction.id);
          if (response?.data) {
            modalAuction = response.data;
            setAuctions((prev) => (prev || []).map((a) => (a.id === auction.id ? { ...a, ...response.data } : a)));
          }
        } catch (error) {
          console.error('Error loading auction details:', error);
        }
        try {
          const lotsResp = await apiService.getAuctionLots(auction.id);
          const prefetchedLots = Array.isArray(lotsResp?.data) ? lotsResp.data : [];
          modalAuction = { ...(modalAuction || {}), lots_prefetched: prefetchedLots };
          if (
            String(modalAuction?.auction_type || '') === 'public_offer'
            && !String(modalAuction?.applications_start || '').trim()
          ) {
            const firstLotStart = getFirstLotFirstApplicationsStartFromLots(prefetchedLots);
            if (firstLotStart) {
              modalAuction = { ...modalAuction, applications_start: firstLotStart };
            }
          }
        } catch (error) {
          console.error('Error preloading auction lots for view:', error);
        }
      }
      setModalState({ isOpen: true, mode: 'view', auction: modalAuction });
    };
    openView();
  };

  const handleEditAuction = () => {
    setModalState((prev) => ({ ...prev, mode: 'edit' }));
  };

  const handleEditAuctionFromList = (auction) => {
    const openEdit = async () => {
      let modalAuction = auction;
      if (auction?.id) {
        try {
          const response = await apiService.getAuction(auction.id);
          if (response?.data) {
            modalAuction = response.data;
            setAuctions((prev) => (prev || []).map((a) => (a.id === auction.id ? { ...a, ...response.data } : a)));
          }
        } catch (error) {
          console.error('Error loading auction details for edit:', error);
        }
        try {
          const lotsResp = await apiService.getAuctionLots(auction.id);
          const prefetchedLots = Array.isArray(lotsResp?.data) ? lotsResp.data : [];
          modalAuction = { ...(modalAuction || {}), lots_prefetched: prefetchedLots };
          if (
            String(modalAuction?.auction_type || '') === 'public_offer'
            && !String(modalAuction?.applications_start || '').trim()
          ) {
            const firstLotStart = getFirstLotFirstApplicationsStartFromLots(prefetchedLots);
            if (firstLotStart) {
              modalAuction = { ...modalAuction, applications_start: firstLotStart };
            }
          }
        } catch (error) {
          console.error('Error preloading auction lots for edit:', error);
        }
      }
      setModalState({ isOpen: true, mode: 'edit', auction: modalAuction });
    };
    openEdit();
  };

  const handleCreateRepeatAuction = async (auction) => {
    if (!auction?.id || repeatCreatingIds[auction.id]) return;
    setRepeatCreatingIds((prev) => ({ ...(prev || {}), [auction.id]: true }));
    let createdAuction = null;
    let response = null;
    try {
      response = await apiService.createRepeatAuction(auction.id);
      createdAuction = response?.data?.auction || response?.data || null;
      if (!createdAuction?.id) {
        throw new Error('Сервер не вернул созданные торги.');
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Не удалось создать повторные торги'), 'error');
      setRepeatCreatingIds((prev) => {
        const next = { ...(prev || {}) };
        delete next[auction.id];
        return next;
      });
      return;
    }

    let modalAuction = createdAuction;
    try {
      const lotsResp = await apiService.getAuctionLots(createdAuction.id);
      modalAuction = {
        ...modalAuction,
        lots_prefetched: Array.isArray(lotsResp?.data) ? lotsResp.data : [],
      };
    } catch (error) {
      console.error('Error preloading repeated auction lots:', error);
    }

    try {
      const auctionsData = await fetchAllAuctions();
      setAuctions(auctionsData || []);
    } catch (error) {
      console.error('Error refreshing auctions list after repeat creation:', error);
    }

    setModalState({ isOpen: true, mode: 'edit', auction: modalAuction });
    setAuctionFormResetToken((prev) => prev + 1);

    const changes = Array.isArray(response?.data?.changes) ? response.data.changes : [];
    const message = [
      `Созданы повторные торги #${getAuctionDisplayNumber(createdAuction) || createdAuction.id}.`,
      ...changes,
    ].join(' ');
    showToast(message, 'info', { sticky: true });

    setRepeatCreatingIds((prev) => {
      const next = { ...(prev || {}) };
      delete next[auction.id];
      return next;
    });
  };

  const handleSwitchToViewMode = ({ resetForm = false } = {}) => {
    setModalState((prev) => ({ ...prev, mode: 'view' }));
    setHasUnsavedChanges(false);
    if (resetForm) {
      setAuctionFormResetToken((prev) => prev + 1);
    }
  };

  const handleCancelModal = async (options = {}) => {
    const source = options?.source || '';
    const shouldSwitchToViewOnDiscard =
      source === 'escape' &&
      modalState.mode === 'edit' &&
      !!modalState.auction?.id;

    const closeModal = () => {
      const auctionId = modalState.auction?.id;
      setModalState({ isOpen: false, mode: null, auction: null });
      setHasUnsavedChanges(false);

      // Обновляем данные торгов в фоне, чтобы закрытие модалки было мгновенным.
      if (auctionId) {
        (async () => {
          try {
            const auctionsData = await fetchAllAuctions();
            setAuctions(auctionsData || []);
          } catch (error) {
            console.error('Error refreshing auctions list on close:', error);
          }
        })();
      }
    };

    if (hasUnsavedChanges && modalState.mode !== 'view') {
      showConfirmDialog(
        'Несохраненные изменения',
        'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно? Все изменения будут потеряны.',
        shouldSwitchToViewOnDiscard
          ? () => handleSwitchToViewMode({ resetForm: true })
          : closeModal,
        'Закрыть без сохранения'
      );
    } else {
      if (shouldSwitchToViewOnDiscard) {
        handleSwitchToViewMode({ resetForm: true });
      } else {
        closeModal();
      }
    }
  };

  const handleDeleteAuction = async (auctionId) => {
    const auction = auctions.find(a => a.id === auctionId);
    if (!auction) return;

    showConfirmDialog(
      'Подтверждение удаления',
      `Вы уверены, что хотите удалить торги ${auction.debtor_name}? Это действие необратимо.`,
      async () => {
        try {
          await apiService.deleteAuction(auctionId);
          const nextAuctions = auctions.filter(auction => auction.id !== auctionId);
          setAuctions(nextAuctions);
          showToast('Торги успешно удалены', 'success');
        } catch (error) {
          setError('Ошибка удаления торгов');
          console.error('Error deleting auction:', error);
          showToast('Ошибка при удалении торгов', 'error');
        }
      }
    );
  };

  const handleAuctionUpdate = async (auctionId) => {
    try {
      // Перезагружаем весь список торгов чтобы получить актуальную статистику
      const auctionsData = await fetchAllAuctions();
      setAuctions(auctionsData || []);

    } catch (error) {
      console.error('Error updating auctions data:', error);
    }
  };

  // Вынесем handleAttachExistingLot в основной компонент
  const handleAttachExistingLot = async (lotId, auctionData, lots, setLots, loadAuctionLots, showToast, setShowAttachLotModal, allLots) => {
    try {
      // Находим лот из списка всех лотов
      const lotToAttach = allLots.find(lot => lot.id === lotId);
      if (!lotToAttach) {
        showToast('Лот не найден', 'error');
        return;
      }

      if (auctionData?.id) {
        // Торг сохранен - прикрепляем лот на сервере
        const requestData = { lot_id: lotId };
        await apiService.addLotToAuction(auctionData.id, requestData);
        await apiService.patchLot(lotId, {
          public_offer_settings: normalizeLotPublicOfferSettings(lotToAttach?.public_offer_settings),
          public_offer_periods: normalizeLotPublicOfferPeriods(lotToAttach?.public_offer_periods, '', false),
          public_offer_periods_filled_once: !!lotToAttach?.public_offer_periods_filled_once,
          auction: auctionData.id,
        });
        await loadAuctionLots(auctionData.id); // Перезагружаем лоты
        // Обновляем статистику в карточках торгов
        await handleAuctionUpdate(auctionData.id);
        showToast('Лот успешно прикреплен к торгам');
      } else {
        // Торг не сохранен - добавляем лот во временное хранилище
        // Проверяем, не прикреплен ли уже этот лот
        if (lots.find(lot => lot.id === lotId)) {
          showToast('Этот лот уже добавлен к торгам', 'error');
          return;
        }
        // Создаем копию лота с пометкой что он временный
        const tempLot = {
          ...lotToAttach,
          isTemporary: true,
          public_offer_settings: normalizeLotPublicOfferSettings(lotToAttach?.public_offer_settings),
          public_offer_periods: normalizeLotPublicOfferPeriods(lotToAttach?.public_offer_periods, '', false),
          public_offer_periods_filled_once: !!lotToAttach?.public_offer_periods_filled_once,
        };
        setLots(normalizeLotsWithFallbackNumbers([...(lots || []), tempLot]));
        showToast('Лот добавлен к торгам (будет сохранен вместе с торгами)');
      }
      setShowAttachLotModal(false);
    } catch (error) {
      console.error('Error attaching lot:', error);
      const data = error.response?.data;
      let message = data?.detail || error.message;
      if (data && typeof data === 'object') {
        const firstEntry = Object.entries(data)[0];
        if (firstEntry) {
          const [, value] = firstEntry;
          message = Array.isArray(value) ? value.join(', ') : String(value || message);
        }
      }
      showToast(`Ошибка прикрепления лота: ${message}`, 'error');
    }
  };

  const loadAuctionLots = async (auctionId) => {
    try {
      const response = await apiService.getAuctionLots(auctionId);
      setLots(normalizeLotsWithFallbackNumbers(response.data || []));
      setSelectedAuction(auctionId);
    } catch (error) {
      setError('Ошибка загрузки лотов');
      console.error('Error loading lots:', error);
    }
  };

  const handleAddLot = useCallback(async (lotData) => {
    try {
      const response = await apiService.addLotToAuction(selectedAuction, lotData);
      setLots(normalizeLotsWithFallbackNumbers([...(lots || []), response.data]));
      setShowLotForm(false);
    } catch (error) {
      setError('Ошибка добавления лота');
      console.error('Error adding lot:', error);
    }
  }, [selectedAuction, lots]);


  const formatDate = (dateString) => {
    return formatLocalDateTime(dateString);
  };

  const showToast = (message, type = 'success', options = {}) => {
    setToast({ message, type, sticky: !!options?.sticky });
    if (options?.sticky) {
      return;
    }
    setTimeout(() => setToast(null), 3000); // Убираем через 3 секунды
  };

  const loadDocumentsForCard = async (auctionId) => {
    try {
      const [documentsResponse, lotsResponse] = await Promise.all([
        apiService.getDocuments({ auction_id: auctionId }),
        apiService.getAuctionLots(auctionId)
      ]);

      setDocumentsForModal(documentsResponse.data.results || []);
      setLotsForModal(Array.isArray(lotsResponse.data) ? lotsResponse.data : (lotsResponse.data.results || []));

      // Находим текущие торги для передачи в просмотр документов
      const currentAuction = auctions.find(a => a.id === auctionId);
      setCurrentAuctionForModal(currentAuction);

      setShowDocumentsListModal(true);
    } catch (error) {
      console.error('Error loading documents:', error);
      setError('Ошибка загрузки документов');
    }
  };

  // Функции для управления диалогом подтверждения
  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Отмена'
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  const getStatusText = (status) => {
    const texts = {
      draft: 'Черновик',
      pending: 'Ожидает',
      applications: 'Прием заявок',
      applications_closed: 'Прием заявок закрыт',
      in_progress: 'В процессе',
      completed: 'Завершены',
      cancelled: 'Отменены',
      failed: 'Не состоялись',
    };
    return texts[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: '#6c757d',
      pending: '#ffc107',
      applications: '#17a2b8',
      applications_closed: '#fd7e14',
      in_progress: '#007bff',
      completed: '#28a745',
      cancelled: '#dc3545',
      failed: '#6f42c1',
    };
    return colors[status] || '#6c757d';
  };

  const getAuctionTypeText = (type) => {
    const types = {
      auction: 'Аукцион',
      contest: 'Конкурс',
      public_offer: 'Публичное предложение',
    };
    return types[type] || type;
  };

  const getOfferFormText = (form) => {
    const forms = { open: 'Открытая', closed: 'Закрытая' };
    return forms[form] || form;
  };

  const updateTableState = (patch, { resetPage = true } = {}) => {
    setTableState(prev => ({
      ...prev,
      ...patch,
      ...(resetPage ? { currentPage: 1 } : null),
    }));
  };

  const formatDateInput = (value) => {
    if (!value) return '';
    const raw = String(value);
    if (raw.includes('.') || raw.includes('/')) return raw;
    const formatted = formatLocalDate(raw);
    return formatted || raw;
  };

  useEffect(() => {
    try {
      localStorage.setItem(AUCTIONS_FILTERS_COLLAPSED_KEY, JSON.stringify(filtersCollapsed));
    } catch (_) { }
  }, [filtersCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(AUCTIONS_TABLE_STATE_KEY, JSON.stringify(tableState));
    } catch (_) { }
  }, [tableState]);

  const parseDateSafe = (value) => {
    return parseLocalDateTime(value);
  };
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  const applyPreset = (presetId) => {
    const now = new Date();
    const startWeek = (() => {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      return startOfDay(d);
    })();
    const endWeek = (() => {
      const d = new Date(startWeek);
      d.setDate(d.getDate() + 6);
      return endOfDay(d);
    })();

    if (presetId === 'all') {
      setTableState(prev => ({
        ...DEFAULT_TABLE_STATE,
        sortField: prev.sortField,
        sortDir: prev.sortDir,
        pageSize: prev.pageSize,
      }));
      return;
    }
    if (presetId === 'draft') {
      setTableState(prev => ({ ...prev, preset: 'draft', statuses: ['draft'], currentPage: 1 }));
      return;
    }
    if (presetId === 'active') {
      setTableState(prev => ({
        ...prev,
        preset: 'active',
        statuses: ['pending', 'applications', 'applications_closed', 'in_progress'],
        currentPage: 1
      }));
      return;
    }
    if (presetId === 'this_week') {
      setTableState(prev => ({
        ...prev,
        preset: 'this_week',
        applicationsRangeFrom: startWeek.toISOString().slice(0, 10),
        applicationsRangeTo: endWeek.toISOString().slice(0, 10),
        currentPage: 1
      }));
      return;
    }
    if (presetId === 'applications_now') {
      setTableState(prev => ({ ...prev, preset: 'applications_now', statuses: ['applications'], currentPage: 1 }));
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (String(tableState.search || '').trim()) count += 1;
    if (tableState.debtor) count += 1;
    if (tableState.organizer) count += 1;
    if (Array.isArray(tableState.debtorTypes) && tableState.debtorTypes.length) count += 1;
    if (Array.isArray(tableState.organizerKinds) && tableState.organizerKinds.length) count += 1;
    if (Array.isArray(tableState.statuses) && tableState.statuses.length) count += 1;
    if (Array.isArray(tableState.auctionTypes) && tableState.auctionTypes.length) count += 1;
    if (Array.isArray(tableState.offerForms) && tableState.offerForms.length) count += 1;
    if (tableState.auctionDateFrom || tableState.auctionDateTo) count += 1;
    if (tableState.applicationsRangeFrom || tableState.applicationsRangeTo) count += 1;
    if (tableState.hasLots && tableState.hasLots !== 'any') count += 1;
    if (tableState.hasDocs && tableState.hasDocs !== 'any') count += 1;
    if (tableState.hasAccounts && tableState.hasAccounts !== 'any') count += 1;
    return count;
  }, [
    tableState.search,
    tableState.debtor,
    tableState.organizer,
    tableState.debtorTypes,
    tableState.organizerKinds,
    tableState.statuses,
    tableState.auctionTypes,
    tableState.offerForms,
    tableState.auctionDateFrom,
    tableState.auctionDateTo,
    tableState.applicationsRangeFrom,
    tableState.applicationsRangeTo,
    tableState.hasLots,
    tableState.hasDocs,
    tableState.hasAccounts,
  ]);

  const filteredSortedAuctions = useMemo(() => {
    const st = tableState || DEFAULT_TABLE_STATE;

    const hasLotsCheck = (a) => Number(a?.lots_count || 0) > 0;
    const hasDocsCheck = (a) => Number(a?.documents_count || 0) > 0;
    const hasAccountsCheck = (a) => !!a?.deposit_account_details || !!a?.payment_account_details || !!a?.deposit_account || !!a?.payment_account;

    const search = (st.search || '').trim().toLowerCase();
    const debtorId = st.debtor ? String(st.debtor) : '';
    const organizerId = st.organizer ? String(st.organizer) : '';
    const debtorTypes = Array.isArray(st.debtorTypes) ? st.debtorTypes : [];
    const organizerKinds = Array.isArray(st.organizerKinds) ? st.organizerKinds : [];
    const statuses = Array.isArray(st.statuses) ? st.statuses : [];
    const auctionTypes = Array.isArray(st.auctionTypes) ? st.auctionTypes : [];
    const offerForms = Array.isArray(st.offerForms) ? st.offerForms : [];

    const auctionFrom = st.auctionDateFrom ? startOfDay(new Date(st.auctionDateFrom)) : null;
    const auctionTo = st.auctionDateTo ? endOfDay(new Date(st.auctionDateTo)) : null;
    const appsFrom = st.applicationsRangeFrom ? startOfDay(new Date(st.applicationsRangeFrom)) : null;
    const appsTo = st.applicationsRangeTo ? endOfDay(new Date(st.applicationsRangeTo)) : null;

    const filtered = (auctions || []).filter((a) => {
      if (search) {
        const hay = `${a.id} ${getAuctionDisplayNumber(a)} ${a.debtor_name || ''} ${a.organizer_name || ''}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      if (debtorId && String(a.debtor) !== debtorId) return false;
      if (organizerId && String(a.organizer) !== organizerId) return false;
      if (debtorTypes.length > 0) {
        const debtor = debtorsById.get(a.debtor);
        const t = String(debtor?.type || '');
        if (!t || !debtorTypes.includes(t)) return false;
      }
      if (organizerKinds.length > 0) {
        const organizer = organizersById.get(a.organizer);
        const t = String(organizer?.type || '');
        if (!t || !organizerKinds.includes(t)) return false;
      }
      if (statuses.length > 0 && !statuses.includes(a.status)) return false;
      if (auctionTypes.length > 0 && !auctionTypes.includes(a.auction_type)) return false;
      if (offerForms.length > 0 && !offerForms.includes(a.price_offer_form)) return false;

      if (st.hasLots === 'yes' && !hasLotsCheck(a)) return false;
      if (st.hasLots === 'no' && hasLotsCheck(a)) return false;
      if (st.hasDocs === 'yes' && !hasDocsCheck(a)) return false;
      if (st.hasDocs === 'no' && hasDocsCheck(a)) return false;
      if (st.hasAccounts === 'yes' && !hasAccountsCheck(a)) return false;
      if (st.hasAccounts === 'no' && hasAccountsCheck(a)) return false;

      if (auctionFrom || auctionTo) {
        const d = parseDateSafe(a.auction_date);
        if (!d) return false;
        if (auctionFrom && d < auctionFrom) return false;
        if (auctionTo && d > auctionTo) return false;
      }

      if (appsFrom || appsTo) {
        const start = parseDateSafe(a.applications_start);
        const end = parseDateSafe(a.applications_end) || start;
        if (!start) return false;
        const rangeStart = start;
        const rangeEnd = end || start;
        const from = appsFrom || rangeStart;
        const to = appsTo || rangeEnd;
        const overlaps = rangeStart <= to && rangeEnd >= from;
        if (!overlaps) return false;
      }

      return true;
    });

    const dirMul = st.sortDir === 'asc' ? 1 : -1;
    const getSortVal = (a) => {
      switch (st.sortField) {
        case 'id': return Number(a.id || 0);
        case 'status': return String(getStatusText(a.status) || '');
        case 'auction_type': return String(getAuctionTypeText(a.auction_type) || '');
        case 'auction_form': return String(getOfferFormText(a.auction_form) || '');
        case 'price_offer_form': return String(getOfferFormText(a.price_offer_form) || '');
        case 'debtor_name': return String(a.debtor_name || '');
        case 'organizer_name': return String(a.organizer_name || '');
        case 'auction_date': return parseDateSafe(a.auction_date)?.getTime() || 0;
        case 'applications_start': return parseDateSafe(a.applications_start)?.getTime() || 0;
        case 'applications_end': return parseDateSafe(a.applications_end)?.getTime() || 0;
        case 'results_datetime': return parseDateSafe(a.results_datetime)?.getTime() || 0;
        case 'total_start_price': return Number(a.total_start_price || 0);
        case 'lots_count': return Number(a.lots_count || 0);
        case 'documents_count': return Number(a.documents_count || 0);
        case 'created_date': return parseDateSafe(a.created_date)?.getTime() || 0;
        case 'updated_date': return parseDateSafe(a.updated_date)?.getTime() || 0;
        default: return String(a?.[st.sortField] ?? '');
      }
    };

    return [...filtered].sort((a, b) => {
      const av = getSortVal(a);
      const bv = getSortVal(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dirMul;
      return String(av).localeCompare(String(bv), 'ru', { sensitivity: 'base' }) * dirMul;
    });
  }, [auctions, tableState, debtorsById, organizersById]);

  const pageSize = Number(tableState.pageSize) || 20;
  const totalCount = filteredSortedAuctions.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(Math.max(1, Number(tableState.currentPage) || 1), totalPages);
  const pagedAuctions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSortedAuctions.slice(start, start + pageSize);
  }, [filteredSortedAuctions, currentPage, pageSize]);

  useEffect(() => {
    setPagination({
      currentPage,
      totalPages,
      totalCount,
      hasNext: currentPage < totalPages,
      hasPrevious: currentPage > 1
    });
    if (currentPage !== tableState.currentPage) {
      setTableState(prev => ({ ...prev, currentPage }));
    }
  }, [currentPage, totalPages, totalCount]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Загрузка торгов...</div>
      </div>
    );
  }

  return (
    <div className="auctions-container">
      <div className="page-header">
        <h2>⚖️ Управление торгами</h2>
        <button
          className="primary-create-button"
          onClick={() => setModalState({ isOpen: true, mode: 'create', auction: null })}
        >
          <span className="primary-create-button__icon">+</span>
          Создать торги
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Модальное окно для работы с торгами */}
      <AuctionModal
        isOpen={modalState.isOpen}
        onClose={handleCancelModal}
        auction={modalState.auction}
        debtors={debtors}
        organizers={organizers}
        isViewMode={modalState.mode === 'view'}
        onEdit={handleEditAuction}
        onSwitchToViewMode={handleSwitchToViewMode}
        hasUnsavedChanges={hasUnsavedChanges}
        titleValue={modalTitleDraft}
        onTitleInput={(value) => {
          const nextTitle = String(value || '');
          modalTitleDraftRef.current = nextTitle;
          setModalTitleDraft(nextTitle);
          setIsModalTitleManuallyEdited(true);
          if (modalState.mode === 'edit') {
            setHasUnsavedChanges((prev) => (prev ? prev : true));
          }
        }}
        isTitleEditable={modalState.mode !== 'view'}
      >
        <AuctionForm
          auction={modalState.auction}
          forceResetToken={auctionFormResetToken}
          debtors={debtors}
          organizers={organizers}
          etps={etps}
          bankAccounts={bankAccounts}
          isAdmin={isAdmin}
          user={user}
          lotColWidths={effectiveAuctionCardLotColumnWidths}
          onStartResizeLotCol={startResizeAuctionCardLotColumn}
          publicOfferColWidths={effectivePublicOfferPeriodsColumnWidths}
          onStartResizePublicOfferCol={startResizePublicOfferPeriodsColumn}
          integrationsJournalColWidths={effectiveIntegrationsJournalColWidths}
          onStartResizeIntegrationsJournalCol={startResizeIntegrationsJournalColumn}
          onSave={(formData, lots, options) => {
            const effectiveTitle = String(modalTitleDraftRef.current || modalTitleDraft || formData?.title || '').trim();
            const mergedFormData = {
              ...(formData || {}),
              title: effectiveTitle,
            };
            if (modalState.auction) {
              return handleUpdateAuction(modalState.auction.id, mergedFormData, lots, options);
            }
            return handleCreateAuction(mergedFormData, lots, options);
          }}
          onCancel={handleCancelModal}
          isViewMode={modalState.mode === 'view'}
          // Обработчики для создания новых записей
          onCreateDebtor={() => {
            setCreatedDebtorDraft(null);
            setShowDebtorForm(true);
          }}
          onCreateOrganizer={(type) => {
            setOrganizerFormFixedType(type || 'specialized_org');
            setShowOrganizerForm(true);
          }}
          onCreateAccount={(payload = {}) => {
            const nextPurpose = payload?.purpose || 'deposit';
            const nextDebtorId = payload?.debtorId || '';
            const nextOrganizerId = payload?.organizerId || '';
            const nextEtpId = payload?.etpId || '';
            setAccountOwnerContext({
              debtorId: nextDebtorId,
              organizerId: nextOrganizerId,
              etpId: nextEtpId,
              backfillDebtorToAuction: !nextDebtorId,
              backfillOrganizerToAuction: !nextOrganizerId,
              backfillEtpToAuction: !nextEtpId,
            });
            setCreatedAccountDraft({
              purpose: nextPurpose,
              owner_type: nextPurpose === 'payment' ? 'debtor' : 'platform',
              owner_id: nextPurpose === 'payment' ? nextDebtorId : (nextEtpId || '')
            });
            setShowAccountForm(true);
          }}
          // Обработчики для редактирования записей
          onEditDebtor={onEditDebtor}
          onEditOrganizer={onEditOrganizer}
          onEditAccount={onEditAccount}
          onEdit={handleEditAuction}
          onFormChange={setHasUnsavedChanges}
          onAuctionUpdate={handleAuctionUpdate}
          onAttachExistingLot={handleAttachExistingLot}
          autoSelectDebtorId={autoSelectDebtorId}
          autoSelectOrganizerId={autoSelectOrganizerId}
          autoSelectEtpId={autoSelectEtpId}
          onAutoSelectDebtorApplied={() => setAutoSelectDebtorId('')}
          onAutoSelectOrganizerApplied={() => setAutoSelectOrganizerId('')}
          onAutoSelectEtpApplied={() => setAutoSelectEtpId('')}
          autoSelectAccount={autoSelectAccount}
          onAutoSelectAccountApplied={() => setAutoSelectAccount(null)}
          externalTitleValue={modalTitleDraft}
          onDebtorAutoTitle={(debtorId) => {
            if (modalState?.auction?.id) return;
            if (isModalTitleManuallyEdited) return;
            const nextDebtor = (debtors || []).find((d) => String(d?.id) === String(debtorId || ''));
            const debtorBasedTitle = buildDebtorAuctionTitle(nextDebtor);
            if (debtorBasedTitle) {
              setModalTitleProgrammatically(debtorBasedTitle);
              return;
            }
            if (!modalState?.auction) {
              const ownAuctions = (auctions || []).filter((a) => {
                const ownerEmail = String(a?.owner_email || '').trim().toLowerCase();
                const currentEmail = String(user?.email || '').trim().toLowerCase();
                return !ownerEmail || !currentEmail || ownerEmail === currentEmail;
              });
              const maxOwnerSeq = ownAuctions.reduce((maxSeq, item) => {
                const n = Number.parseInt(String(item?.owner_sequence || item?.auction_owner_sequence || ''), 10);
                return Number.isFinite(n) ? Math.max(maxSeq, n) : maxSeq;
              }, 0);
              const nextOwnerSeq = maxOwnerSeq + 1;
              setModalTitleProgrammatically(nextOwnerSeq > 0 ? `Торги #${nextOwnerSeq}` : '');
            }
          }}
        />
      </AuctionModal>

      <div className="auctions-list">
        <div className="auctions-table-presets">
          <button type="button" className={`preset-btn ${tableState.preset === 'all' ? 'active' : ''}`} onClick={() => applyPreset('all')}>Все</button>
          <button type="button" className={`preset-btn ${tableState.preset === 'active' ? 'active' : ''}`} onClick={() => applyPreset('active')}>Активные</button>
          <button type="button" className={`preset-btn ${tableState.preset === 'draft' ? 'active' : ''}`} onClick={() => applyPreset('draft')}>Черновики</button>
          <button type="button" className={`preset-btn ${tableState.preset === 'this_week' ? 'active' : ''}`} onClick={() => applyPreset('this_week')}>Эта неделя</button>
          <button type="button" className={`preset-btn ${tableState.preset === 'applications_now' ? 'active' : ''}`} onClick={() => applyPreset('applications_now')}>Прием заявок</button>
          <div className="auctions-table-presets__right">
            <button
              type="button"
              className={`preset-btn preset-btn--filters ${activeFiltersCount > 0 ? 'preset-btn--filters-active' : ''}`}
              onClick={() => setFiltersCollapsed(v => !v)}
              aria-expanded={!filtersCollapsed}
              title={filtersCollapsed ? 'Показать фильтры' : 'Скрыть фильтры'}
            >
              Фильтры{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
            </button>
            <button type="button" className="preset-btn preset-btn--reset" onClick={() => applyPreset('all')}>Сбросить</button>
            {!!isAdmin && (
              <button type="button" className="preset-btn preset-btn--ui" onClick={resetColumnWidths} title="Сбросить ширины столбцов (системная настройка)">
                Сбросить ширины
              </button>
            )}
          </div>
        </div>

        <div className={`auctions-table-filters ${filtersCollapsed ? 'auctions-table-filters--collapsed' : ''}`}>
          <input
            type="text"
            value={tableState.search}
            onChange={(e) => updateTableState({ search: e.target.value, preset: 'custom' })}
            placeholder="Поиск: №, должник, организатор..."
            className="filter-input"
          />

          <div className="filter-select">
            <SearchableSelect
              value={tableState.debtor}
              onChange={(value) => updateTableState({ debtor: value, preset: 'custom' })}
              options={debtors}
              placeholder="Должник"
              searchPlaceholder="Поиск должника..."
              displayKey="name"
              valueKey="id"
              disabled={false}
              required={false}
            />
          </div>

          <div className="filter-select">
            <SearchableSelect
              value={tableState.organizer}
              onChange={(value) => updateTableState({ organizer: value, preset: 'custom' })}
              options={organizers}
              placeholder="Организатор"
              searchPlaceholder="Поиск организатора..."
              displayKey="name"
              valueKey="id"
              disabled={false}
              required={false}
            />
          </div>

          <select
            multiple
            value={tableState.statuses}
            onChange={(e) => updateTableState({ statuses: Array.from(e.target.selectedOptions).map(o => o.value), preset: 'custom' })}
            className="filter-multi"
            title="Статусы (можно выбрать несколько)"
          >
            <option value="draft">Черновик</option>
            <option value="pending">Ожидает</option>
            <option value="applications">Прием заявок</option>
            <option value="applications_closed">Прием заявок закрыт</option>
            <option value="in_progress">В процессе</option>
            <option value="completed">Завершены</option>
            <option value="cancelled">Отменены</option>
            <option value="failed">Не состоялись</option>
          </select>

          <select
            multiple
            value={tableState.debtorTypes}
            onChange={(e) => updateTableState({ debtorTypes: Array.from(e.target.selectedOptions).map(o => o.value), preset: 'custom' })}
            className="filter-multi"
            title="Тип должника (можно выбрать несколько)"
          >
            <option value="legal">Должник: ЮЛ</option>
            <option value="entrepreneur">Должник: ИП</option>
            <option value="individual">Должник: ФЛ</option>
          </select>

          <select
            multiple
            value={tableState.organizerKinds}
            onChange={(e) => updateTableState({ organizerKinds: Array.from(e.target.selectedOptions).map(o => o.value), preset: 'custom' })}
            className="filter-multi"
            title="Категория организатора (можно выбрать несколько)"
          >
            <option value="specialized_org">Организатор: ОТ</option>
            <option value="arbitrage_manager">Организатор: АУ</option>
          </select>

          <select
            multiple
            value={tableState.auctionTypes}
            onChange={(e) => updateTableState({ auctionTypes: Array.from(e.target.selectedOptions).map(o => o.value), preset: 'custom' })}
            className="filter-multi"
            title="Тип торгов (можно выбрать несколько)"
          >
            <option value="auction">Аукцион</option>
            <option value="contest">Конкурс</option>
            <option value="public_offer">Публичное предложение</option>
          </select>

          <select
            multiple
            value={tableState.offerForms}
            onChange={(e) => updateTableState({ offerForms: Array.from(e.target.selectedOptions).map(o => o.value), preset: 'custom' })}
            className="filter-multi"
            title="Форма (можно выбрать несколько)"
          >
            <option value="open">Открытая</option>
            <option value="closed">Закрытая</option>
          </select>

          <div className="filter-dates">
            <div className="filter-date-group">
              <span className="filter-date-label">Дата торгов,</span>
              <span className="filter-date-sub">от</span>
              <input
                type="text"
                value={formatDateInput(tableState.auctionDateFrom)}
                onChange={(e) => updateTableState({ auctionDateFrom: e.target.value, preset: 'custom' })}
                className="filter-date"
                placeholder="ДД.ММ.ГГГГ"
              />
              <span className="filter-date-sub">до</span>
              <input
                type="text"
                value={formatDateInput(tableState.auctionDateTo)}
                onChange={(e) => updateTableState({ auctionDateTo: e.target.value, preset: 'custom' })}
                className="filter-date"
                placeholder="ДД.ММ.ГГГГ"
              />
            </div>
            <div className="filter-date-group">
              <span className="filter-date-label">Прием заявок,</span>
              <span className="filter-date-sub">от</span>
              <input
                type="text"
                value={formatDateInput(tableState.applicationsRangeFrom)}
                onChange={(e) => updateTableState({ applicationsRangeFrom: e.target.value, preset: 'custom' })}
                className="filter-date"
                placeholder="ДД.ММ.ГГГГ"
              />
              <span className="filter-date-sub">до</span>
              <input
                type="text"
                value={formatDateInput(tableState.applicationsRangeTo)}
                onChange={(e) => updateTableState({ applicationsRangeTo: e.target.value, preset: 'custom' })}
                className="filter-date"
                placeholder="ДД.ММ.ГГГГ"
              />
            </div>
          </div>

          <select value={tableState.hasLots} onChange={(e) => updateTableState({ hasLots: e.target.value, preset: 'custom' })} className="filter-select-simple">
            <option value="any">Лоты: любые</option>
            <option value="yes">Лоты: есть</option>
            <option value="no">Лоты: нет</option>
          </select>
          <select value={tableState.hasDocs} onChange={(e) => updateTableState({ hasDocs: e.target.value, preset: 'custom' })} className="filter-select-simple">
            <option value="any">Документы: любые</option>
            <option value="yes">Документы: есть</option>
            <option value="no">Документы: нет</option>
          </select>
          <select value={tableState.hasAccounts} onChange={(e) => updateTableState({ hasAccounts: e.target.value, preset: 'custom' })} className="filter-select-simple">
            <option value="any">Счета: любые</option>
            <option value="yes">Счета: есть</option>
            <option value="no">Счета: нет</option>
          </select>

          <select
            value={String(tableState.pageSize)}
            onChange={(e) => {
              const v = Number(e.target.value) || 24;
              updateTableState({ pageSize: v }, { resetPage: true });
              apiService.saveUiSettings({ table_page_size: v }).catch(() => {});
            }}
            className="filter-select-simple"
            title="Строк на странице"
          >
            <option value="12">12/стр</option>
            <option value="24">24/стр</option>
            <option value="48">48/стр</option>
            <option value="96">96/стр</option>
          </select>
        </div>

        <div className="auctions-table-wrapper auctions-table-wrapper--v11">
          <div className="auctions-table-scroll">
          <table className="auctions-table auctions-table--v11" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: effectiveColumnWidths.id }} />
              <col style={{ width: effectiveColumnWidths.status }} />
              <col style={{ width: effectiveColumnWidths.auction_form }} />
              <col style={{ width: effectiveColumnWidths.auction_type }} />
              <col style={{ width: effectiveColumnWidths.price_offer_form }} />
              <col style={{ width: effectiveColumnWidths.debtor_name }} />
              <col style={{ width: effectiveColumnWidths.organizer_name }} />
              <col style={{ width: effectiveColumnWidths.applications_start }} />
              <col style={{ width: effectiveColumnWidths.applications_end }} />
              <col style={{ width: effectiveColumnWidths.results_datetime }} />
              <col style={{ width: effectiveColumnWidths.auction_date }} />
              <col style={{ width: effectiveColumnWidths.total_start_price }} />
              <col style={{ width: effectiveColumnWidths.created_date }} />
              <col style={{ width: effectiveColumnWidths.actions }} />
            </colgroup>
            <thead>
              <tr>
                {[
                  { key: 'id', label: '№' },
                  { key: 'status', label: 'Статус' },
                  { key: 'auction_form', label: 'Форма торгов' },
                  { key: 'auction_type', label: 'Тип торгов' },
                  { key: 'price_offer_form', label: 'Форма подачи' },
                  { key: 'debtor_name', label: 'Должник' },
                  { key: 'organizer_name', label: 'Организатор' },
                  { key: 'applications_start', label: 'Старт заявок' },
                  { key: 'applications_end', label: 'Конец заявок' },
                  { key: 'results_datetime', label: 'Итоги' },
                  { key: 'auction_date', label: 'Дата торгов' },
                  { key: 'total_start_price', label: 'Цена' },
                  { key: 'created_date', label: 'Создано' },
                ].map((col) => (
                  <th
                    key={col.key}
                    className={`sortable ${tableState.sortField === col.key ? `sort-${tableState.sortDir}` : ''}`}
                    onClick={() => {
                      if (tableState.sortField === col.key) {
                        updateTableState({ sortDir: tableState.sortDir === 'desc' ? 'asc' : 'desc' }, { resetPage: false });
                      } else {
                        updateTableState({ sortField: col.key, sortDir: 'desc' }, { resetPage: false });
                      }
                    }}
                    title="Сортировать"
                  >
                    <span className="th-label">{col.label}</span>
                    {!!isAdmin && (
                      <span
                        className="th-resize-handle"
                        role="presentation"
                        onMouseDown={(e) => startResizeColumn(e, col.key)}
                        onClick={(e) => e.stopPropagation()}
                        title="Изменить ширину столбца"
                      />
                    )}
                  </th>
                ))}
                <th className="table-actions">
                  <span className="th-label">Действия</span>
                  {!!isAdmin && (
                    <span
                      className="th-resize-handle"
                      role="presentation"
                      onMouseDown={(e) => startResizeColumn(e, 'actions')}
                      onClick={(e) => e.stopPropagation()}
                      title="Изменить ширину столбца"
                    />
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedAuctions.length === 0 ? (
                <tr>
                  <td colSpan={14} style={{ padding: 24, textAlign: 'center', color: '#6c757d' }}>
                    Торги не найдены по текущим фильтрам. Нажмите «Сбросить» или измените параметры.
                  </td>
                </tr>
              ) : (
                pagedAuctions.map((auction) => (
                  <tr key={auction.id} className="auction-row auction-row--v11" onClick={() => handleViewAuction(auction)}>
                    <td className="cell-id">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {!!isAdmin && (
                          <>
                            <span>ID {auction.id}</span>
                            <span style={{ fontSize: 11, color: '#6c757d' }}>
                              № {getAuctionDisplayNumber(auction)}
                            </span>
                          </>
                        )}
                        {!isAdmin && <span>{getAuctionDisplayNumber(auction)}</span>}
                        <OwnerBadge email={auction.owner_email} />
                      </div>
                    </td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: getStatusColor(auction.status) }}>
                        {getStatusText(auction.status)}
                      </span>
                    </td>
                    <td>{getOfferFormText(auction.auction_form)}</td>
                    <td>{getAuctionTypeText(auction.auction_type)}</td>
                    <td>{auction.auction_type === 'public_offer' ? '—' : (getOfferFormText(auction.price_offer_form) || '—')}</td>
                    <td className="cell-wide">{getDebtorLabelForTable(auction)}</td>
                    <td className="cell-wide">{getOrganizerLabelForTable(auction)}</td>
                    <td>{auction.applications_start ? formatDate(auction.applications_start) : '—'}</td>
                    <td>{auction.applications_end ? formatDate(auction.applications_end) : '—'}</td>
                    <td>{auction.results_datetime ? formatDate(auction.results_datetime) : '—'}</td>
                    <td>{auction.auction_date ? formatDate(auction.auction_date) : '—'}</td>
                    <td className="cell-number">{Number(auction.total_start_price || 0).toLocaleString('ru-RU')} ₽</td>
                    <td>{auction.created_date ? formatDate(auction.created_date) : '—'}</td>
                    <td className="table-actions">
                      <div className="auction-actions-menu">
                        <button
                          type="button"
                          className="card-action-btn auction-actions-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openAuctionActionsMenuId === auction.id) {
                              setOpenAuctionActionsMenuId(null);
                              return;
                            }
                            openAuctionActionsMenu(auction.id, e.currentTarget);
                          }}
                          title="Действия"
                        >
                          ⋮
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
        {openAuctionActionsMenuId && openedAuctionForActionsMenu && createPortal(
          <div
            className={`auction-actions-dropdown auction-actions-dropdown-portal ${auctionActionsMenuPos.placement === 'top' ? 'auction-actions-dropdown-portal--top' : ''}`}
            style={{
              position: 'fixed',
              top: `${auctionActionsMenuPos.top}px`,
              left: `${auctionActionsMenuPos.left}px`,
              right: 'auto',
              maxHeight: `${auctionActionsMenuPos.maxHeight}px`,
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="auction-actions-item"
              onClick={() => {
                setOpenAuctionActionsMenuId(null);
                handleEditAuctionFromList(openedAuctionForActionsMenu);
              }}
            >
              <span className="auction-actions-item__icon" aria-hidden="true">✎</span>
              <span className="auction-actions-item__text">Редактировать</span>
            </button>
            <button
              type="button"
              className="auction-actions-item"
              disabled={!!repeatCreatingIds[openedAuctionForActionsMenu.id]}
              onClick={() => {
                setOpenAuctionActionsMenuId(null);
                handleCreateRepeatAuction(openedAuctionForActionsMenu);
              }}
            >
              <span className="auction-actions-item__icon" aria-hidden="true">🔁</span>
              <span className="auction-actions-item__text">Создать повторные</span>
            </button>
            <button
              type="button"
              className="auction-actions-item"
              onClick={() => {
                setOpenAuctionActionsMenuId(null);
                loadAuctionLots(openedAuctionForActionsMenu.id);
              }}
            >
              <span className="auction-actions-item__icon" aria-hidden="true">📦</span>
              <span className="auction-actions-item__text">Лоты ({openedAuctionForActionsMenu.lots_count || 0})</span>
            </button>
            <button
              type="button"
              className="auction-actions-item"
              onClick={() => {
                setOpenAuctionActionsMenuId(null);
                loadDocumentsForCard(openedAuctionForActionsMenu.id);
              }}
            >
              <span className="auction-actions-item__icon" aria-hidden="true">📄</span>
              <span className="auction-actions-item__text">Документы ({openedAuctionForActionsMenu.documents_count || 0})</span>
            </button>
            <button
              type="button"
              className="auction-actions-item auction-actions-item--danger"
              onClick={() => {
                setOpenAuctionActionsMenuId(null);
                handleDeleteAuction(openedAuctionForActionsMenu.id);
              }}
            >
              <span className="auction-actions-item__icon" aria-hidden="true">🗑️</span>
              <span className="auction-actions-item__text">Удалить</span>
            </button>
          </div>,
          document.body
        )}
      </div>

      {/* Компонент пагинации */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Показано {pagedAuctions.length} из {pagination.totalCount} торгов
          </div>
          <div className="pagination">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevious}
            >
              ← Предыдущая
            </button>

            <div className="page-numbers">
              {Array.from({ length: pagination.totalPages }, (_, index) => {
                const page = index + 1;
                const isVisible = (
                  page === 1 ||
                  page === pagination.totalPages ||
                  (page >= pagination.currentPage - 2 && page <= pagination.currentPage + 2)
                );

                if (!isVisible) {
                  if (page === pagination.currentPage - 3 || page === pagination.currentPage + 3) {
                    return <span key={page} className="page-ellipsis">...</span>;
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    className={`btn ${page === pagination.currentPage ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
            >
              Следующая →
            </button>
          </div>
        </div>
      )}

      {selectedAuction && (
        <LotsModal
          auctionId={selectedAuction}
          lots={lots}
          showLotForm={showLotForm}
          setShowLotForm={setShowLotForm}
          onAddLot={handleAddLot}
          onClose={() => {
            setSelectedAuction(null);
            setLots([]);
            setShowLotForm(false);
          }}
        />
      )}

      <Toast toast={toast} />

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      {/* Модальные окна для создания записей */}
      {showDebtorForm && (
        <DebtorFormModal
          debtor={createdDebtorDraft}
          showToast={showToast}
          onSave={async (debtorData, { exit } = {}) => {
            try {
              let savedDebtor = null;
              const isUpdate = Boolean(createdDebtorDraft?.id);
              if (createdDebtorDraft?.id) {
                const response = await apiService.updateDebtor(createdDebtorDraft.id, debtorData);
                const responseData = response.data || {};
                savedDebtor = { ...responseData, ...debtorData };
              } else {
                const response = await apiService.createDebtor(debtorData);
                const responseData = response.data || {};
                savedDebtor = { ...responseData, ...debtorData };
              }

              if (savedDebtor?.id) {
                const mappedDebtor = mapDebtorOption(savedDebtor);
                setCreatedDebtorDraft(mappedDebtor);
                setAutoSelectDebtorId(savedDebtor.id);
                setDebtors(prev => {
                  const exists = prev.some(d => d.id === savedDebtor.id);
                  if (exists) {
                    return prev.map(d => (d.id === savedDebtor.id ? mappedDebtor : d));
                  }
                  return [mappedDebtor, ...prev];
                });
                const managerFromDebtor = savedDebtor?.arbitration_manager_data
                  ? mapOrganizerOption(savedDebtor.arbitration_manager_data)
                  : null;
                if (managerFromDebtor?.id) {
                  setOrganizers((prev) => {
                    const exists = (prev || []).some((o) => String(o?.id) === String(managerFromDebtor.id));
                    if (exists) {
                      return (prev || []).map((o) =>
                        String(o?.id) === String(managerFromDebtor.id) ? managerFromDebtor : o
                      );
                    }
                    return [managerFromDebtor, ...(prev || [])];
                  });
                }
              }
              if (exit) {
                setShowDebtorForm(false);
                setCreatedDebtorDraft(null);
              }
              showToast(isUpdate ? 'Должник успешно обновлен' : 'Должник успешно создан', 'success');
              return savedDebtor;
            } catch (error) {
              const message = getApiErrorMessage(error, 'Не удалось сохранить должника');
              showToast(`Ошибка при сохранении должника: ${message}`, 'error');
              return false;
            }
          }}
          onCancel={() => {
            setShowDebtorForm(false);
            setCreatedDebtorDraft(null);
          }}
        />
      )}

      {showOrganizerForm && (
        <OrganizerFormModal
          fixedType={organizerFormFixedType}
          onSave={async (organizerData, { exit } = {}) => {
            try {
              const response = await apiService.createOrganizer(organizerData);
              const createdOrganizer = mapOrganizerOption(response.data);
              if (createdOrganizer?.id) {
                setAutoSelectOrganizerId(createdOrganizer.id);
              }
              setOrganizers(prev => {
                if (!createdOrganizer?.id) {
                  return prev;
                }
                const exists = prev.some(o => o.id === createdOrganizer.id);
                if (exists) {
                  return prev.map(o => (o.id === createdOrganizer.id ? createdOrganizer : o));
                }
                return [createdOrganizer, ...prev];
              });
              if (exit) {
                setShowOrganizerForm(false);
              }
              showToast('Организатор успешно создан', 'success');
              return createdOrganizer;
            } catch (error) {
              showToast(`Ошибка при создании организатора: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setShowOrganizerForm(false)}
        />
      )}

      {showAccountForm && (
        <AccountFormModal
          account={createdAccountDraft}
          debtors={debtors}
          organizers={organizers}
          etps={etps}
          initialPurpose={createdAccountDraft?.purpose}
          initialOwnerType={createdAccountDraft?.owner_type}
          initialOwnerId={createdAccountDraft?.owner_id}
          lockPurpose={createdAccountDraft?.purpose === 'payment' || createdAccountDraft?.purpose === 'deposit'}
          lockOwnerType={createdAccountDraft?.purpose === 'payment'}
          lockOwnerId={createdAccountDraft?.purpose === 'payment'}
          forcedOwnerIdsByType={{
            debtor: accountOwnerContext?.debtorId || '',
            organizer: accountOwnerContext?.organizerId || '',
            platform: accountOwnerContext?.etpId || '',
          }}
          onOwnerSelected={(ownerType, ownerId) => {
            const normalizedType = String(ownerType || '').trim().toLowerCase();
            const normalizedOwnerId = ownerId ? String(ownerId) : '';
            if (!normalizedOwnerId) return;
            setAccountOwnerContext((prev) => {
              const next = {
                ...(prev || {}),
                ...(normalizedType === 'debtor' ? { debtorId: normalizedOwnerId } : {}),
                ...(normalizedType === 'organizer' ? { organizerId: normalizedOwnerId } : {}),
                ...(normalizedType === 'platform' ? { etpId: normalizedOwnerId } : {}),
              };

              if (normalizedType === 'debtor' && prev?.backfillDebtorToAuction) {
                setAutoSelectDebtorId(normalizedOwnerId);
                next.backfillDebtorToAuction = false;
              }
              if (normalizedType === 'organizer' && prev?.backfillOrganizerToAuction) {
                setAutoSelectOrganizerId(normalizedOwnerId);
                next.backfillOrganizerToAuction = false;
              }
              if (normalizedType === 'platform' && prev?.backfillEtpToAuction) {
                setAutoSelectEtpId(normalizedOwnerId);
                next.backfillEtpToAuction = false;
              }

              return next;
            });
          }}
          onCreateOwner={handleAccountOwnerCreate}
          onEditOwner={handleAccountOwnerEdit}
          onSave={async (accountData, { exit } = {}) => {
            try {
              const normalized = {
                ...(accountData || {}),
                owner_id: accountData?.owner_id ? parseInt(accountData.owner_id, 10) : null
              };
              if (!normalized.owner_id) {
                if (normalized.owner_type === 'debtor' && accountOwnerContext?.debtorId) {
                  normalized.owner_id = parseInt(accountOwnerContext.debtorId, 10) || null;
                }
                if (normalized.owner_type === 'organizer' && accountOwnerContext?.organizerId) {
                  normalized.owner_id = parseInt(accountOwnerContext.organizerId, 10) || null;
                }
                if (normalized.owner_type === 'platform' && accountOwnerContext?.etpId) {
                  normalized.owner_id = parseInt(accountOwnerContext.etpId, 10) || null;
                }
              }
              let savedAccount = null;
              const isUpdate = Boolean(createdAccountDraft?.id);
              if (createdAccountDraft?.id) {
                const response = await apiService.updateBankAccount(createdAccountDraft.id, normalized);
                savedAccount = response.data || { ...createdAccountDraft, ...normalized };
              } else {
                const response = await apiService.createBankAccount(normalized);
                savedAccount = response.data;
              }

              if (savedAccount?.id) {
                setCreatedAccountDraft(savedAccount);
                setAutoSelectAccount(savedAccount);
                setBankAccounts(prev => {
                  const exists = prev.some(a => a.id === savedAccount.id);
                  if (exists) {
                    return prev.map(a => (a.id === savedAccount.id ? savedAccount : a));
                  }
                  return [savedAccount, ...prev];
                });
              }
              if (exit) {
                setShowAccountForm(false);
                setCreatedAccountDraft(null);
              }
              showToast(isUpdate ? 'Банковский счет успешно обновлен' : 'Банковский счет успешно создан', 'success');
              return savedAccount;
            } catch (error) {
              const reason = getApiErrorMessage(error, 'Не удалось сохранить счет.');
              showToast(`Ошибка при сохранении счета: ${reason}`, 'error');
              return false;
            }
          }}
          onCancel={() => {
            setShowAccountForm(false);
            setCreatedAccountDraft(null);
            setAccountOwnerContext({
              debtorId: '',
              organizerId: '',
              etpId: '',
              backfillDebtorToAuction: false,
              backfillOrganizerToAuction: false,
              backfillEtpToAuction: false,
            });
          }}
        />
      )}

      {showAccountOrganizerTypeModal && (
        <div className="modal-overlay" style={{ zIndex: 2500 }}>
          <div className="modal confirm-dialog" style={{ maxWidth: 520, width: 'min(520px, 92vw)' }}>
            <div className="modal-header">
              <h3>Добавить организатора</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-message">Выберите тип добавляемой записи.</p>
              <div className="confirm-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    setShowAccountOrganizerTypeModal(false);
                    setOrganizerFormFixedType('specialized_org');
                    setShowOrganizerForm(true);
                  }}
                >
                  ОТ (организатор торгов)
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    setShowAccountOrganizerTypeModal(false);
                    setOrganizerFormFixedType('arbitrage_manager');
                    setShowOrganizerForm(true);
                  }}
                >
                  АУ (арбитражный управляющий)
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ width: '100%', gridColumn: '1 / -1' }}
                  onClick={() => setShowAccountOrganizerTypeModal(false)}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна для редактирования записей */}
      {editDebtorModal.isOpen && (
        <DebtorFormModal
          debtor={editDebtorModal.debtor}
          showToast={showToast}
          onSave={async (debtorData, { exit } = {}) => {
            try {
              const response = await apiService.updateDebtor(editDebtorModal.debtor.id, debtorData);
              const responseData = response.data || {};
              const updatedDebtor = { ...responseData, ...debtorData };
              const mappedDebtor = mapDebtorOption(updatedDebtor);
              setDebtors(debtors.map(d => d.id === editDebtorModal.debtor.id ? mappedDebtor : d));
              const managerFromDebtor = updatedDebtor?.arbitration_manager_data
                ? mapOrganizerOption(updatedDebtor.arbitration_manager_data)
                : null;
              if (managerFromDebtor?.id) {
                setOrganizers((prev) => {
                  const exists = (prev || []).some((o) => String(o?.id) === String(managerFromDebtor.id));
                  if (exists) {
                    return (prev || []).map((o) =>
                      String(o?.id) === String(managerFromDebtor.id) ? managerFromDebtor : o
                    );
                  }
                  return [managerFromDebtor, ...(prev || [])];
                });
              }
              setEditDebtorModal(prev =>
                exit
                  ? { isOpen: false, debtor: null }
                  : { ...prev, isOpen: true, debtor: mappedDebtor }
              );
              showToast('Должник успешно обновлен', 'success');
              // Не дергаем loadData(), чтобы не было "мигания" страницы.
              // Обновляем данные в списке торгов и в открытой карточке локально.
              setAuctions(prev => (prev || []).map(a =>
                String(a?.debtor) === String(updatedDebtor?.id)
                  ? {
                    ...a,
                    debtor_name: mappedDebtor?.name || a.debtor_name,
                    debtor_display_name: getDebtorDisplayName(mappedDebtor) || mappedDebtor?.name || a.debtor_display_name
                  }
                  : a
              ));
              setModalState(prev => {
                if (!prev?.auction) return prev;
                if (String(prev.auction?.debtor) !== String(updatedDebtor?.id)) return prev;
                return {
                  ...prev,
                  auction: {
                    ...prev.auction,
                    debtor_name: mappedDebtor?.name || prev.auction.debtor_name,
                    debtor_display_name: getDebtorDisplayName(mappedDebtor) || mappedDebtor?.name || prev.auction.debtor_display_name
                  }
                };
              });
              return mappedDebtor;
            } catch (error) {
              showToast(`Ошибка при обновлении должника: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setEditDebtorModal({ isOpen: false, debtor: null })}
        />
      )}

      {editOrganizerModal.isOpen && (
        <OrganizerFormModal
          organizer={editOrganizerModal.organizer}
          fixedType={editOrganizerModal.fixedType || editOrganizerModal.organizer?.type || 'specialized_org'}
          onSave={async (organizerData, { exit } = {}) => {
            try {
              const response = await apiService.updateOrganizer(editOrganizerModal.organizer.id, organizerData);
              const updatedOrganizer = mapOrganizerOption(response.data);
              setOrganizers(organizers.map(o => o.id === editOrganizerModal.organizer.id ? updatedOrganizer : o));
              setDebtors(prev => (prev || []).map((d) => {
                const managerId = d?.arbitration_manager || d?.arbitration_manager_data?.id;
                if (String(managerId || '') !== String(updatedOrganizer?.id || '')) return d;
                return {
                  ...d,
                  arbitration_manager_data: {
                    ...(d.arbitration_manager_data || {}),
                    ...updatedOrganizer,
                  },
                };
              }));
              setEditOrganizerModal(prev =>
                exit
                  ? { isOpen: false, organizer: null, fixedType: null }
                  : { ...prev, isOpen: true, organizer: updatedOrganizer, fixedType: prev.fixedType }
              );
              showToast('Организатор успешно обновлен', 'success');
              // Не дергаем loadData(), чтобы не было "мигания" страницы.
              // Обновляем данные в списке торгов и в открытой карточке локально.
              setAuctions(prev => (prev || []).map(a =>
                String(a?.organizer) === String(updatedOrganizer?.id)
                  ? {
                    ...a,
                    organizer_name: updatedOrganizer?.name || a.organizer_name,
                    organizer_display_name: getOrganizerDisplayName(updatedOrganizer) || updatedOrganizer?.name || a.organizer_display_name,
                    organizer_type: updatedOrganizer?.type || a.organizer_type
                  }
                  : a
              ));
              setModalState(prev => {
                if (!prev?.auction) return prev;
                if (String(prev.auction?.organizer) !== String(updatedOrganizer?.id)) return prev;
                return {
                  ...prev,
                  auction: {
                    ...prev.auction,
                    organizer_name: updatedOrganizer?.name || prev.auction.organizer_name,
                    organizer_display_name: getOrganizerDisplayName(updatedOrganizer) || updatedOrganizer?.name || prev.auction.organizer_display_name,
                    organizer_type: updatedOrganizer?.type || prev.auction.organizer_type
                  }
                };
              });
              return updatedOrganizer;
            } catch (error) {
              showToast(`Ошибка при обновлении организатора: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setEditOrganizerModal({ isOpen: false, organizer: null, fixedType: null })}
        />
      )}

      {editAccountModal.isOpen && (
        <AccountFormModal
          account={editAccountModal.account}
          debtors={debtors}
          organizers={organizers}
          etps={etps}
          initialPurpose={editAccountModal?.type === 'payment' ? 'payment' : editAccountModal?.account?.purpose}
          initialOwnerType={editAccountModal?.type === 'payment' ? 'debtor' : editAccountModal?.account?.owner_type}
          initialOwnerId={editAccountModal?.type === 'payment' ? editAccountModal?.debtorId : editAccountModal?.account?.owner_id}
          lockPurpose={editAccountModal?.type === 'payment' || editAccountModal?.type === 'deposit'}
          lockOwnerType={editAccountModal?.type === 'payment'}
          lockOwnerId={editAccountModal?.type === 'payment'}
          forcedOwnerIdsByType={editAccountModal?.type === 'deposit'
            ? {
                debtor: editAccountModal?.debtorId || '',
                organizer: editAccountModal?.organizerId || '',
                platform: editAccountModal?.etpId || '',
              }
            : {}}
          onCreateOwner={handleAccountOwnerCreate}
          onEditOwner={handleAccountOwnerEdit}
          onSave={async (accountData, { exit } = {}) => {
            try {
              const lockedOwnerId = editAccountModal?.debtorId || editAccountModal?.account?.owner_id || accountData?.owner_id || null;
              const normalized = editAccountModal?.type === 'payment'
                ? {
                    ...accountData,
                    purpose: 'payment',
                    owner_type: 'debtor',
                    owner_id: lockedOwnerId
                  }
                : accountData;
              const response = await apiService.updateBankAccount(editAccountModal.account.id, normalized);
              const updatedAccount = response.data;
              setBankAccounts(bankAccounts.map(a => a.id === editAccountModal.account.id ? updatedAccount : a));
              setEditAccountModal(prev =>
                exit
                  ? { isOpen: false, account: null, type: null, debtorId: '', organizerId: '', etpId: '' }
                  : { ...prev, isOpen: true, account: updatedAccount, type: prev.type }
              );
              showToast('Банковский счет успешно обновлен', 'success');
              return updatedAccount;
            } catch (error) {
              showToast(`Ошибка при обновлении банковского счета: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setEditAccountModal({ isOpen: false, account: null, type: null, debtorId: '', organizerId: '', etpId: '' })}
        />
      )}

      {/* Модальное окно со списком документов для карточки торгов */}
      {showDocumentsListModal && (
        <DocumentsListModal
          documents={documentsForModal}
          auction={currentAuctionForModal}
          debtors={debtors}
          organizers={organizers}
          lots={lotsForModal}
          etps={etps}
          onClose={() => {
            setShowDocumentsListModal(false);
            setDocumentsForModal([]);
            setCurrentAuctionForModal(null);
            setLotsForModal([]);
          }}
          onViewDocument={() => {
            // Теперь логика просмотра обрабатывается внутри DocumentsListModal
          }}
          onDocumentDeleted={() => {
            // Обновляем список торгов для актуализации счетчика документов
            loadData();
            // Обновляем список документов в модальном окне
            if (currentAuctionForModal?.id) {
              loadDocumentsForCard(currentAuctionForModal.id);
            }
          }}
        />
      )}

      {/* Модальное окно просмотра документа из списка теперь внутри DocumentsListModal */}
    </div>
  );
};

const AuctionForm = ({
  auction,
  forceResetToken = 0,
  debtors,
  organizers,
  etps,
  bankAccounts,
  onSave,
  onCancel,
  isViewMode = false,
  onEdit,
  onFormChange,
  onCreateDebtor,
  onCreateOrganizer,
  onCreateAccount,
  onEditDebtor,
  onEditOrganizer,
  onEditAccount,
  onAuctionUpdate,
  onAttachExistingLot,
  isAdmin = false,
  lotColWidths,
  onStartResizeLotCol,
  publicOfferColWidths,
  onStartResizePublicOfferCol,
  integrationsJournalColWidths = DEFAULT_INTEGRATIONS_JOURNAL_COL_WIDTHS,
  onStartResizeIntegrationsJournalCol,
  autoSelectDebtorId = '',
  autoSelectOrganizerId = '',
  autoSelectEtpId = '',
  onAutoSelectDebtorApplied,
  onAutoSelectOrganizerApplied,
  onAutoSelectEtpApplied,
  autoSelectAccount = null,
  onAutoSelectAccountApplied,
  externalTitleValue = '',
  onDebtorAutoTitle = null,
  user = null,
}) => {
  const inspectionPhoneRef = React.useRef(null);
  const efrsbMessageInputRef = React.useRef(null);
  const fedresursAddMessageBtnRef = React.useRef(null);
  const organizerFieldRef = React.useRef(null);
  const [formData, setFormData] = useState({
    title: auction?.title || getAuctionTitleLabel(auction),
    debtor: auction?.debtor || '',
    organizer: auction?.organizer || '',
    etp: auction?.etp || '',
    etp_host: auction?.etp_host || 'bankruptcy.bepspb.ru',
    efrsb_message_id: auction?.efrsb_message_id || '',
    auction_type: auction?.auction_type || 'auction',
    is_repeat_trade: !!auction?.is_repeat_trade,
    auction_form: auction?.auction_form || 'open',
    price_offer_form: auction?.auction_type === 'public_offer'
      ? 'open'
      : (auction?.price_offer_form || 'open'),
    auction_start_datetime: formatInputDateTime(auction?.auction_start_datetime || auction?.applications_start),
    applications_start: formatInputDateTime(auction?.applications_start),
    applications_end: formatInputDateTime(auction?.applications_end),
    results_datetime: formatInputDateTime(auction?.results_datetime),
    public_offer_periods: normalizePublicOfferPeriods(
      auction?.public_offer_periods,
      formatInputDateTime(auction?.applications_start)
    ),
    auction_date: formatInputDateTime(auction?.auction_date),
    auction_step: formatPercentForUi(auction?.auction_step),
    contest_step: auction?.contest_step || '',
    status: auction?.status || 'draft',
    price_decrease_rules: auction?.price_decrease_rules || '',
    contest_conditions: auction?.contest_conditions || '',
    deposit_account: auction?.deposit_account || '',
    payment_account: auction?.payment_account || '',
    deposit_amount: formatPercentForUi(auction?.deposit_amount),
    deposit_deadline: auction?.deposit_deadline || '',
    inspection_phone: auction?.inspection_phone || '',
    inspection_email: auction?.inspection_email || ''
  });
  const formDataRef = React.useRef(formData);

  const [initialFormData, setInitialFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [depositAmountWarning, setDepositAmountWarning] = useState('');
  const [auctionStepWarning, setAuctionStepWarning] = useState('');
  const [inspectionPhoneCustom, setInspectionPhoneCustom] = useState(false);
  const [inspectionEmailCustom, setInspectionEmailCustom] = useState(false);
  const [organizerSource, setOrganizerSource] = useState('directory'); // directory | debtor_manager
  const [showAddOrganizerTypeModal, setShowAddOrganizerTypeModal] = useState(false);
  const lastDirectoryOrganizerIdRef = React.useRef('');

  const [lots, setLots] = useState([]);
  const lotsRef = React.useRef([]);
  const [showLotForm, setShowLotForm] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [lotModalMode, setLotModalMode] = useState(null); // create | edit | view
  const [lotHasUnsavedChanges, setLotHasUnsavedChanges] = useState(false);
  const [lotFormInitialNumber, setLotFormInitialNumber] = useState('');
  const [showAttachLotModal, setShowAttachLotModal] = useState(false);
  const [allLots, setAllLots] = useState([]);
  const [allAuctions, setAllAuctions] = useState([]);
  const [toast, setToast] = useState(null);
  const [lotFormData, setLotFormData] = useState({
    lot_number: '',
    name: '',
    description: '',
    start_price: '',
    vat_text: '',
    cadastral_number: '',
    location: '',
    category: 'real_estate',
    efrsb_classifiers: [],
    pledge_creditor_satisfaction: false,
    pledge_creditors: [],
    public_offer_settings: {},
    public_offer_periods: [],
    public_offer_periods_filled_once: false,
  });
  const effectivePublicOfferColWidths = useMemo(() => ({
    interval_start_datetime: 190,
    applications_start_datetime: 190,
    applications_end_datetime: 190,
    interval_end_datetime: 190,
    reduction_value: 220,
    deposit_amount: 140,
    interval_price: 140,
    comment: 220,
    ...(publicOfferColWidths || {}),
  }), [publicOfferColWidths]);
  const publicOfferTableWidthPx = useMemo(() => {
    const usedCols = [
      'interval_start_datetime',
      'applications_start_datetime',
      'applications_end_datetime',
      'interval_end_datetime',
      'reduction_value',
      'deposit_amount',
      'interval_price',
      'comment',
    ];
    return usedCols.reduce((sum, key) => {
      const value = effectivePublicOfferColWidths?.[key];
      const n = Number(value);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [effectivePublicOfferColWidths]);
  const [publicOfferAutoConfigOpen, setPublicOfferAutoConfigOpen] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  // Состояния для работы с документами
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentAuctionContext, setDocumentAuctionContext] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [etpExports, setEtpExports] = useState([]);
  const [etpExportsLoading, setEtpExportsLoading] = useState(false);
  const [etpExportStarting, setEtpExportStarting] = useState(false);
  const [etpHostInput, setEtpHostInput] = useState('bankruptcy.bepspb.ru');
  const [canSelectEtpHost, setCanSelectEtpHost] = useState(false);
  const [allowedEtpHosts, setAllowedEtpHosts] = useState(['bankruptcy.bepspb.ru']);
  const [etpHostMenuOpen, setEtpHostMenuOpen] = useState(false);
  const etpHostMenuRef = useRef(null);
  const [etpLoginInput, setEtpLoginInput] = useState('');
  const [etpPromptPassword, setEtpPromptPassword] = useState('');
  const [fedresursLoginInput, setFedresursLoginInput] = useState('');
  const [fedresursPromptPassword, setFedresursPromptPassword] = useState('');
  const [fedresursExportStarting, setFedresursExportStarting] = useState(false);
  const [fedresursExports, setFedresursExports] = useState([]);
  const [fedresursExportsLoading, setFedresursExportsLoading] = useState(false);
  const [docSortField, setDocSortField] = useState('created_date');
  const [docSortDirection, setDocSortDirection] = useState('desc');
  const initializedFormKeyRef = React.useRef(null);
  const skipInspectionPhoneSyncRef = React.useRef(false);
  const skipInspectionEmailSyncRef = React.useRef(false);
  const depositDeadlineAutoFilledRef = React.useRef(false);
  const publicOfferDateFocusValuesRef = React.useRef({});
  const publicOfferReductionFocusValuesRef = React.useRef({});
  const globalPublicOfferDateFocusValuesRef = React.useRef({});
  const [invalidGlobalPublicOfferFields, setInvalidGlobalPublicOfferFields] = useState({});
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderInitial, setReminderInitial] = useState({});
  const pendingPublicOfferRecalcRef = React.useRef({});
  const publicOfferAutoEndSyncMetaRef = React.useRef({
    initialized: false,
    lotCount: 0,
    lastEndsByLotId: {},
  });
  const [invalidPublicOfferFields, setInvalidPublicOfferFields] = useState({});
  const invalidPublicOfferFieldTimeoutsRef = React.useRef({});
  const persistentInvalidPublicOfferFieldsRef = React.useRef({});
  const invalidGlobalPublicOfferFieldTimeoutsRef = React.useRef({});
  const [invalidEfrsbField, setInvalidEfrsbField] = useState(false);
  const invalidEfrsbFieldTimeoutRef = React.useRef(null);
  const FEDRESURS_LOGIN_BY_AUCTION_KEY = 'aot_fedresurs_login_by_auction_v1';
  const ETP_LOGIN_BY_AUCTION_KEY = 'aot_etp_login_by_auction_v1';
  const processedFedresursDraftIdsRef = React.useRef(new Set());
  const trackedFedresursJobIdsRef = React.useRef(new Set());
  const [pendingEfrsbFocusAfterEdit, setPendingEfrsbFocusAfterEdit] = useState(false);
  const etpHostOptions = useMemo(() => {
    const hostToName = new Map();
    (etps || []).forEach((item) => {
      const host = String(item?.fogsoft_host || '').trim();
      if (!host) return;
      if (!hostToName.has(host)) {
        const name = String(item?.name || '').trim();
        hostToName.set(host, name);
      }
    });
    return (allowedEtpHosts || []).map((hostValue) => {
      const host = String(hostValue || '').trim();
      const name = hostToName.get(host) || '';
      return {
        value: host,
        label: name ? `${name} (${host})` : host,
      };
    });
  }, [allowedEtpHosts, etps]);

  useEffect(() => {
    if (!etpHostMenuOpen) return undefined;
    const onDocMouseDown = (event) => {
      if (!etpHostMenuRef.current) return;
      if (!etpHostMenuRef.current.contains(event.target)) {
        setEtpHostMenuOpen(false);
      }
    };
    const onDocKeyDown = (event) => {
      if (event.key === 'Escape') {
        setEtpHostMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [etpHostMenuOpen]);
  const [inlineChoice, setInlineChoice] = useState({
    isOpen: false,
    top: 0,
    left: 0,
    message: '',
    confirmText: 'Да',
    cancelText: 'Отмена',
    onConfirm: null,
    onCancel: null,
  });
  const inlineChoiceRef = React.useRef(inlineChoice);
  const publicOfferStartBase = formData?.auction_start_datetime || formData?.applications_start || '';
  const getPublicOfferLotKey = useCallback((lot) => String(lot?.id ?? `tmp-${lot?.lot_number ?? ''}`), []);
  const getPublicOfferFieldKey = useCallback((lotId, rowIndex, key) => `${String(lotId ?? '')}:${String(rowIndex ?? '')}:${String(key ?? '')}`, []);
  const readFedresursLoginByAuction = useCallback((auctionId) => {
    const id = String(auctionId || '').trim();
    if (!id) return '';
    try {
      const raw = localStorage.getItem(FEDRESURS_LOGIN_BY_AUCTION_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      if (!parsed || typeof parsed !== 'object') return '';
      return String(parsed[id] || '');
    } catch (_) {
      return '';
    }
  }, []);
  const writeFedresursLoginByAuction = useCallback((auctionId, loginValue) => {
    const id = String(auctionId || '').trim();
    if (!id) return;
    try {
      const raw = localStorage.getItem(FEDRESURS_LOGIN_BY_AUCTION_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const next = (parsed && typeof parsed === 'object') ? { ...parsed } : {};
      next[id] = String(loginValue || '');
      localStorage.setItem(FEDRESURS_LOGIN_BY_AUCTION_KEY, JSON.stringify(next));
    } catch (_) {
      // noop
    }
  }, []);
  const readEtpLoginByAuction = useCallback((auctionId) => {
    const id = String(auctionId || '').trim();
    if (!id) return '';
    try {
      const raw = localStorage.getItem(ETP_LOGIN_BY_AUCTION_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      if (!parsed || typeof parsed !== 'object') return '';
      return String(parsed[id] || '');
    } catch (_) {
      return '';
    }
  }, []);
  const writeEtpLoginByAuction = useCallback((auctionId, loginValue) => {
    const id = String(auctionId || '').trim();
    if (!id) return;
    try {
      const raw = localStorage.getItem(ETP_LOGIN_BY_AUCTION_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const next = (parsed && typeof parsed === 'object') ? { ...parsed } : {};
      next[id] = String(loginValue || '');
      localStorage.setItem(ETP_LOGIN_BY_AUCTION_KEY, JSON.stringify(next));
    } catch (_) {
      // noop
    }
  }, []);

  useEffect(() => {
    setPublicOfferAutoConfigOpen((prev) => {
      const next = {};
      (lots || []).forEach((lot) => {
        const key = getPublicOfferLotKey(lot);
        if (Object.prototype.hasOwnProperty.call(prev, key)) {
          next[key] = !!prev[key];
        }
      });
      return next;
    });
  }, [lots, getPublicOfferLotKey]);

  useEffect(() => {
    lotsRef.current = lots || [];
  }, [lots]);

  useEffect(() => {
    return () => {
      Object.values(invalidPublicOfferFieldTimeoutsRef.current || {}).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      invalidPublicOfferFieldTimeoutsRef.current = {};
      Object.values(invalidGlobalPublicOfferFieldTimeoutsRef.current || {}).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      invalidGlobalPublicOfferFieldTimeoutsRef.current = {};
      if (invalidEfrsbFieldTimeoutRef.current) {
        clearTimeout(invalidEfrsbFieldTimeoutRef.current);
        invalidEfrsbFieldTimeoutRef.current = null;
      }
    };
  }, []);

  const focusEfrsbMessageInput = useCallback(() => {
    const input = efrsbMessageInputRef.current;
    if (!input) return;
    try {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus({ preventScroll: true });
    } catch (_) {
      input.focus();
    }
  }, []);

  const scrollToOrganizerField = useCallback(() => {
    const field = organizerFieldRef.current;
    if (!field) return;
    try {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      field.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    if (!pendingEfrsbFocusAfterEdit) return;
    if (isViewMode) return;
    const timerId = setTimeout(() => {
      focusEfrsbMessageInput();
      setPendingEfrsbFocusAfterEdit(false);
    }, 0);
    return () => clearTimeout(timerId);
  }, [pendingEfrsbFocusAfterEdit, isViewMode, focusEfrsbMessageInput]);

  const handleInspectionPhoneChange = (event) => {
    if (isViewMode || !inspectionPhoneCustom) return;
    const { value, selectionStart } = event.target;
    const { formatted, caret } = applyPhoneMask(value, selectionStart);

    setFormData((prev) => {
      const next = { ...(prev || {}), inspection_phone: formatted };
      formDataRef.current = next;
      return next;
    });
    requestAnimationFrame(() => {
      const input = inspectionPhoneRef.current;
      if (!input) return;
      input.setSelectionRange(caret, caret);
    });
  };

  const handleInspectionEmailChange = (event) => {
    if (isViewMode || !inspectionEmailCustom) return;
    const nextEmail = String(event.target?.value || '');
    setFormData((prev) => {
      const next = { ...(prev || {}), inspection_email: nextEmail };
      formDataRef.current = next;
      return next;
    });
  };

  const specializedOrganizers = React.useMemo(
    () => (organizers || []).filter((o) => o?.type === 'specialized_org'),
    [organizers]
  );
  const arbitrageManagers = React.useMemo(
    () => (organizers || []).filter((o) => o?.type === 'arbitrage_manager'),
    [organizers]
  );

  const selectedOrganizerObj = React.useMemo(
    () => (organizers || []).find((o) => String(o?.id) === String(formData?.organizer)),
    [organizers, formData?.organizer]
  );

  const directoryOrganizerOptions = React.useMemo(() => {
    if (isViewMode) {
      return organizers || [];
    }
    const base = [...(specializedOrganizers || []), ...(arbitrageManagers || [])];

    // Историческая совместимость: если в торге сохранён организатор типа АУ,
    // добавляем его в список опций, чтобы поле не "пустело" при переходе в режим редактирования.
    if (selectedOrganizerObj?.type === 'arbitrage_manager') {
      const exists = base.some((o) => String(o?.id) === String(selectedOrganizerObj.id));
      if (!exists) {
        return [
          { ...selectedOrganizerObj, display_name: `${getOrganizerDisplayName(selectedOrganizerObj) || selectedOrganizerObj.name} (АУ)` },
          ...base,
        ];
      }
    }

    return base;
  }, [isViewMode, organizers, specializedOrganizers, arbitrageManagers, selectedOrganizerObj]);

  const allDepositAccounts = React.useMemo(
    () => (bankAccounts || []).filter((acc) => String(acc?.purpose || '').trim().toLowerCase() === 'deposit'),
    [bankAccounts]
  );

  const bankAccountsDepositOptions = React.useMemo(() => {
    const selectedDebtorId = String(formData?.debtor || '').trim();
    const selectedOrganizerId = String(formData?.organizer || '').trim();
    const selectedEtpId = String(formData?.etp || '').trim();
    return allDepositAccounts.filter((acc) => {
      const ownerType = String(acc?.owner_type || '').trim().toLowerCase();
      if (!['debtor', 'organizer', 'platform'].includes(ownerType)) {
        return false;
      }
      if (ownerType === 'debtor') {
        if (!selectedDebtorId) {
          return true;
        }
        const accountDebtorId = String(acc?.owner_id ?? acc?.ownerId ?? '').trim();
        return accountDebtorId === selectedDebtorId;
      }
      if (ownerType === 'organizer') {
        if (!selectedOrganizerId) {
          return true;
        }
        const accountOrganizerId = String(acc?.owner_id ?? acc?.ownerId ?? '').trim();
        return accountOrganizerId === selectedOrganizerId;
      }
      if (ownerType !== 'platform') {
        return true;
      }
      if (!selectedEtpId) {
        return true;
      }
      const accountEtpId = String(acc?.owner_id ?? acc?.ownerId ?? '').trim();
      return accountEtpId === selectedEtpId;
    });
  }, [allDepositAccounts, formData?.debtor, formData?.organizer, formData?.etp]);

  const bankAccountsPaymentOptions = React.useMemo(() => {
    return (bankAccounts || []).filter((acc) => {
      const purpose = String(acc?.purpose || '').trim().toLowerCase();
      const ownerType = String(acc?.owner_type || '').trim().toLowerCase();
      const ownerId = acc?.owner_id ?? acc?.ownerId ?? '';
      return purpose === 'payment' && ownerType === 'debtor' && String(ownerId) === String(formData?.debtor || '');
    });
  }, [bankAccounts, formData?.debtor]);

  const selectedDepositAccount = React.useMemo(
    () => allDepositAccounts.find((acc) => String(acc?.id) === String(formData?.deposit_account || '')),
    [allDepositAccounts, formData?.deposit_account]
  );
  const selectedPaymentAccount = React.useMemo(
    () => bankAccountsPaymentOptions.find((acc) => String(acc?.id) === String(formData?.payment_account || '')),
    [bankAccountsPaymentOptions, formData?.payment_account]
  );

  const canEditDepositAccount = !isViewMode && !!selectedDepositAccount;
  const canEditPaymentAccount = !isViewMode && !!selectedPaymentAccount;

  const isDepositPlatformEtpMismatch = React.useMemo(() => {
    if (!selectedDepositAccount) return false;
    const ownerType = String(selectedDepositAccount?.owner_type || '').trim().toLowerCase();
    if (ownerType !== 'platform') return false;
    const selectedEtpId = String(formData?.etp || '').trim();
    if (!selectedEtpId) return false;
    const accountEtpId = String(selectedDepositAccount?.owner_id ?? selectedDepositAccount?.ownerId ?? '').trim();
    return !!accountEtpId && accountEtpId !== selectedEtpId;
  }, [selectedDepositAccount, formData?.etp]);

  const isDepositDebtorMismatch = React.useMemo(() => {
    if (!selectedDepositAccount) return false;
    const ownerType = String(selectedDepositAccount?.owner_type || '').trim().toLowerCase();
    if (ownerType !== 'debtor') return false;
    const selectedDebtorId = String(formData?.debtor || '').trim();
    if (!selectedDebtorId) return false;
    const accountDebtorId = String(selectedDepositAccount?.owner_id ?? selectedDepositAccount?.ownerId ?? '').trim();
    return !!accountDebtorId && accountDebtorId !== selectedDebtorId;
  }, [selectedDepositAccount, formData?.debtor]);

  const isDepositOrganizerMismatch = React.useMemo(() => {
    if (!selectedDepositAccount) return false;
    const ownerType = String(selectedDepositAccount?.owner_type || '').trim().toLowerCase();
    if (ownerType !== 'organizer') return false;
    const selectedOrganizerId = String(formData?.organizer || '').trim();
    if (!selectedOrganizerId) return false;
    const accountOrganizerId = String(selectedDepositAccount?.owner_id ?? selectedDepositAccount?.ownerId ?? '').trim();
    return !!accountOrganizerId && accountOrganizerId !== selectedOrganizerId;
  }, [selectedDepositAccount, formData?.organizer]);

  const hasDepositAccountOwnershipMismatch =
    isDepositPlatformEtpMismatch || isDepositDebtorMismatch || isDepositOrganizerMismatch;

  const integrationsReady = useMemo(() => {
    const hasCoreFields = !!(
      formData?.debtor
      && formData?.organizer
      && formData?.auction_type
      && formData?.auction_form
      && formData?.price_offer_form
      && formData?.etp
      && formData?.deposit_account
      && formData?.payment_account
      && String(formData?.deposit_amount || '').trim()
      && String(formData?.deposit_deadline || '').trim()
    );
    if (!hasCoreFields) return false;
    if (!Array.isArray(lots) || lots.length === 0) return false;
    if (!selectedDepositAccount || !selectedPaymentAccount || hasDepositAccountOwnershipMismatch) return false;

    if (formData?.auction_type === 'public_offer') {
      const publicOfferReady = lots.every((lot) => {
        const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase, false);
        const first = rows?.[0] || null;
        return !!(
          first
          && first.interval_start_datetime
          && first.applications_start_datetime
          && first.applications_end_datetime
          && first.interval_end_datetime
        );
      });
      if (!publicOfferReady) return false;
    } else if (!formData?.applications_start || !formData?.applications_end) {
      return false;
    }

    const needsResults = formData?.price_offer_form === 'closed' || formData?.auction_type === 'public_offer';
    if (needsResults && !formData?.results_datetime) return false;

    const needsAuctionDate = formData?.price_offer_form === 'open' && formData?.auction_type !== 'public_offer';
    if (needsAuctionDate && !formData?.auction_date) return false;

    if (formData?.auction_type === 'auction' && formData?.price_offer_form === 'open' && !String(formData?.auction_step || '').trim()) {
      return false;
    }

    if (formData?.auction_type === 'contest') {
      if (formData?.price_offer_form === 'open' && !String(formData?.contest_step || '').trim()) return false;
      if (!String(formData?.contest_conditions || '').trim()) return false;
    }

    return true;
  }, [
    formData,
    lots,
    selectedDepositAccount,
    selectedPaymentAccount,
    hasDepositAccountOwnershipMismatch,
    publicOfferStartBase,
  ]);
  const sectionsLocked = !integrationsReady;

  useEffect(() => {
    if (isViewMode) return;
    if (formData?.deposit_account && !selectedDepositAccount) {
      updateFormData('deposit_account', '');
    }
  }, [formData?.deposit_account, selectedDepositAccount, isViewMode]);

  useEffect(() => {
    if (isViewMode) return;
    if (formData?.payment_account && !selectedPaymentAccount) {
      updateFormData('payment_account', '');
    }
  }, [formData?.payment_account, selectedPaymentAccount, isViewMode]);

  const formatOrganizerOption = React.useCallback((o) => {
    if (!o) return '';
    const label = o.display_name || o.name || '';
    if (o.type === 'arbitrage_manager') return `${label} (АУ)`;
    if (o.type === 'specialized_org') return `${label} (ОТ)`;
    return label;
  }, []);

  const selectedDebtor = React.useMemo(
    () => (debtors || []).find((d) => String(d?.id) === String(formData?.debtor)),
    [debtors, formData?.debtor]
  );

  const debtorManagerId = selectedDebtor?.arbitration_manager || selectedDebtor?.arbitration_manager_data?.id || '';
  const debtorManagerName =
    selectedDebtor?.arbitration_manager_data?.name ||
    (organizers || []).find((o) => String(o?.id) === String(debtorManagerId))?.name ||
    '';
  const selectedDebtorManagerObj = React.useMemo(() => {
    const managerFromDebtor = selectedDebtor?.arbitration_manager_data || null;
    if (!debtorManagerId) return null;
    const managerFromOrganizers = (organizers || []).find((o) => String(o?.id) === String(debtorManagerId)) || null;
    return managerFromOrganizers || managerFromDebtor || null;
  }, [selectedDebtor, debtorManagerId, organizers]);
  const effectiveOrganizerForContacts = React.useMemo(() => {
    if (organizerSource === 'debtor_manager') {
      return selectedDebtorManagerObj || selectedOrganizerObj || null;
    }
    return selectedOrganizerObj || null;
  }, [organizerSource, selectedDebtorManagerObj, selectedOrganizerObj]);

  const organizerPhone = React.useMemo(
    () => formatPhone(normalizePhoneDigits(effectiveOrganizerForContacts?.phone || '')),
    [effectiveOrganizerForContacts]
  );
  const organizerEmail = React.useMemo(
    () => String(effectiveOrganizerForContacts?.email || '').trim(),
    [effectiveOrganizerForContacts]
  );

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (auction?.id) {
      const prefetchedLotsRaw = Array.isArray(auction?.lots_prefetched) ? auction.lots_prefetched : [];
      if (prefetchedLotsRaw.length > 0) {
        const normalizedPrefetchedLots = normalizeLotsWithFallbackNumbers(prefetchedLotsRaw).map((lot) => ({
          ...lot,
          public_offer_settings: normalizeLotPublicOfferSettings(lot?.public_offer_settings),
          public_offer_periods: normalizeLotPublicOfferPeriods(
            lot?.public_offer_periods,
            formatInputDateTime(auction?.applications_start)
          ),
          public_offer_periods_filled_once: !!lot?.public_offer_periods_filled_once,
        }));
        setLots(normalizedPrefetchedLots);
      } else {
        setLots([]);
      }
      loadAuctionLots(auction.id);
      loadAuctionDocuments();
    }
  }, [auction]);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (!showAttachLotModal) return;
    loadAllLots();
    loadAllAuctions();
  }, [showAttachLotModal]);

  // Инициализация формы
  useEffect(() => {
    const auctionSnapshotKey = auction?.id
      ? [
          `id=${auction.id}`,
          `upd=${String(auction.updated_date || '')}`,
          `insp_phone=${String(auction.inspection_phone || '')}`,
          `insp_email=${String(auction.inspection_email || '')}`,
          `org=${String(auction.organizer || '')}`,
          `debtor=${String(auction.debtor || '')}`,
        ].join('|')
      : 'new';
    const key = `${auctionSnapshotKey}:reset=${forceResetToken}`;
    // Важно: не пересоздаём форму при обновлении справочников (например, после сохранения должника из карточки торгов),
    // иначе визуально выглядит как "перезагрузка" из-за сброса состояния формы.
    if (initializedFormKeyRef.current === key) {
      return;
    }

    let newFormData;

    if (auction) {
      // Предпочитаем ID из самого торга, при отсутствии пытаемся найти по имени
      const selectedDebtor = debtors.find(d => d.id === auction.debtor) || debtors.find(d => d.name === auction.debtor_name);
      const selectedOrganizer = organizers.find(o => o.id === auction.organizer) || organizers.find(o => o.name === auction.organizer_name);
      const organizerPhone = formatPhone(normalizePhoneDigits(selectedOrganizer?.phone || ''));
      const organizerEmail = String(selectedOrganizer?.email || '').trim();
      const inspectionPhoneRaw = String(auction.inspection_phone || '').trim();
      const inspectionPhoneDigits = normalizePhoneDigits(inspectionPhoneRaw);
      const inspectionPhone = inspectionPhoneDigits
        ? formatPhone(inspectionPhoneDigits)
        : inspectionPhoneRaw;
      const inspectionEmail = String(auction.inspection_email || '').trim();
      const hasInspectionPhone = !!inspectionPhoneRaw;
      const hasOrganizerPhone = !!normalizePhoneDigits(organizerPhone || '');
      const inspectionPhoneComparisonValue = inspectionPhoneDigits || inspectionPhoneRaw;
      const organizerPhoneComparisonValue = normalizePhoneDigits(organizerPhone) || String(organizerPhone || '').trim();
      const isCustomInspectionPhone = hasInspectionPhone && (
        !hasOrganizerPhone || inspectionPhoneComparisonValue !== organizerPhoneComparisonValue
      );
      const hasInspectionEmail = !!inspectionEmail;
      const hasOrganizerEmail = !!organizerEmail;
      const isCustomInspectionEmail = hasInspectionEmail && (!hasOrganizerEmail || inspectionEmail.toLowerCase() !== organizerEmail.toLowerCase());

      newFormData = {
        title: auction.title || `Торги #${auction.id}`,
        debtor: selectedDebtor?.id || auction.debtor || '',
        organizer: selectedOrganizer?.id || auction.organizer || '',
        etp: auction.etp || '',
        etp_host: auction.etp_host || 'bankruptcy.bepspb.ru',
        efrsb_message_id: auction.efrsb_message_id || '',
        auction_type: auction.auction_type || 'auction',
        is_repeat_trade: !!auction.is_repeat_trade,
        auction_form: auction.auction_form || 'open',
        price_offer_form: auction.auction_type === 'public_offer'
          ? 'open'
          : (auction.price_offer_form || 'open'),
        auction_start_datetime: formatInputDateTime(auction.auction_start_datetime || auction.applications_start),
        applications_start: formatInputDateTime(auction.applications_start),
        applications_end: formatInputDateTime(auction.applications_end),
        results_datetime: formatInputDateTime(auction.results_datetime),
        public_offer_periods: normalizePublicOfferPeriods(
          auction.public_offer_periods,
          formatInputDateTime(auction.applications_start)
        ),
        auction_date: formatInputDateTime(auction.auction_date),
        auction_step: formatPercentForUi(auction.auction_step),
        contest_step: auction.contest_step || '',
        status: auction.status || 'draft',
        price_decrease_rules: auction.price_decrease_rules || '',
        contest_conditions: auction.contest_conditions || '',
        deposit_account: auction.deposit_account || '',
        payment_account: auction.payment_account || '',
        deposit_amount: formatPercentForUi(auction.deposit_amount),
        deposit_deadline: auction.deposit_deadline || '',
        inspection_phone: hasInspectionPhone
          ? inspectionPhone
          : (organizerPhone || ''),
        inspection_email: hasInspectionEmail
          ? inspectionEmail
          : (organizerEmail || '')
      };

      const prefetchedLots = Array.isArray(auction?.lots_prefetched)
        ? auction.lots_prefetched
        : [];
      if (prefetchedLots.length > 0) {
        const normalizedPrefetchedLots = normalizeLotsWithFallbackNumbers(prefetchedLots).map((lot) => ({
          ...lot,
          public_offer_settings: normalizeLotPublicOfferSettings(lot?.public_offer_settings),
          public_offer_periods: normalizeLotPublicOfferPeriods(
            lot?.public_offer_periods,
            formatInputDateTime(auction?.applications_start)
          ),
          public_offer_periods_filled_once: !!lot?.public_offer_periods_filled_once,
        }));
        setLots(normalizedPrefetchedLots);

        if (String(auction?.auction_type || '') === 'public_offer') {
          if (!String(newFormData.applications_start || '').trim()) {
            const firstLotStart = getFirstLotFirstApplicationsStartFromLots(normalizedPrefetchedLots);
            if (firstLotStart) {
              newFormData.applications_start = firstLotStart;
            }
          }
          if (!String(newFormData.auction_start_datetime || '').trim()) {
            const firstIntervalStart = getMinPublicOfferIntervalStartFromLots(normalizedPrefetchedLots);
            if (firstIntervalStart) {
              newFormData.auction_start_datetime = firstIntervalStart;
            }
          }
        }
      }

      // Если в торге организатором уже выбран АУ должника — включаем этот режим
      const debtorManagerId = selectedDebtor?.arbitration_manager || selectedDebtor?.arbitration_manager_data?.id;
      const isDebtorManagerAsOrganizer =
        debtorManagerId &&
        (
          (selectedOrganizer?.type === 'arbitrage_manager' && String(selectedOrganizer?.id) === String(debtorManagerId))
          || String(auction?.organizer || '') === String(debtorManagerId)
        );
      setOrganizerSource(isDebtorManagerAsOrganizer ? 'debtor_manager' : 'directory');
      lastDirectoryOrganizerIdRef.current = isDebtorManagerAsOrganizer ? '' : (selectedOrganizer?.id || '');
      setInspectionPhoneCustom(isCustomInspectionPhone);
      setInspectionEmailCustom(isCustomInspectionEmail);
    } else {
      // Для нового торга
      newFormData = {
        title: '',
        debtor: '',
        organizer: '',
        etp: '',
        etp_host: 'bankruptcy.bepspb.ru',
        efrsb_message_id: '',
        auction_type: 'auction',
        is_repeat_trade: false,
        auction_form: 'open',
        price_offer_form: 'open',
        auction_start_datetime: '',
        applications_start: '',
        applications_end: '',
        results_datetime: '',
        public_offer_periods: normalizePublicOfferPeriods([], ''),
        auction_date: '',
        auction_step: '',
        contest_step: '',
        status: 'draft',
        price_decrease_rules: '',
        contest_conditions: '',
        deposit_account: '',
        payment_account: '',
        deposit_amount: '',
        deposit_deadline: '',
        inspection_phone: '',
        inspection_email: ''
      };
      setOrganizerSource('directory');
      lastDirectoryOrganizerIdRef.current = '';
      setInspectionPhoneCustom(false);
      setInspectionEmailCustom(false);
      setLots([]);
    }

    setFormData(newFormData);
    formDataRef.current = newFormData;
    if (typeof onDebtorAutoTitle === 'function') {
      onDebtorAutoTitle(newFormData?.debtor || '');
    }
    setInitialFormData(newFormData);
    setHasChanges(false);
    initializedFormKeyRef.current = key;
    // Для существующего торга не перезаписываем телефон при первичной инициализации формы,
    // но для нового торга первая ручная смена организатора должна сразу автозаполнить поле.
    skipInspectionPhoneSyncRef.current = !!auction?.id;
    skipInspectionEmailSyncRef.current = !!auction?.id;
    depositDeadlineAutoFilledRef.current = false;
  }, [auction, debtors, organizers, forceResetToken, onDebtorAutoTitle]);

  // Автозаполнение срока внесения задатка:
  // 1) если поле пустое;
  // 2) если поле ранее было автозаполнено и изменилась дата окончания приема заявок.
  useEffect(() => {
    if (isViewMode) return;
    const nextDeadline = buildDefaultDepositDeadline(formData?.auction_type, formData?.applications_end);
    if (!nextDeadline) return;
    setFormData((prev) => {
      const prevValue = String(prev?.deposit_deadline || '').trim();
      if (!prevValue) {
        const next = { ...(prev || {}), deposit_deadline: nextDeadline };
        formDataRef.current = next;
        depositDeadlineAutoFilledRef.current = true;
        return next;
      }
      if (!depositDeadlineAutoFilledRef.current || prevValue === nextDeadline) {
        return prev;
      }
      const next = { ...(prev || {}), deposit_deadline: nextDeadline };
      formDataRef.current = next;
      depositDeadlineAutoFilledRef.current = true;
      return next;
    });
  }, [isViewMode, formData?.deposit_deadline, formData?.auction_type, formData?.applications_end]);

  // Запоминаем последний выбранный организатор из списка (ОТ/АУ), чтобы можно было восстановить после переключения чекбокса
  useEffect(() => {
    if (organizerSource !== 'directory') return;
    if (!formData?.organizer) return;
    lastDirectoryOrganizerIdRef.current = formData.organizer;
  }, [organizerSource, formData?.organizer]);

  useEffect(() => {
    if (isViewMode) return;
    if (inspectionPhoneCustom) return;
    setFormData((prev) => {
      if (!prev) return prev;
      const current = normalizePhoneDigits(prev.inspection_phone || '');
      if (skipInspectionPhoneSyncRef.current) {
        skipInspectionPhoneSyncRef.current = false;
        // Для существующих торгов пропускаем только при уже заполненном поле.
        // Пустое поле должно автозаполняться при первой установке/смене ОТ.
        if (current) return prev;
      }
      const nextPhone = organizerPhone || '';
      if (String(prev.inspection_phone || '') === nextPhone) return prev;
      const next = { ...prev, inspection_phone: nextPhone };
      formDataRef.current = next;
      return next;
    });
  }, [organizerPhone, inspectionPhoneCustom, isViewMode]);

  useEffect(() => {
    if (isViewMode) return;
    if (inspectionEmailCustom) return;
    setFormData((prev) => {
      if (!prev) return prev;
      const current = String(prev.inspection_email || '').trim().toLowerCase();
      if (skipInspectionEmailSyncRef.current) {
        skipInspectionEmailSyncRef.current = false;
        // Для существующих торгов пропускаем только при уже заполненном поле.
        // Пустое поле должно автозаполняться при первой установке/смене ОТ.
        if (current) return prev;
      }
      const nextEmail = organizerEmail || '';
      if (String(prev.inspection_email || '').trim() === nextEmail) return prev;
      const next = { ...prev, inspection_email: nextEmail };
      formDataRef.current = next;
      return next;
    });
  }, [organizerEmail, inspectionEmailCustom, isViewMode]);

  useEffect(() => {
    if (!autoSelectDebtorId || isViewMode) return;
    setFormData((prev) => ({ ...(prev || {}), debtor: autoSelectDebtorId }));
    setShowValidation(false);
    if (typeof onDebtorAutoTitle === 'function') {
      onDebtorAutoTitle(autoSelectDebtorId);
    }
    if (typeof onAutoSelectDebtorApplied === 'function') {
      onAutoSelectDebtorApplied();
    }
  }, [autoSelectDebtorId, isViewMode, onAutoSelectDebtorApplied, onDebtorAutoTitle]);

  useEffect(() => {
    if (!autoSelectOrganizerId || isViewMode) return;
    setFormData((prev) => ({ ...(prev || {}), organizer: autoSelectOrganizerId }));
    setShowValidation(false);
    if (typeof onAutoSelectOrganizerApplied === 'function') {
      onAutoSelectOrganizerApplied();
    }
  }, [autoSelectOrganizerId, isViewMode, onAutoSelectOrganizerApplied]);

  useEffect(() => {
    if (!autoSelectEtpId || isViewMode) return;
    setFormData((prev) => ({ ...(prev || {}), etp: autoSelectEtpId }));
    if (typeof onAutoSelectEtpApplied === 'function') {
      onAutoSelectEtpApplied();
    }
  }, [autoSelectEtpId, isViewMode, onAutoSelectEtpApplied]);

  useEffect(() => {
    if (!autoSelectAccount || isViewMode) return;
    setFormData((prev) => {
      const next = { ...(prev || {}) };
      const purpose = String(autoSelectAccount?.purpose || '').toLowerCase();
      const accountId = autoSelectAccount?.id ?? null;
      if (!accountId) return prev;
      if (purpose === 'payment') {
        next.payment_account = accountId;
      } else {
        next.deposit_account = accountId;
      }
      return autofillAuctionFieldsFromAccount(next, autoSelectAccount);
    });
    if (typeof onAutoSelectAccountApplied === 'function') {
      onAutoSelectAccountApplied();
    }
  }, [autoSelectAccount, isViewMode, onAutoSelectAccountApplied, autofillAuctionFieldsFromAccount]);

  useEffect(() => {
    if (formData?.auction_type !== 'public_offer') return;

    const prevMeta = publicOfferAutoEndSyncMetaRef.current;
    const currentLastEnds = getLotLastIntervalEndsMap(lots);
    const currentLotCount = Array.isArray(lots) ? lots.length : 0;

    // На первичной инициализации формы только запоминаем снимок, ничего не перезаписываем.
    if (!prevMeta.initialized) {
      publicOfferAutoEndSyncMetaRef.current = {
        initialized: true,
        lotCount: currentLotCount,
        lastEndsByLotId: currentLastEnds,
      };
      return;
    }

    const lotCountChanged = prevMeta.lotCount !== currentLotCount;
    let lastIntervalEndChanged = false;
    const allLotIds = new Set([
      ...Object.keys(prevMeta.lastEndsByLotId || {}),
      ...Object.keys(currentLastEnds || {}),
    ]);
    allLotIds.forEach((lotId) => {
      if ((prevMeta.lastEndsByLotId?.[lotId] || '') !== (currentLastEnds?.[lotId] || '')) {
        lastIntervalEndChanged = true;
      }
    });

    publicOfferAutoEndSyncMetaRef.current = {
      initialized: true,
      lotCount: currentLotCount,
      lastEndsByLotId: currentLastEnds,
    };

    if (!lotCountChanged && !lastIntervalEndChanged) return;

    if (!Array.isArray(lots) || lots.length === 0) return;
    const autoEndDates = getPublicOfferAutoEndDatesFromLots(lots);
    setFormData((prev) => {
      if (!prev || prev.auction_type !== 'public_offer') return prev;
      const nextResults = autoEndDates.resultsDatetime || '';
      const nextApplicationsEnd = autoEndDates.applicationsEnd || '';
      if ((prev.results_datetime || '') === nextResults && (prev.applications_end || '') === nextApplicationsEnd) {
        return prev;
      }
      return {
        ...prev,
        results_datetime: nextResults,
        applications_end: nextApplicationsEnd,
      };
    });
  }, [lots, formData?.auction_type]);

  useEffect(() => {
    if (formData?.auction_type !== 'public_offer') return;
    if (!Array.isArray(lots) || lots.length === 0) return;
    const firstLotStart = getFirstLotFirstApplicationsStartFromLots(lots);
    setFormData((prev) => {
      if (!prev || prev.auction_type !== 'public_offer') return prev;
      if ((prev.applications_start || '') === (firstLotStart || '')) return prev;
      return { ...prev, applications_start: firstLotStart || '' };
    });
  }, [lots, formData?.auction_type]);

  useEffect(() => {
    if (formData?.auction_type === 'public_offer') return;
    publicOfferAutoEndSyncMetaRef.current = {
      initialized: false,
      lotCount: 0,
      lastEndsByLotId: {},
    };
  }, [formData?.auction_type]);

  useEffect(() => {
    if (formData?.auction_type !== 'public_offer') return;
    const currentLotCount = Array.isArray(lots) ? lots.length : 0;
    publicOfferAutoEndSyncMetaRef.current = {
      initialized: false,
      lotCount: currentLotCount,
      lastEndsByLotId: getLotLastIntervalEndsMap(lots),
    };
  }, [auction?.id]);

  useEffect(() => {
    if (isViewMode) return;
    const hasDeposit = !!String(formData?.deposit_account || '').trim();
    const hasPayment = !!String(formData?.payment_account || '').trim();
    if (!hasDeposit && !hasPayment) return;

    setFormData((prev) => {
      const next = { ...(prev || {}) };
      const before = JSON.stringify({
        debtor: next.debtor || '',
        organizer: next.organizer || '',
        etp: next.etp || '',
      });

      if (hasDeposit) {
        const depositAccount = (bankAccounts || []).find((acc) => String(acc?.id) === String(prev?.deposit_account || ''));
        autofillAuctionFieldsFromAccount(next, depositAccount);
      }
      if (hasPayment) {
        const paymentAccount = (bankAccounts || []).find((acc) => String(acc?.id) === String(prev?.payment_account || ''));
        autofillAuctionFieldsFromAccount(next, paymentAccount);
      }

      const after = JSON.stringify({
        debtor: next.debtor || '',
        organizer: next.organizer || '',
        etp: next.etp || '',
      });

      return before === after ? prev : next;
    });
  }, [
    isViewMode,
    formData?.deposit_account,
    formData?.payment_account,
    bankAccounts,
    autofillAuctionFieldsFromAccount,
  ]);

  // Закрытие модалки выбора типа по Esc
  useEffect(() => {
    if (!showAddOrganizerTypeModal) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      setShowAddOrganizerTypeModal(false);
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [showAddOrganizerTypeModal]);

  // Отслеживаем изменения в форме
  useEffect(() => {
    if (initialFormData) {
      const formChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      setHasChanges(formChanged);
      if (onFormChange) {
        onFormChange(formChanged);
      }
    }
  }, [formData, initialFormData, onFormChange]);

  const loadAuctionLots = async () => {
    if (!auction?.id) return;
    try {
      const response = await apiService.getAuctionLots(auction.id);
      const normalizedLots = normalizeLotsWithFallbackNumbers(response.data || []).map((lot) => ({
        ...lot,
        public_offer_settings: normalizeLotPublicOfferSettings(lot?.public_offer_settings),
        public_offer_periods: normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase),
        public_offer_periods_filled_once: !!lot?.public_offer_periods_filled_once,
      }));
      setLots(normalizedLots);
      if ((auction?.auction_type || '') === 'public_offer') {
        const firstIntervalStart = getMinPublicOfferIntervalStartFromLots(normalizedLots);
        if (firstIntervalStart) {
          setFormData((prev) => ({ ...(prev || {}), auction_start_datetime: firstIntervalStart }));
        }
      }
    } catch (error) {
      console.error('Error loading lots:', error);
    }
  };

  const loadAuctionDocuments = async () => {
    if (!auction?.id) return;
    try {
      const response = await apiService.getDocuments({ auction_id: auction.id });
      setDocuments(response.data.results || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadEtpExportHistory = async (auctionId, { silent = false } = {}) => {
    if (!auctionId) return;
    if (!silent) {
      setEtpExportsLoading(true);
    }
    try {
      const [historyResp, bindingResp] = await Promise.all([
        apiService.listEtpDraftExportsByAuction(auctionId),
        apiService.getEtpProfileBinding(),
      ]);
      setEtpExports(Array.isArray(historyResp?.data) ? historyResp.data : []);
      const bindingData = bindingResp?.data || null;
      const defaultHost = String(bindingData?.default_etp_host || 'bankruptcy.bepspb.ru').trim() || 'bankruptcy.bepspb.ru';
      const selectable = !!bindingData?.can_select_etp_host;
      const allowedHosts = Array.isArray(bindingData?.allowed_etp_hosts)
        ? bindingData.allowed_etp_hosts.map((x) => String(x || '').trim()).filter(Boolean)
        : [defaultHost];
      const uniqueAllowedHosts = [...new Set(allowedHosts.length ? allowedHosts : [defaultHost])];
      const historyHost = Array.isArray(historyResp?.data)
        ? String(historyResp.data.find((job) => String(job?.etp_host || '').trim())?.etp_host || '').trim()
        : '';
      const auctionHost = String(auction?.etp_host || '').trim();
      setCanSelectEtpHost(selectable);
      setAllowedEtpHosts(uniqueAllowedHosts);
      setEtpHostInput((prev) => {
        const candidate = auctionHost || historyHost || String(prev || '').trim() || String(bindingData?.etp_host || '').trim() || defaultHost;
        if (!selectable) return defaultHost;
        return uniqueAllowedHosts.includes(candidate) ? candidate : defaultHost;
      });
      // Логин ЭТП заполняется отдельно в карточке торгов (персистится по auction id),
      // поэтому не подмешиваем сюда значение из глобальной привязки профиля.
    } catch (error) {
      console.error('Error loading ETP export history:', error);
    } finally {
      if (!silent) {
        setEtpExportsLoading(false);
      }
    }
  };

  const hasRunningEtpExport = useMemo(
    () => etpExports.some((job) => ['queued', 'running'].includes(String(job?.status || '').toLowerCase())),
    [etpExports]
  );
  const activeEtpJob = useMemo(
    () => etpExports.find((job) => ['queued', 'running'].includes(String(job?.status || '').toLowerCase())) || null,
    [etpExports]
  );
  const activeEtpStep = useMemo(() => {
    const steps = Array.isArray(activeEtpJob?.steps) ? activeEtpJob.steps : [];
    if (!steps.length) return null;
    return steps[steps.length - 1] || null;
  }, [activeEtpJob]);
  const activeEtpProgress = useMemo(() => {
    const direct = Number(activeEtpJob?.progress_percent);
    if (Number.isFinite(direct)) {
      return Math.max(0, Math.min(100, direct));
    }
    const statusValue = String(activeEtpJob?.status || '').toLowerCase();
    if (statusValue === 'queued') return 5;
    const steps = Array.isArray(activeEtpJob?.steps) ? activeEtpJob.steps.length : 0;
    return Math.max(10, Math.min(95, 10 + steps * 6));
  }, [activeEtpJob]);

  const getEtpExportStatusText = (status) => {
    const key = String(status || '').toLowerCase();
    const labels = {
      queued: 'В очереди',
      running: 'Выполняется',
      success: 'Успешно',
      partial_success: 'Частично успешно',
      failed: 'Ошибка',
      cancelled: 'Отменено',
      pending: 'В очереди',
      done: 'Завершено',
    };
    return labels[key] || (key ? key.toUpperCase() : '-');
  };

  const getFedresursExportStatusText = (status) => {
    const key = String(status || '').toLowerCase();
    const labels = {
      queued: 'В очереди',
      running: 'Выполняется',
      success: 'Успешно',
      partial_success: 'Частично успешно',
      failed: 'Ошибка',
      cancelled: 'Отменено',
      pending: 'В очереди',
      done: 'Завершено',
    };
    return labels[key] || (key ? key.toUpperCase() : '-');
  };

  const loadFedresursExportHistory = async (auctionId, { silent = false } = {}) => {
    if (!auctionId) return;
    if (!silent) {
      setFedresursExportsLoading(true);
    }
    let loadedHistory = [];
    try {
      const historyResp = await apiService.listFedresursExportsByAuction(auctionId);
      loadedHistory = Array.isArray(historyResp?.data) ? historyResp.data : [];
      setFedresursExports(loadedHistory);
    } catch (error) {
      console.error('Error loading Fedresurs export history:', error);
    } finally {
      if (!silent) {
        setFedresursExportsLoading(false);
      }
    }
    return loadedHistory;
  };

  const hasRunningFedresursExport = useMemo(
    () => fedresursExports.some((job) => ['queued', 'running'].includes(String(job?.status || '').toLowerCase())),
    [fedresursExports]
  );
  const activeFedresursJob = useMemo(
    () => fedresursExports.find((job) => ['queued', 'running'].includes(String(job?.status || '').toLowerCase())) || null,
    [fedresursExports]
  );
  const activeFedresursStep = useMemo(() => {
    const steps = Array.isArray(activeFedresursJob?.steps) ? activeFedresursJob.steps : [];
    if (!steps.length) return null;
    return steps[steps.length - 1] || null;
  }, [activeFedresursJob]);
  const activeFedresursProgress = useMemo(() => {
    const direct = Number(activeFedresursJob?.progress_percent);
    if (Number.isFinite(direct)) {
      return Math.max(0, Math.min(100, direct));
    }
    const statusValue = String(activeFedresursJob?.status || '').toLowerCase();
    if (statusValue === 'queued') return 5;
    const steps = Array.isArray(activeFedresursJob?.steps) ? activeFedresursJob.steps.length : 0;
    return Math.max(10, Math.min(95, 10 + steps * 6));
  }, [activeFedresursJob]);
  const handleStartEtpExport = async () => {
    const efrsbValue = String(formData?.efrsb_message_id || auction?.efrsb_message_id || '').trim();
    if (!efrsbValue) {
      setInvalidEfrsbField(true);
      if (invalidEfrsbFieldTimeoutRef.current) {
        clearTimeout(invalidEfrsbFieldTimeoutRef.current);
      }
      invalidEfrsbFieldTimeoutRef.current = setTimeout(() => {
        setInvalidEfrsbField(false);
        invalidEfrsbFieldTimeoutRef.current = null;
      }, 2500);
      showToast('Номер сообщения в ЕФРСБ не заполнен', 'error');
      if (isViewMode && typeof onEdit === 'function') {
        setPendingEfrsbFocusAfterEdit(true);
        onEdit();
      } else {
        requestAnimationFrame(() => {
          focusEfrsbMessageInput();
        });
      }
      return;
    }

    const lotsForValidation = normalizeLotsWithFallbackNumbers(Array.isArray(lots) ? lots : []);
    const lotsWithoutClassifiers = lotsForValidation.filter((lotItem) => {
      const ids = Array.isArray(lotItem?.efrsb_classifiers)
        ? lotItem.efrsb_classifiers.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [];
      return ids.length === 0;
    });
    if (lotsWithoutClassifiers.length > 0) {
      const lotLabels = lotsWithoutClassifiers
        .map((lotItem) => `№${lotItem?.lot_number || '—'}`)
        .join(', ');
      showToast(`Не заполнен классификатор ЕФРСБ в лотах: ${lotLabels}`, 'error');
      return;
    }

    setEtpExportStarting(true);
    try {
      let exportAuctionId = auction?.id || '';
      if (!isViewMode && (!exportAuctionId || hasChanges)) {
        const saved = await performSubmit({ exit: false });
        if (saved === false) {
          return;
        }
        if (saved?.id) {
          exportAuctionId = saved.id;
        }
      }
      if (!exportAuctionId) {
        showToast('Не удалось сохранить торги перед экспортом в ЭТП', 'error');
        return;
      }

      const payload = {
        trade_id: exportAuctionId,
        etp_host: String(etpHostInput || '').trim() || 'bankruptcy.bepspb.ru',
        etp_login: String(etpLoginInput || '').trim(),
      };
      const pass = String(etpPromptPassword || '').trim();
      if (!pass) {
        throw new Error('Укажите пароль ЭТП в поле рядом с кнопкой экспорта.');
      }
      payload.etp_password = pass;
      await apiService.startEtpDraftExport(payload);
      writeEtpLoginByAuction(exportAuctionId, payload.etp_login);
      showToast('Экспорт в ЭТП запущен. Статус можно отслеживать в истории ниже.', 'success');
      setEtpPromptPassword('');
      await loadEtpExportHistory(exportAuctionId);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Не удалось запустить экспорт в ЭТП');
      showToast(message, 'error');
    } finally {
      setEtpExportStarting(false);
    }
  };

  const handleStartFedresursExport = async () => {
    setFedresursExportStarting(true);
    try {
      let exportAuctionId = auction?.id || '';
      if (!isViewMode && (!exportAuctionId || hasChanges)) {
        const saved = await performSubmit({ exit: false });
        if (saved === false) {
          return;
        }
        if (saved?.id) {
          exportAuctionId = saved.id;
        }
      }
      if (!exportAuctionId) {
        showToast('Не удалось сохранить торги перед экспортом в Федресурс', 'error');
        return;
      }

      const requestPayload = {};
      const pass = String(fedresursPromptPassword || '');
      if (!pass) {
        throw new Error('Укажите пароль Федресурса в поле рядом с кнопкой добавления.');
      }

      let lotsForVariables = lots || [];
      if (exportAuctionId) {
        try {
          const lotsResponse = await apiService.getAuctionLots(exportAuctionId);
          lotsForVariables = lotsResponse?.data || [];
        } catch (lotsError) {
          console.error('Error loading lots for Fedresurs notice:', lotsError);
        }
      }

      requestPayload.fedresurs_message_html = await buildFedresursNoticeHtmlFromTemplate({
        auction: { ...(auction || {}), ...(formDataRef.current || formData || {}), id: exportAuctionId },
        debtors,
        organizers,
        lots: lotsForVariables,
        etps,
        templates,
        variablesData: getDocumentVariablesData(
          { ...(auction || {}), ...(formDataRef.current || formData || {}), id: exportAuctionId },
          debtors,
          organizers,
          lotsForVariables,
          etps
        ),
      });
      requestPayload.fedresurs_password = pass;
      requestPayload.fedresurs_login = String(fedresursLoginInput || '');
      writeFedresursLoginByAuction(exportAuctionId, requestPayload.fedresurs_login);

      const resp = await apiService.startFedresursExport(exportAuctionId, requestPayload);
      const payload = resp?.data || {};
      if (payload?.ok) {
        if (payload?.job_id) {
          trackedFedresursJobIdsRef.current.add(String(payload.job_id));
        }
        showToast(payload?.message || 'Задание Федресурса поставлено в очередь.', 'success');
        setFedresursPromptPassword('');
      } else {
        const safeMessage = getApiErrorMessage(
          { response: { data: payload?.message } },
          'Не удалось выполнить экспорт в Федресурс. Проверьте статус в истории ниже.'
        );
        showToast(safeMessage, 'error');
      }
      const history = await loadFedresursExportHistory(exportAuctionId);
      if (!payload?.ok) {
        const diagnostic = buildFedresursDiagnosticMessage(history);
        if (diagnostic) {
          showToast(`Диагностика: ${diagnostic}`, 'info');
        }
      }
    } catch (error) {
      const history = auction?.id ? await loadFedresursExportHistory(auction.id) : [];
      const hasServerRunningJob = Array.isArray(history)
        && history.some((job) => ['queued', 'running'].includes(String(job?.status || '').toLowerCase()));
      if (hasServerRunningJob && isGatewayTimeoutError(error)) {
        showToast('Запрос принят сервером и выполняется. Следите за статусом в истории ниже.', 'info');
      } else {
        const message = getApiErrorMessage(error, 'Не удалось выполнить экспорт в Федресурс');
        showToast(message, 'error');
        const diagnostic = buildFedresursDiagnosticMessage(history);
        if (diagnostic) {
          showToast(`Диагностика: ${diagnostic}`, 'info');
        }
      }
    } finally {
      setFedresursExportStarting(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await apiService.getDocumentTemplates();
      const templates = response.data.results || [];
      setTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  useEffect(() => {
    if (!auction?.id) {
      setEtpExports([]);
      setEtpHostInput('bankruptcy.bepspb.ru');
      setCanSelectEtpHost(false);
      setAllowedEtpHosts(['bankruptcy.bepspb.ru']);
      setEtpLoginInput('');
      setFedresursExports([]);
      setFedresursLoginInput('');
      setFedresursPromptPassword('');
      return;
    }
    setEtpLoginInput(readEtpLoginByAuction(auction.id));
    setFedresursLoginInput(readFedresursLoginByAuction(auction.id));
    setFedresursPromptPassword('');
    loadEtpExportHistory(auction.id);
    loadFedresursExportHistory(auction.id);
  }, [auction?.id, readEtpLoginByAuction, readFedresursLoginByAuction]);

  useEffect(() => {
    if (!auction?.id || !hasRunningEtpExport) return undefined;
    const timer = setInterval(() => {
      loadEtpExportHistory(auction.id, { silent: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [auction?.id, hasRunningEtpExport]);

  useEffect(() => {
    if (!auction?.id || !hasRunningFedresursExport) return undefined;
    const timer = setInterval(() => {
      loadFedresursExportHistory(auction.id, { silent: true });
    }, 2000);
    return () => clearInterval(timer);
  }, [auction?.id, hasRunningFedresursExport]);

  const handleViewDocument = async (docId) => {
    try {
      const response = await apiService.getDocument(docId);
      setViewingDocument(response.data);
      setShowDocumentViewer(true);
    } catch (error) {
      console.error('Error viewing document:', error);
      showToast('Ошибка при открытии документа', 'error');
    }
  };

  const handleSaveDocument = async (documentId, updatedData, { exit = false } = {}) => {
    try {
      await apiService.patchDocument(documentId, updatedData);

      // Загружаем полный документ с обновленными данными
      const fullDocumentResponse = await apiService.getDocument(documentId);
      setViewingDocument(fullDocumentResponse.data);

      // Обновляем список документов торгов
      await loadAuctionDocuments();

      showToast('Документ успешно сохранен', 'success');

      if (exit) {
        setShowDocumentViewer(false);
        setViewingDocument(null);
      }
    } catch (error) {
      console.error('Error saving document:', error);
      showToast('Ошибка при сохранении документа', 'error');
      throw error;
    }
  };


  const handleDeleteDocument = async (docId) => {
    showConfirmDialog(
      'Удаление документа',
      'Вы действительно хотите удалить этот документ?',
      async () => {
        try {
          await apiService.deleteDocument(docId);
          await loadAuctionDocuments();
          // Обновляем статистику в основном списке торгов
          if (onAuctionUpdate && auction?.id) {
            onAuctionUpdate(auction.id);
          }
          showToast('Документ успешно удален', 'success');
        } catch (error) {
          console.error('Error deleting document:', error);
          showToast('Ошибка при удалении документа', 'error');
        }
      }
    );
  };

  const handleDownloadWord = async (docId, docTitle) => {
    try {
      const documentResponse = await apiService.getDocument(docId);
      const parsedDoc = extractTemplateMeta(documentResponse.data?.content || '');
      const documentContent = parsedDoc.content || '';
      const headerContent = documentResponse.data?.header_content || '';
      const footerContent = documentResponse.data?.footer_content || '';

      // Подставляем переменные из данных торгов
      const variablesData = getDocumentVariablesData(auction, debtors, organizers, lots, etps);
      const processedContent = substituteTemplateVariables(documentContent, {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData
      });
      const processedHeaderContent = substituteTemplateVariables(headerContent || parsedDoc.header_content || '', {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData,
      });
      const processedFooterContent = substituteTemplateVariables(footerContent || parsedDoc.footer_content || '', {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData,
      });

      const { margins, bodyHTML } = extractMarginsAndBody(processedContent);
      const htmlContent = bodyHTML || processedContent;

      // Формируем имя файла только из названия документа
      const fileName = `${docTitle}.docx`;

      await exportToDocx(htmlContent, fileName.replace(/\.docx$/i, ''), margins || {}, {
        headerContent: processedHeaderContent,
        footerContent: processedFooterContent,
        headerGapPx: parsedDoc.header_gap_px,
        footerGapPx: parsedDoc.footer_gap_px,
      });

      showToast('Документ успешно скачан', 'success');
    } catch (error) {
      console.error('Error downloading document:', error);
      showToast('Ошибка при скачивании документа', 'error');
    }
  };

  const loadAllLots = async () => {
    try {
      const pageSize = 200;
      let page = 1;
      let collected = [];
      // Fetch all pages to show every free lot in the attach modal.
      while (true) {
        const response = await apiService.getLots(`?page=${page}&page_size=${pageSize}`);
        const results = response.data?.results || [];
        collected = collected.concat(results);
        const hasNext = Boolean(response.data?.next);
        if (!hasNext || results.length < pageSize) {
          break;
        }
        page += 1;
      }
      setAllLots(collected);
    } catch (error) {
      console.error('Error loading all lots:', error);
    }
  };

  const loadAllAuctions = async () => {
    try {
      const response = await apiService.getAuctions();
      setAllAuctions(response.data.results || []);
    } catch (error) {
      console.error('Error loading all auctions:', error);
    }
  };


  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const normalizeEfrsbMessageId = useCallback((raw) => String(raw || '').replace(/\D/g, '').slice(0, 15), []);

  const extractFedresursDraftMessageId = useCallback((payload) => {
    const direct = normalizeEfrsbMessageId(
      payload?.draft_message_id || payload?.message_id || payload?.efrsb_message_id || ''
    );
    if (direct.length >= 6 && direct.length <= 15) {
      return direct;
    }

    const link = String(payload?.next_url || payload?.result_url || '').trim();
    if (!link) {
      return '';
    }

    const match = link.match(/[?&](?:msgId|messageId)=([0-9]{6,15})/i);
    if (!match?.[1]) {
      return '';
    }
    return normalizeEfrsbMessageId(match[1]);
  }, [normalizeEfrsbMessageId]);

  const applyEfrsbMessageIdFromFedresurs = useCallback(async (messageId) => {
    const normalized = normalizeEfrsbMessageId(messageId);
    if (!normalized || normalized.length < 6 || normalized.length > 15) {
      return false;
    }
    if (!auction?.id) {
      return false;
    }

    await apiService.patchAuction(auction.id, { efrsb_message_id: normalized });
    setFormData((prev) => {
      const next = { ...(prev || {}), efrsb_message_id: normalized };
      formDataRef.current = next;
      return next;
    });
    setInitialFormData((prev) => (prev ? { ...prev, efrsb_message_id: normalized } : prev));
    if (typeof onAuctionUpdate === 'function') {
      onAuctionUpdate(auction.id);
    }
    return true;
  }, [auction?.id, normalizeEfrsbMessageId, onAuctionUpdate]);

  const showInlineChoice = useCallback((anchorEl, message, onConfirm, confirmText = 'Да', cancelText = 'Нет', onCancel = null) => {
    const rect = anchorEl?.getBoundingClientRect?.();
    const popupWidth = 340;
    const viewportWidth = window.innerWidth || 1200;
    const viewportHeight = window.innerHeight || 800;
    let left = rect ? rect.left : Math.max(8, Math.round((viewportWidth - popupWidth) / 2));
    left = Math.max(8, Math.min(left, viewportWidth - popupWidth - 8));
    let top = rect ? (rect.bottom + 8) : 120;
    top = Math.max(8, Math.min(top, viewportHeight - 130));
    setInlineChoice({
      isOpen: true,
      top,
      left,
      message,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
    });
  }, []);

  const hideInlineChoice = useCallback(() => {
    setInlineChoice((prev) => ({ ...prev, isOpen: false }));
  }, []);

  useEffect(() => {
    (Array.isArray(fedresursExports) ? fedresursExports : []).forEach((job) => {
      const statusValue = String(job?.status || '').toLowerCase();
      if (job?.id && ['queued', 'running'].includes(statusValue)) {
        trackedFedresursJobIdsRef.current.add(String(job.id));
      }
    });
    const completedJob = (Array.isArray(fedresursExports) ? fedresursExports : []).find((job) => {
      const statusValue = String(job?.status || '').toLowerCase();
      return job?.id
        && trackedFedresursJobIdsRef.current.has(String(job.id))
        && ['success', 'partial_success'].includes(statusValue)
        && extractFedresursDraftMessageId(job);
    });
    const fedresursMessageId = extractFedresursDraftMessageId(completedJob || {});
    if (!fedresursMessageId || processedFedresursDraftIdsRef.current.has(fedresursMessageId)) {
      return;
    }
    processedFedresursDraftIdsRef.current.add(fedresursMessageId);

    const currentEfrsbId = normalizeEfrsbMessageId(
      formDataRef.current?.efrsb_message_id || auction?.efrsb_message_id || ''
    );
    if (currentEfrsbId !== fedresursMessageId) {
      applyEfrsbMessageIdFromFedresurs(fedresursMessageId)
        .then((updated) => {
          if (!updated) return;
          if (currentEfrsbId) {
            showToast(`Номер сообщения в ЕФРСБ обновлен: ${currentEfrsbId} -> ${fedresursMessageId}`, 'success');
          } else {
            showToast(`Номер сообщения в ЕФРСБ установлен: ${fedresursMessageId}`, 'success');
          }
        })
        .catch((e) => {
          const message = getApiErrorMessage(e, 'Не удалось сохранить номер сообщения в ЕФРСБ');
          showToast(message, 'error');
        });
    }
  }, [fedresursExports, auction?.efrsb_message_id, extractFedresursDraftMessageId, normalizeEfrsbMessageId, applyEfrsbMessageIdFromFedresurs]);

  useEffect(() => {
    inlineChoiceRef.current = inlineChoice;
  }, [inlineChoice]);

  useEffect(() => {
    if (!inlineChoice?.isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      // Не даем Toast мгновенно закрыться тем же Esc (особенно на keyup).
      try {
        window.__AOT_TOAST_ESC_IGNORE_UNTIL__ = Date.now() + 700;
      } catch (_) {}
      setInlineChoice((prev) => ({ ...prev, isOpen: false }));
      const cancelHandler = inlineChoiceRef.current?.onCancel;
      if (typeof cancelHandler === 'function') {
        setTimeout(() => {
          cancelHandler();
        }, 0);
      }
    };
    // Capture phase blocks outer modal ESC-handlers.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [inlineChoice?.isOpen]);

  const handleGlobalPublicOfferDateFieldFocus = useCallback((key, value) => {
    globalPublicOfferDateFocusValuesRef.current[key] = sanitizeDateTimeLocalInput(value || '', { allowEmpty: true });
    setInvalidGlobalPublicOfferFields((prev) => {
      if (!prev[key]) return prev;
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
    if (invalidGlobalPublicOfferFieldTimeoutsRef.current[key]) {
      clearTimeout(invalidGlobalPublicOfferFieldTimeoutsRef.current[key]);
      delete invalidGlobalPublicOfferFieldTimeoutsRef.current[key];
    }
  }, []);

  const handleGlobalPublicOfferDateFieldBlur = useCallback((key, value) => {
    if (key !== 'applications_start' && key !== 'auction_start_datetime') return;
    const before = sanitizeDateTimeLocalInput(globalPublicOfferDateFocusValuesRef.current[key] || '', { allowEmpty: true });
    const after = sanitizeDateTimeLocalInput(value || '', { allowEmpty: true });
    delete globalPublicOfferDateFocusValuesRef.current[key];

    if (before === after) return;
    if (!after || !parseLocalDateTime(after)) return;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayStart = formatInputDateTime(now);
    if (compareInputDateTime(after, todayStart) < 0) {
      setInvalidGlobalPublicOfferFields((prev) => ({ ...(prev || {}), [key]: true }));
      if (invalidGlobalPublicOfferFieldTimeoutsRef.current[key]) {
        clearTimeout(invalidGlobalPublicOfferFieldTimeoutsRef.current[key]);
      }
      invalidGlobalPublicOfferFieldTimeoutsRef.current[key] = setTimeout(() => {
        setInvalidGlobalPublicOfferFields((prev) => {
          if (!prev[key]) return prev;
          const next = { ...(prev || {}) };
          delete next[key];
          return next;
        });
        delete invalidGlobalPublicOfferFieldTimeoutsRef.current[key];
      }, 2500);
      setFormData((prev) => ({ ...(prev || {}), [key]: before }));
      showToast(
        key === 'auction_start_datetime'
          ? 'Дата начала первого интервала не может быть раньше сегодняшней даты.'
          : 'Дата начала приема заявок не может быть раньше сегодняшней даты.',
        'error'
      );
    }
  }, [showToast]);

  useEffect(() => {
    if (formData?.auction_type !== 'public_offer') return;
    const firstStart = toInputDateTimeString(formData?.auction_start_datetime || '');
    if (!firstStart) return;
    setLots((prev) => (prev || []).map((lot) => {
      const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, firstStart);
      if (!rows.length) return lot;
      const settings = normalizeLotPublicOfferSettings(lot?.public_offer_settings);
      const firstRow = { ...(rows[0] || {}) };
      const nextStart = firstStart;
      if (firstRow.interval_start_datetime === nextStart) return lot;
      firstRow.interval_start_datetime = nextStart;
      if (settings?.applications_start_matches_interval) {
        firstRow.applications_start_datetime = nextStart;
      } else if (
        firstRow.applications_start_datetime &&
        compareInputDateTime(firstRow.applications_start_datetime, nextStart) < 0
      ) {
        firstRow.applications_start_datetime = nextStart;
      }
      const nextRows = [...rows];
      nextRows[0] = firstRow;
      return { ...lot, public_offer_periods: nextRows };
    }));
  }, [formData?.auction_start_datetime, formData?.auction_type]);

  const loadAuctionReminders = async (auctionId) => {
    try {
      const resp = await apiService.getReminders(`?auction=${auctionId}`);
      const items = resp.data?.results || resp.data || [];
      return items;
    } catch (err) {
      console.error('Ошибка загрузки напоминаний', err);
      return [];
    }
  };

  const openReminderForEvent = async (eventType) => {
    if (!auction?.id) {
      showToast('Сначала сохраните торги, чтобы добавить напоминание', 'error');
      return;
    }
    const existing = await loadAuctionReminders(auction.id);
    setReminderInitial({
      auction_id: auction.id,
      event_type: eventType,
      offsets_minutes: [],
      channel_email: true,
      channel_push: true,
      existing_reminders: existing,
    });
    setReminderModalOpen(true);
  };

  const handleReminderSubmit = async (payload) => {
    try {
      await apiService.createReminder({
        auction: payload.auction_id,
        event_type: payload.event_type,
        offsets_minutes: payload.offsets_minutes || [],
        channel_email: payload.channel_email,
        channel_push: payload.channel_push,
      });
      showToast('Напоминание создано');
      setReminderModalOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Ошибка создания напоминания: ' + msg, 'error');
    }
  };

  // Если выбран режим "АУ как организатор" — синхронизируем organizer с АУ должника
  useEffect(() => {
    if (organizerSource !== 'debtor_manager') return;
    setFormData((prev) => ({
      ...(prev || {}),
      organizer: debtorManagerId || '',
    }));
  }, [organizerSource, debtorManagerId]);

  const performSubmit = async ({ exit } = { exit: false }) => {
    if (isSaving) {
      return false;
    }
    setShowValidation(true);

    if (hasDepositAccountOwnershipMismatch) {
      showToast(
        'Счет для задатка не соответствует выбранным параметрам торгов. Выберите другой счет или скорректируйте владельца счета.',
        'error'
      );
      return false;
    }

    const resolvedOrganizerId = organizerSource === 'debtor_manager'
      ? debtorManagerId
      : formData?.organizer;
    if (!String(resolvedOrganizerId || '').trim()) {
      showToast('Ошибка при создании торгов: поле Организатор торгов не может быть пустым.', 'error');
      scrollToOrganizerField();
      return false;
    }

    // На этапе сохранения карточки торгов не блокируем из-за незаполненных интервалов
    // публичного предложения. Проверка обязательных дат выполняется при создании документов.

    const tempLots = lots.filter(lot => lot.isTemporary);
    const attachedLots = lots.filter(lot => lot.isAttached);
    const persistExportBindings = async () => {
      const etpHost = String(etpHostInput || '').trim() || 'bankruptcy.bepspb.ru';
      const etpLogin = String(etpLoginInput || '').trim();
      const fedresursLogin = String(fedresursLoginInput || '');
      const settle = [];

      if (etpLogin) {
        settle.push(
          apiService.saveEtpProfileBinding({
            etp_host: etpHost,
            etp_login: etpLogin,
            password_mode: 'prompt_each_time',
          })
        );
      }

      settle.push(
        apiService.saveFedresursBinding({
          fedresurs_login: fedresursLogin,
          fedresurs_password_mode: 'prompt_each_time',
        })
      );

      const results = await Promise.allSettled(settle);
      const rejected = results.filter((item) => item.status === 'rejected');
      if (rejected.length > 0) {
        showToast('Торги сохранены, но не удалось сохранить логины Федресурса/ЭТП.', 'info');
      }
    };

    try {
      setIsSaving(true);
      const submitData = { ...(formDataRef.current || formData || {}) };
      submitData.etp_host = String(etpHostInput || '').trim() || 'bankruptcy.bepspb.ru';
      const externalTitle = String(externalTitleValue || '').trim();
      if (externalTitle) {
        submitData.title = externalTitle;
      }
      if (organizerSource === 'debtor_manager') {
        submitData.organizer = debtorManagerId || '';
      }

      const result = await onSave(submitData, { tempLots, attachedLots, lots }, { exit });

      if (result !== false) {
        await persistExportBindings();
        const savedSnapshot = JSON.parse(JSON.stringify(submitData));
        setFormData(savedSnapshot);
        setInitialFormData(savedSnapshot);
        setHasChanges(false);
        setShowValidation(false);
        if (onFormChange) {
          onFormChange(false);
        }
      }

      return result;
    } catch (error) {
      console.error('Ошибка при сохранении торгов:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    performSubmit({ exit: false });
  };

  function normalizeEntityId(rawId) {
    const value = String(rawId ?? '').trim();
    if (!value) return '';
    return /^\d+$/.test(value) ? Number(value) : value;
  }

  function autofillAuctionFieldsFromAccount(nextFormData, account) {
    if (!nextFormData || !account) return nextFormData;
    const ownerType = String(account?.owner_type || '').trim().toLowerCase();
    const ownerIdRaw = account?.owner_id ?? account?.ownerId ?? '';
    const ownerId = normalizeEntityId(ownerIdRaw);
    if (!ownerType || !ownerId) return nextFormData;

    if (ownerType === 'debtor' && !String(nextFormData?.debtor || '').trim()) {
      nextFormData.debtor = ownerId;
    }
    if (ownerType === 'organizer' && !String(nextFormData?.organizer || '').trim()) {
      nextFormData.organizer = ownerId;
    }
    if (ownerType === 'platform' && !String(nextFormData?.etp || '').trim()) {
      nextFormData.etp = ownerId;
    }

    return nextFormData;
  }

  const updateFormData = (field, value) => {
    if (['auction_start_datetime', 'applications_start', 'applications_end', 'auction_date', 'results_datetime'].includes(field)) {
      value = sanitizeDateTimeLocalInput(value, { allowEmpty: true });
    }

    if (field === 'deposit_amount') {
      const rawUi = String(value ?? '').replace(/\./g, ',').trim();
      if (!rawUi) {
        setDepositAmountWarning('');
      } else {
        if (!/^\d{0,2}(?:,\d{0,2})?$/.test(rawUi)) {
          return;
        }
        const amount = Number(rawUi.replace(',', '.'));
        if (!Number.isNaN(amount) && amount > 20) {
          setDepositAmountWarning('По законам РФ задаток не может превышать 20%.');
          return;
        }
        setDepositAmountWarning('');
        value = rawUi;
      }
    }
    if (field === 'auction_step') {
      const rawUi = String(value ?? '').replace(/\./g, ',').trim();
      if (!rawUi) {
        setAuctionStepWarning('');
      } else {
        // До 2 цифр до точки и до 5 знаков после запятой.
        if (!/^\d{0,2}(?:,\d{0,5})?$/.test(rawUi)) {
          return;
        }
        if (rawUi === ',' || rawUi === '00') {
          return;
        }
        const amount = Number(rawUi.replace(',', '.'));
        if (!Number.isNaN(amount) && amount > 10) {
          setAuctionStepWarning('По законам РФ шаг аукциона должен быть от 5% до 10%.');
          return;
        }
        setAuctionStepWarning(!Number.isNaN(amount) && amount < 5 ? 'По законам РФ шаг аукциона должен быть от 5% до 10%.' : '');
        value = rawUi;
      }
    }
    if (field === 'efrsb_message_id') {
      value = String(value ?? '').replace(/\D/g, '').slice(0, 15);
      if (invalidEfrsbField) {
        setInvalidEfrsbField(false);
      }
      if (invalidEfrsbFieldTimeoutRef.current) {
        clearTimeout(invalidEfrsbFieldTimeoutRef.current);
        invalidEfrsbFieldTimeoutRef.current = null;
      }
    }

    // Важно: используем functional update, чтобы не перетирать ввод устаревшим состоянием.
    setFormData((prev) => {
      const updatedData = { ...(prev || {}), [field]: value };
      if (field === 'deposit_deadline') {
        depositDeadlineAutoFilledRef.current = false;
      }

      if ((field === 'deposit_account' || field === 'payment_account') && value) {
        const selectedAccount = (bankAccounts || []).find((acc) => String(acc?.id) === String(value));
        autofillAuctionFieldsFromAccount(updatedData, selectedAccount);
      }

      // Автоматически устанавливаем открытую форму для публичного предложения
      if (field === 'auction_type' && value === 'public_offer') {
        updatedData.price_offer_form = 'open';
        if (!updatedData.auction_start_datetime) {
          updatedData.auction_start_datetime = updatedData.applications_start || formatInputDateTime(new Date());
        }
      }

      // Убираем показ валидации если пользователь заполнил обязательное поле
      if (showValidation && (field === 'debtor' || field === 'organizer') && value) {
        setShowValidation(false);
      }

      if (field === 'debtor' && typeof onDebtorAutoTitle === 'function') {
        onDebtorAutoTitle(value);
      }

      formDataRef.current = updatedData;
      return updatedData;
    });
  };

  const isSameLotId = useCallback((a, b) => String(a ?? '') === String(b ?? ''), []);

  const updateLotPublicOfferSettings = useCallback((lotId, key, value) => {
    setLots((prev) => (prev || []).map((lot) => {
      if (!isSameLotId(lot?.id, lotId)) return lot;
      const settings = normalizeLotPublicOfferSettings(lot?.public_offer_settings);
      return {
        ...lot,
        public_offer_settings: {
          ...settings,
          [key]: value,
        },
      };
    }));
  }, [isSameLotId]);

  const updateLotPublicOfferPeriodField = useCallback((lotId, rowIndex, key, value) => {
    setLots((prev) => (prev || []).map((lot) => {
      if (!isSameLotId(lot?.id, lotId)) return lot;
      const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase);
      if (!rows[rowIndex]) return lot;
      const settings = normalizeLotPublicOfferSettings(lot?.public_offer_settings);
      const nextRows = [...rows];
      const row = { ...(nextRows[rowIndex] || {}) };
      const isDateTimeField = [
        'interval_start_datetime',
        'applications_start_datetime',
        'applications_end_datetime',
        'interval_end_datetime',
      ].includes(key);
      if (key === 'reduction_value') {
        row[key] = sanitizeReductionInput(value, settings?.reduction_type, { allowEmpty: true });
      } else if (['interval_price', 'deposit_amount'].includes(key)) {
        row[key] = sanitizeDecimalInput(value, { allowEmpty: true });
      } else if (isDateTimeField) {
        row[key] = sanitizeDateTimeLocalInput(value, { allowEmpty: true });
      } else {
        row[key] = value;
      }
      if (key === 'interval_start_datetime' && settings.applications_start_matches_interval) {
        row.applications_start_datetime = row.interval_start_datetime || '';
      }
      if (key === 'interval_end_datetime' && settings.applications_end_matches_interval) {
        row.applications_end_datetime = row.interval_end_datetime || '';
      }
      nextRows[rowIndex] = row;

      let finalRows = nextRows;
      // Проверку и подтверждение делаем на blur, чтобы не мешать вводу даты.

      return {
        ...lot,
        public_offer_periods: finalRows,
        public_offer_periods_filled_once: finalRows.some((r) => !!String(r?.interval_price || r?.comment || r?.applications_end_datetime || '').trim()) || !!lot?.public_offer_periods_filled_once,
      };
    }));
  }, [publicOfferStartBase, isSameLotId]);

  const handleLotPublicOfferDateFieldFocus = useCallback((lotId, rowIndex, key, value) => {
    const mapKey = `${lotId}:${rowIndex}:${key}`;
    publicOfferDateFocusValuesRef.current[mapKey] = String(value || '');

    const invalidFieldKey = getPublicOfferFieldKey(lotId, rowIndex, key);
    setInvalidPublicOfferFields((prev) => {
      if (persistentInvalidPublicOfferFieldsRef.current[invalidFieldKey]) return prev;
      if (!prev[invalidFieldKey]) return prev;
      const next = { ...(prev || {}) };
      delete next[invalidFieldKey];
      return next;
    });

    if (!persistentInvalidPublicOfferFieldsRef.current[invalidFieldKey] && invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]) {
      clearTimeout(invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]);
      delete invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey];
    }
  }, [getPublicOfferFieldKey]);

  const handleLotPublicOfferDateFieldBlur = useCallback((lotId, rowIndex, key, value, anchorEl = null) => {
    const mapKey = `${lotId}:${rowIndex}:${key}`;
    const before = sanitizeDateTimeLocalInput(publicOfferDateFocusValuesRef.current[mapKey] || '', { allowEmpty: true });
    const after = sanitizeDateTimeLocalInput(value || '', { allowEmpty: true });
    delete publicOfferDateFocusValuesRef.current[mapKey];

    if (before === after) return;

    const currentLot = (lotsRef.current || []).find((item) => isSameLotId(item?.id, lotId));
    const currentSettings = normalizeLotPublicOfferSettings(currentLot?.public_offer_settings);
    const currentRows = normalizeLotPublicOfferPeriods(currentLot?.public_offer_periods, publicOfferStartBase);
    if (!currentRows[rowIndex]) return;

    const nextRows = [...currentRows];
    const nextRow = { ...(nextRows[rowIndex] || {}) };
    nextRow[key] = after;
    if (key === 'interval_start_datetime' && currentSettings.applications_start_matches_interval) {
      nextRow.applications_start_datetime = nextRow.interval_start_datetime || '';
    }
    if (key === 'interval_end_datetime' && currentSettings.applications_end_matches_interval) {
      nextRow.applications_end_datetime = nextRow.interval_end_datetime || '';
    }
    nextRows[rowIndex] = nextRow;

    const clearInvalidField = (fieldKey) => {
      const invalidFieldKey = getPublicOfferFieldKey(lotId, rowIndex, fieldKey);
      if (invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]) {
        clearTimeout(invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]);
        delete invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey];
      }
      delete persistentInvalidPublicOfferFieldsRef.current[invalidFieldKey];
      setInvalidPublicOfferFields((prev) => {
        if (!prev[invalidFieldKey]) return prev;
        const next = { ...(prev || {}) };
        delete next[invalidFieldKey];
        return next;
      });
    };

    const markInvalidField = (fieldKey, options = {}) => {
      const { persistent = false } = options || {};
      const invalidFieldKey = getPublicOfferFieldKey(lotId, rowIndex, fieldKey);
      setInvalidPublicOfferFields((prev) => ({ ...(prev || {}), [invalidFieldKey]: true }));
      if (persistent) {
        persistentInvalidPublicOfferFieldsRef.current[invalidFieldKey] = true;
        if (invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]) {
          clearTimeout(invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]);
          delete invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey];
        }
        return;
      }
      delete persistentInvalidPublicOfferFieldsRef.current[invalidFieldKey];
      if (invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]) {
        clearTimeout(invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey]);
      }
      invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey] = setTimeout(() => {
        setInvalidPublicOfferFields((prev) => {
          if (!prev[invalidFieldKey]) return prev;
          const next = { ...(prev || {}) };
          delete next[invalidFieldKey];
          return next;
        });
        delete invalidPublicOfferFieldTimeoutsRef.current[invalidFieldKey];
      }, 2500);
    };

    const rollbackRowAndToast = (fieldKey, message) => {
      markInvalidField(fieldKey);
      setLots((prev) => (prev || []).map((lot) => {
        if (!isSameLotId(lot?.id, lotId)) return lot;
        const restoreRows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase);
        if (!restoreRows[rowIndex]) return lot;
        const restoredRow = { ...(restoreRows[rowIndex] || {}) };
        restoredRow[key] = before;
        if (key === 'interval_start_datetime' && currentSettings.applications_start_matches_interval) {
          restoredRow.applications_start_datetime = before;
        }
        if (key === 'interval_end_datetime' && currentSettings.applications_end_matches_interval) {
          restoredRow.applications_end_datetime = before;
        }
        restoreRows[rowIndex] = restoredRow;
        return { ...lot, public_offer_periods: restoreRows };
      }));
      showToast(message, 'error');
    };

    // Всегда сначала применяем фактическое значение в таблицу.
    setLots((prev) => (prev || []).map((lot) => {
      if (!isSameLotId(lot?.id, lotId)) return lot;
      return { ...lot, public_offer_periods: nextRows };
    }));

    if (key === 'interval_start_datetime' && after && parseLocalDateTime(after)) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const todayStart = formatInputDateTime(now);
      if (compareInputDateTime(after, todayStart) < 0) {
        rollbackRowAndToast('interval_start_datetime', 'Дата начала интервала не может быть раньше сегодняшней даты.');
        return;
      }
    }

    if (
      key === 'interval_start_datetime' &&
      rowIndex > 0 &&
      nextRow.interval_start_datetime &&
      nextRows?.[rowIndex - 1]?.interval_end_datetime &&
      compareInputDateTime(nextRow.interval_start_datetime, nextRows[rowIndex - 1].interval_end_datetime) < 0
    ) {
      rollbackRowAndToast('interval_start_datetime', 'Дата начала интервала не может быть раньше даты окончания предыдущего интервала.');
      return;
    }

    if (
      key === 'applications_start_datetime' &&
      rowIndex === 0 &&
      formData?.applications_start &&
      nextRow.applications_start_datetime &&
      compareInputDateTime(nextRow.applications_start_datetime, formData.applications_start) < 0
    ) {
      rollbackRowAndToast('applications_start_datetime', 'Для первого интервала дата начала приема заявок не может быть раньше даты начала приема заявок.');
      return;
    }

    if (
      nextRow.interval_start_datetime &&
      nextRow.applications_start_datetime &&
      compareInputDateTime(nextRow.applications_start_datetime, nextRow.interval_start_datetime) < 0
    ) {
      const fieldKey = key === 'interval_start_datetime' ? 'interval_start_datetime' : 'applications_start_datetime';
      rollbackRowAndToast(fieldKey, 'Дата начала приема заявок на интервале не может быть раньше даты начала интервала.');
      return;
    }

    if (
      nextRow.applications_start_datetime &&
      nextRow.applications_end_datetime &&
      compareInputDateTime(nextRow.applications_end_datetime, nextRow.applications_start_datetime) < 0
    ) {
      const fieldKey = key === 'interval_end_datetime' ? 'interval_end_datetime' : 'applications_end_datetime';
      rollbackRowAndToast(fieldKey, 'Дата окончания приема заявок на интервале не может быть раньше даты начала приема заявок на этом интервале.');
      return;
    }

    if (
      nextRow.applications_end_datetime &&
      nextRow.interval_end_datetime &&
      compareInputDateTime(nextRow.interval_end_datetime, nextRow.applications_end_datetime) < 0
    ) {
      if (key === 'applications_end_datetime') {
        markInvalidField('applications_end_datetime', { persistent: true });
        showToast('Дата окончания приема заявок на интервале не может быть позже даты окончания интервала.', 'error');
        return;
      }
      rollbackRowAndToast('interval_end_datetime', 'Дата окончания интервала не может быть раньше даты окончания приема заявок на интервале.');
      return;
    }
    // Если пара "окончание приема/окончание интервала" стала валидной,
    // снимаем персистентную подсветку, независимо от того, какое поле редактировали.
    if (
      nextRow.applications_end_datetime &&
      nextRow.interval_end_datetime &&
      compareInputDateTime(nextRow.interval_end_datetime, nextRow.applications_end_datetime) >= 0
    ) {
      clearInvalidField('applications_end_datetime');
    } else if (key === 'applications_end_datetime') {
      clearInvalidField('applications_end_datetime');
    }

    if (after && !parseLocalDateTime(after)) return;
    if (!hasPublicOfferDateOrderViolations(nextRows)) return;

    const preserveEditedValue = (rowsToAdjust) => {
      const adjusted = Array.isArray(rowsToAdjust) ? rowsToAdjust.map((r) => ({ ...(r || {}) })) : [];
      if (!adjusted[rowIndex]) return adjusted;
      adjusted[rowIndex][key] = after;
      if (key === 'interval_start_datetime' && currentSettings.applications_start_matches_interval) {
        adjusted[rowIndex].applications_start_datetime = after;
      }
      if (key === 'interval_end_datetime' && currentSettings.applications_end_matches_interval) {
        adjusted[rowIndex].applications_end_datetime = after;
      }
      return adjusted;
    };

    const recalcRowsPreview = recalculatePublicOfferRows(
      preserveEditedValue(nextRows),
      currentSettings,
      rowIndex
    );
    const pendingKey = `${String(lotId ?? '')}:${String(rowIndex ?? '')}:${String(key ?? '')}`;
    pendingPublicOfferRecalcRef.current[pendingKey] = {
      lotId: String(lotId ?? ''),
      rows: preserveEditedValue(recalcRowsPreview),
    };

    showInlineChoice(
      anchorEl,
      'Пересчитать интервалы с учетом корректировки?',
      () => {
        const pending = pendingPublicOfferRecalcRef.current[pendingKey];
        delete pendingPublicOfferRecalcRef.current[pendingKey];
        if (!pending?.rows || !Array.isArray(pending.rows)) return;
        setLots((recalcPrev) => (recalcPrev || []).map((recalcLot) => {
          if (!isSameLotId(recalcLot?.id, pending.lotId)) return recalcLot;
          const normalizedRows = pending.rows;
          return {
            ...recalcLot,
            public_offer_periods: normalizedRows,
            public_offer_periods_filled_once: normalizedRows.some((r) => !!String(r?.interval_price || r?.comment || r?.applications_end_datetime || '').trim()) || !!recalcLot?.public_offer_periods_filled_once,
          };
        }));
      },
      'Пересчитать',
      'Оставить',
      () => {
        delete pendingPublicOfferRecalcRef.current[pendingKey];
        rollbackRowAndToast(key, 'Без пересчета значение недопустимо: нарушается последовательность интервалов. Значение возвращено.');
      }
    );
  }, [publicOfferStartBase, getPublicOfferFieldKey, isSameLotId, showInlineChoice, showToast]);

  const handleReductionValueBlur = useCallback((lotId, rowIndex, value, anchorEl = null) => {
    const reductionMapKey = `${lotId}:${rowIndex}:reduction_value`;
    const lotForCompare = (lotsRef.current || []).find((item) => isSameLotId(item?.id, lotId));
    const settingsForCompare = normalizeLotPublicOfferSettings(lotForCompare?.public_offer_settings);
    const beforeRaw = sanitizeReductionInput(publicOfferReductionFocusValuesRef.current[reductionMapKey] || '', settingsForCompare?.reduction_type, { allowEmpty: true });
    const before = beforeRaw ? formatFixedTwoDecimals(beforeRaw) : '';
    const afterForCompareRaw = sanitizeReductionInput(value || '', settingsForCompare?.reduction_type, { allowEmpty: true });
    const after = afterForCompareRaw ? formatFixedTwoDecimals(afterForCompareRaw) : '';
    delete publicOfferReductionFocusValuesRef.current[reductionMapKey];
    if (before === after) return;

    const lot = (lotsRef.current || []).find((item) => isSameLotId(item?.id, lotId));
    const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase);
    if (!rows[rowIndex]) return;
    const settings = normalizeLotPublicOfferSettings(lot?.public_offer_settings);
    const sourceValueRaw = sanitizeReductionInput(value, settings?.reduction_type, { allowEmpty: true });
    const sourceValue = sourceValueRaw ? formatFixedTwoDecimals(sourceValueRaw) : '';

    // Фиксируем текущее значение в активной строке даже если onChange не успел стабильно отработать.
    setLots((prev) => (prev || []).map((applyLot) => {
      if (!isSameLotId(applyLot?.id, lotId)) return applyLot;
      const applyRows = normalizeLotPublicOfferPeriods(applyLot?.public_offer_periods, publicOfferStartBase);
      if (!applyRows[rowIndex]) return applyLot;
      const nextRows = [...applyRows];
      nextRows[rowIndex] = { ...(nextRows[rowIndex] || {}), reduction_value: sourceValue };
      return { ...applyLot, public_offer_periods: nextRows };
    }));

    // Для одного интервала просто сохраняем введенное значение без диалога.
    if (!sourceValue || rows.length <= 1) return;

    showInlineChoice(
      anchorEl,
      'Применить снижение к текущему и всем последующим интервалам?',
      () => {
        setLots((applyPrev) => (applyPrev || []).map((applyLot) => {
          if (!isSameLotId(applyLot?.id, lotId)) return applyLot;
          const applyRows = normalizeLotPublicOfferPeriods(applyLot?.public_offer_periods, publicOfferStartBase);
          if (!applyRows[rowIndex]) return applyLot;
          const applySettings = normalizeLotPublicOfferSettings(applyLot?.public_offer_settings);
          const rowActualValue = applyRows?.[rowIndex]?.reduction_value;
          const valueToApplyRaw = sanitizeReductionInput(
            rowActualValue || sourceValue,
            applySettings?.reduction_type,
            { allowEmpty: true }
          );
          const valueToApply = valueToApplyRaw ? formatFixedTwoDecimals(valueToApplyRaw) : '';
          if (!valueToApply) return applyLot;
          const rowsWithReduction = applyRows.map((r, idx) => (
            idx < rowIndex
              ? { ...(r || {}) }
              : { ...(r || {}), reduction_value: valueToApply }
          ));
          const recalculatedRows = recalculateLotRowsByReductionFromIndex(
            rowsWithReduction,
            applySettings,
            applyLot?.start_price,
            rowIndex
          );
          return {
            ...applyLot,
            public_offer_periods: recalculatedRows,
          };
        }));
      },
      'Применить к последующим',
      'Только этот интервал'
    );
  }, [publicOfferStartBase, showInlineChoice, isSameLotId]);

  const addLotPublicOfferPeriod = useCallback((lotId) => {
    setLots((prev) => (prev || []).map((lot) => {
      if (!isSameLotId(lot?.id, lotId)) return lot;
      const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase);
      const last = rows[rows.length - 1] || {};
      const nextRows = [
        ...rows,
        getDefaultLotPublicOfferRow(
          last.interval_end_datetime || '',
          '',
          '',
          ''
        ),
      ];
      return { ...lot, public_offer_periods: nextRows };
    }));
  }, [publicOfferStartBase, isSameLotId]);

  const removeLotPublicOfferPeriod = useCallback((lotId, rowIndex) => {
    setLots((prev) => (prev || []).map((lot) => {
      if (!isSameLotId(lot?.id, lotId)) return lot;
      const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase);
      if (rows.length <= 1 || !rows[rowIndex]) return lot;
      const nextRows = rows.filter((_, idx) => idx !== rowIndex);
      return { ...lot, public_offer_periods: nextRows };
    }));
  }, [publicOfferStartBase, isSameLotId]);

  const generateLotPublicOfferPeriods = useCallback(async (lotId, anchorEl = null, forceReplace = false, scopeLotIds = null, scopeMode = 'current') => {
    const applyToAllLots = scopeMode === 'all';
    const targetIds = Array.isArray(scopeLotIds) && scopeLotIds.length > 0
      ? scopeLotIds
      : [lotId];
    const targetLots = (lotsRef.current || []).filter((lot) => (
      applyToAllLots
        ? true
        : targetIds.some((id) => isSameLotId(lot?.id, id))
    ));
    const hasExistingIntervals = targetLots.some((targetLot) => {
      const existingRowsPreview = normalizeLotPublicOfferPeriods(targetLot?.public_offer_periods, publicOfferStartBase);
      return existingRowsPreview.some((row) => (
        // Стартовые даты часто проставляются автоматически и не считаются
        // признаком реально заполненного интервала.
        !!String(row?.applications_end_datetime || '').trim()
        || !!String(row?.interval_end_datetime || '').trim()
        || !!String(row?.reduction_value || '').trim()
        || !!String(row?.deposit_amount || '').trim()
        || !!String(row?.interval_price || '').trim()
        || !!String(row?.comment || '').trim()
      ));
    });
    if (hasExistingIntervals && !forceReplace) {
      showInlineChoice(
        anchorEl,
        (applyToAllLots || targetIds.length > 1)
          ? 'Для выбранных лотов все существующие интервалы будут заменены. Продолжить?'
          : 'Все существующие интервалы будут заменены. Продолжить?',
        () => generateLotPublicOfferPeriods(lotId, anchorEl, true, targetIds, scopeMode),
        'Заменить',
        'Отмена'
      );
      return;
    }

    const triggerLot = (lotsRef.current || []).find((lot) => isSameLotId(lot?.id, lotId));
    const triggerSettings = normalizeLotPublicOfferSettings(triggerLot?.public_offer_settings);
    const targetLot = targetLots[0] || triggerLot;
    const settingsPreview = applyToAllLots
      ? triggerSettings
      : normalizeLotPublicOfferSettings(targetLot?.public_offer_settings);
    const startDatePreview = publicOfferStartBase;
    const initialPricePreview = Number.parseFloat(String(targetLot?.start_price || '0').replace(',', '.'));
    const intervalValuePreview = Number.parseInt(String(settingsPreview?.interval_value || '0'), 10);
    const intervalUnitPreview = String(settingsPreview?.interval_unit || 'days_working');
    const modePreview = String(settingsPreview?.generation_mode || 'by_count');
    const countLimitPreview = Number.parseInt(String(settingsPreview?.intervals_count || '0'), 10);
    const cutoffPricePreview = Number.parseFloat(String(settingsPreview?.cutoff_price || '0').replace(',', '.'));

    if (!startDatePreview) {
      showToast('Укажите дату начала первого интервала перед генерацией этапов.', 'error');
      return;
    }
    if (!Number.isFinite(initialPricePreview) || initialPricePreview <= 0) {
      showToast('Для генерации этапов у лота должна быть корректная начальная цена (больше 0).', 'error');
      return;
    }
    if (!Number.isFinite(intervalValuePreview) || intervalValuePreview <= 0 || !['days_working', 'days_calendar', 'hours', 'minutes'].includes(intervalUnitPreview)) {
      showToast('Укажите корректную периодичность снижения цены (дней раб./дней календ./часов/минут).', 'error');
      return;
    }
    if (modePreview === 'by_count' && (!Number.isFinite(countLimitPreview) || countLimitPreview <= 0)) {
      showToast('Для генерации этапов в количестве укажите число интервалов больше 0.', 'error');
      return;
    }
    if (modePreview === 'to_cutoff_price' && (!Number.isFinite(cutoffPricePreview) || cutoffPricePreview <= 0)) {
      showToast('Для генерации этапов до цены отсечения укажите корректную цену отсечения (больше 0).', 'error');
      return;
    }

    let generatedAny = false;
    const sourceLots = lotsRef.current || [];
    const nextLots = [];
    for (const lot of sourceLots) {
      if (!applyToAllLots && !targetIds.some((id) => isSameLotId(lot?.id, id))) {
        nextLots.push(lot);
        continue;
      }

      const settings = applyToAllLots
        ? triggerSettings
        : normalizeLotPublicOfferSettings(lot?.public_offer_settings);
      // Генерация всегда пересоздает этапы с нуля от даты начала первого интервала.
      const startDate = publicOfferStartBase;
      const initialPrice = Number.parseFloat(String(lot?.start_price || '0').replace(',', '.'));
      const reductionValue = Number.parseFloat(String(settings?.reduction_value || '0').replace(',', '.'));
      const depositValue = Number.parseFloat(String(settings?.deposit_value || '0').replace(',', '.'));
      const intervalValue = Number.parseInt(String(settings?.interval_value || '0'), 10);
      const intervalUnit = String(settings?.interval_unit || 'days_working');
      const applicationsStartDiffMinutes = durationMinutesFromValueUnit(
        settings?.applications_start_diff_value,
        settings?.applications_start_diff_unit
      );
      const applicationsEndDiffMinutes = durationMinutesFromValueUnit(
        settings?.applications_end_diff_value,
        settings?.applications_end_diff_unit
      );
      if (!startDate || !Number.isFinite(initialPrice) || initialPrice <= 0 || !Number.isFinite(intervalValue) || intervalValue <= 0 || !['days_working', 'days_calendar', 'hours', 'minutes'].includes(intervalUnit)) {
        nextLots.push(lot);
        continue;
      }

      const rows = [];
      const maxSafeRows = 500;
      const mode = String(settings?.generation_mode || 'by_count');
      const countLimit = Number.parseInt(String(settings?.intervals_count || '0'), 10);
      const cutoffPrice = Number.parseFloat(String(settings?.cutoff_price || '0').replace(',', '.'));
      let index = 0;
      let nextStart = startDate;
      let previousPrice = initialPrice;

      while (index < maxSafeRows) {
        if (mode === 'by_count' && Number.isFinite(countLimit) && countLimit > 0 && index >= countLimit) break;

        const nextPrice = computeNextPrice({
          previousPrice,
          initialPrice,
          reductionType: settings.reduction_type,
          reductionValue,
          index,
        });
        if (!Number.isFinite(nextPrice) || nextPrice <= 0) break;

        const intervalEndRaw = await addIntervalBySettings(nextStart, settings);
        const intervalEnd = intervalEndRaw;
        if (!intervalEnd) break;
        const reductionCellValue = index === 0
          ? '0.00'
          : (settings.reduction_type === 'prev_minus_rub'
              ? toTwoDecimals(reductionValue)
              : toTwoDecimals(reductionValue));
        const deposit = computeDeposit({
          depositMethod: settings.deposit_method,
          depositValue,
          initialPrice,
          intervalPrice: nextPrice,
        });

        rows.push({
          interval_start_datetime: nextStart,
          applications_start_datetime: settings.applications_start_matches_interval
            ? nextStart
            : addMinutesToInputDate(nextStart, applicationsStartDiffMinutes),
          applications_end_datetime: settings.applications_end_matches_interval
            ? intervalEnd
            : addMinutesToInputDate(intervalEnd, -applicationsEndDiffMinutes),
          interval_end_datetime: intervalEnd,
          reduction_value: reductionCellValue,
          deposit_amount: toTwoDecimals(deposit),
          interval_price: toTwoDecimals(nextPrice),
          comment: '',
        });

        previousPrice = nextPrice;
        nextStart = intervalEnd;
        index += 1;

        if (mode === 'to_cutoff_price' && Number.isFinite(cutoffPrice) && cutoffPrice > 0 && nextPrice <= cutoffPrice) {
          break;
        }
      }

      if (!rows.length) {
        nextLots.push(lot);
        continue;
      }
      generatedAny = true;
      nextLots.push({
        ...lot,
        public_offer_settings: settings,
        public_offer_periods: rows,
        public_offer_periods_filled_once: true,
      });
    }
    setLots(nextLots);
    lotsRef.current = nextLots;
    if (!generatedAny) {
      showToast('Этапы не сгенерированы: проверьте параметры генерации и диапазон дат.', 'error');
      return;
    }
    showToast(
      (applyToAllLots || targetIds.length > 1)
        ? 'Этапы успешно сгенерированы для выбранных лотов.'
        : 'Этапы успешно сгенерированы.',
      'success'
    );
  }, [publicOfferStartBase, isSameLotId, showInlineChoice, showToast]);

  const handleGenerateLotPublicOfferPeriods = useCallback((lotId, anchorEl = null) => {
    const currentLots = (lotsRef.current || []).filter((lot) => lot && lot.id !== undefined && lot.id !== null);
    if (currentLots.length <= 1) {
      generateLotPublicOfferPeriods(lotId, anchorEl, false, [lotId]);
      return;
    }
    const allLotIds = currentLots.map((lot) => lot.id);
    showInlineChoice(
      anchorEl,
      'Сгенерировать этапы только для текущего лота или для всех лотов в торгах?',
      () => generateLotPublicOfferPeriods(lotId, anchorEl, false, [lotId]),
      'Только текущий',
      'Для всех',
      () => generateLotPublicOfferPeriods(lotId, anchorEl, false, allLotIds, 'all')
    );
  }, [generateLotPublicOfferPeriods, showInlineChoice]);

  // В публичном предложении глобальные даты задаются пользователем вручную.
  // Не синхронизируем их автоматически из таблиц лотов, чтобы не затирать введенные значения.

  const getNextAvailableLotNumber = useCallback(() => {
    const used = new Set(
      (lots || [])
        .map((lot) => parseLotNumber(lot?.lot_number))
        .filter((value) => value !== null)
    );
    let candidate = 1;
    while (used.has(candidate)) {
      candidate += 1;
    }
    return candidate;
  }, [lots]);

  const handleEditLot = (lot, fallbackLotNumber = null) => {
    const resolvedNumber = parseLotNumber(lot?.lot_number) ?? parseLotNumber(fallbackLotNumber);
    const normalizedLot = { ...(lot || {}), lot_number: resolvedNumber ?? '' };
    setLotFormInitialNumber(String(resolvedNumber ?? ''));
    setEditingLot(normalizedLot);
    setLotModalMode('edit');
    setLotFormData({
      lot_number: normalizedLot.lot_number,
      name: normalizedLot.name || '',
      description: normalizedLot.description || '',
      start_price: normalizedLot.start_price || '',
      vat_text: normalizedLot.vat_text || '',
      cadastral_number: normalizedLot.cadastral_number || '',
      location: normalizedLot.location || '',
      category: normalizedLot.category || 'real_estate',
      efrsb_classifiers: Array.isArray(normalizedLot.efrsb_classifiers)
        ? normalizedLot.efrsb_classifiers
        : [],
      pledge_creditor_satisfaction: !!normalizedLot.pledge_creditor_satisfaction,
      pledge_creditors: Array.isArray(normalizedLot.pledge_creditors) ? normalizedLot.pledge_creditors : [],
      public_offer_settings: (normalizedLot.public_offer_settings && typeof normalizedLot.public_offer_settings === 'object')
        ? normalizedLot.public_offer_settings
        : {},
      public_offer_periods: Array.isArray(normalizedLot.public_offer_periods) ? normalizedLot.public_offer_periods : [],
      public_offer_periods_filled_once: !!normalizedLot.public_offer_periods_filled_once,
    });
    setLotHasUnsavedChanges(false);
    setShowLotForm(true);
  };

  const handleOpenDocumentModal = async () => {
    let nextAuction = { ...(auction || {}), ...(formDataRef.current || formData || {}) };
    if (!isViewMode && (!auction?.id || hasChanges)) {
      const saved = await performSubmit({ exit: false });
      if (saved === false) {
        return;
      }
      nextAuction = {
        ...nextAuction,
        ...(saved || {}),
        ...(formDataRef.current || formData || {}),
        id: saved?.id || auction?.id || nextAuction.id,
      };
    }
    if (!nextAuction?.id) {
      showToast('Не удалось сохранить торги перед созданием документа', 'error');
      return;
    }
    setDocumentAuctionContext(nextAuction);
    setShowDocumentModal(true);
  };

  const handleViewLot = (lot, fallbackLotNumber = null) => {
    const resolvedNumber = parseLotNumber(lot?.lot_number) ?? parseLotNumber(fallbackLotNumber);
    const normalizedLot = { ...(lot || {}), lot_number: resolvedNumber ?? '' };
    setLotFormInitialNumber(String(resolvedNumber ?? ''));
    setEditingLot(normalizedLot);
    setLotModalMode('view');
    setLotFormData({
      lot_number: normalizedLot.lot_number,
      name: normalizedLot.name || '',
      description: normalizedLot.description || '',
      start_price: normalizedLot.start_price || '',
      vat_text: normalizedLot.vat_text || '',
      cadastral_number: normalizedLot.cadastral_number || '',
      location: normalizedLot.location || '',
      category: normalizedLot.category || 'real_estate',
      efrsb_classifiers: Array.isArray(normalizedLot.efrsb_classifiers)
        ? normalizedLot.efrsb_classifiers
        : [],
      pledge_creditor_satisfaction: !!normalizedLot.pledge_creditor_satisfaction,
      pledge_creditors: Array.isArray(normalizedLot.pledge_creditors) ? normalizedLot.pledge_creditors : [],
      public_offer_settings: (normalizedLot.public_offer_settings && typeof normalizedLot.public_offer_settings === 'object')
        ? normalizedLot.public_offer_settings
        : {},
      public_offer_periods: Array.isArray(normalizedLot.public_offer_periods) ? normalizedLot.public_offer_periods : [],
      public_offer_periods_filled_once: !!normalizedLot.public_offer_periods_filled_once,
    });
    setLotHasUnsavedChanges(false);
    setShowLotForm(true);
  };

  const resetLotForm = () => {
    setLotFormInitialNumber('');
    setLotFormData({
      lot_number: '',
      name: '',
      description: '',
      start_price: '',
      vat_text: '',
      cadastral_number: '',
      location: '',
      category: 'real_estate',
      efrsb_classifiers: [],
      pledge_creditor_satisfaction: false,
      pledge_creditors: [],
      public_offer_settings: {},
      public_offer_periods: [],
      public_offer_periods_filled_once: false,
    });
    setEditingLot(null);
    setLotModalMode(null);
    setShowLotForm(false);
    setLotHasUnsavedChanges(false);
  };

  const prepareLotPayload = (payload) => {
    const prepared = {
      ...(payload || {}),
      lot_number: parseLotNumber(payload?.lot_number),
      start_price: payload?.start_price ? parseFloat(payload.start_price) : 0,
      category: payload?.category || 'real_estate',
      efrsb_classifiers: Array.isArray(payload?.efrsb_classifiers)
        ? payload.efrsb_classifiers.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [],
      pledge_creditor_satisfaction: !!payload?.pledge_creditor_satisfaction,
      pledge_creditors: Array.isArray(payload?.pledge_creditors) ? payload.pledge_creditors : [],
      public_offer_settings: normalizeLotPublicOfferSettings(payload?.public_offer_settings),
      public_offer_periods: normalizeLotPublicOfferPeriods(payload?.public_offer_periods, publicOfferStartBase, false),
      public_offer_periods_filled_once: !!payload?.public_offer_periods_filled_once,
    };
    if (Number.isNaN(prepared.start_price)) {
      prepared.start_price = 0;
    }
    return prepared;
  };

  const forceCloseLotModal = () => {
    resetLotForm();
  };

  const handleCancelLotModal = () => {
    if (lotModalMode === 'view') {
      forceCloseLotModal();
      return;
    }
    if (lotHasUnsavedChanges) {
      showConfirmDialog(
        'Несохраненные изменения',
        'У вас есть несохраненные изменения. Закрыть окно без сохранения?',
        () => forceCloseLotModal(),
        'Закрыть без сохранения',
        'Отмена'
      );
      return;
    }
    forceCloseLotModal();
  };

  const handleSaveLot = async (payload, { exit } = {}) => {
    try {
      const prepared = prepareLotPayload(payload);
      if (!prepared.lot_number) {
        showToast('Укажите корректный номер лота (целое число больше 0)', 'error');
        return false;
      }
      const duplicateLot = (lots || []).find(
        (l) => Number(l?.lot_number) === Number(prepared.lot_number) && Number(l?.id) !== Number(editingLot?.id)
      );
      if (duplicateLot) {
        showToast(`Лот с номером ${prepared.lot_number} уже есть в карточке торгов`, 'error');
        return false;
      }
      const lotData = {
        ...prepared,
        auction: auction?.id || null,
      };

      if (formData?.auction_type === 'public_offer') {
        lotData.public_offer_settings = normalizeLotPublicOfferSettings(
          prepared?.public_offer_settings
        );
        lotData.public_offer_periods = normalizeLotPublicOfferPeriods(
          prepared?.public_offer_periods, publicOfferStartBase,
          false
        );
        lotData.public_offer_periods_filled_once = !!prepared?.public_offer_periods_filled_once
          || lotData.public_offer_periods.some((row) =>
            !!String(row?.interval_price || row?.comment || row?.applications_end_datetime || '').trim()
          );
      }

      let savedLot = null;
      let successMessage = '';

      if (editingLot) {
        if (editingLot.isTemporary) {
          savedLot = { ...editingLot, ...lotData, isTemporary: true };
          setLots((prev) => normalizeLotsWithFallbackNumbers((prev || []).map((l) => (l.id === editingLot.id ? savedLot : l))));
          successMessage = exit ? 'Лот обновлен (будет сохранен вместе с торгом)' : 'Лот сохранен';
        } else {
          const resp = await apiService.updateLot(editingLot.id, lotData);
          savedLot = resp.data;
          setLots((prev) => normalizeLotsWithFallbackNumbers((prev || []).map((l) => (l.id === editingLot.id ? savedLot : l))));
          if (onAuctionUpdate && auction?.id) {
            onAuctionUpdate(auction.id);
          }
          successMessage = exit ? 'Лот успешно обновлен' : 'Лот сохранен';
        }
      } else if (auction?.id) {
        const resp = await apiService.createLot(lotData);
        savedLot = resp.data;
        setLots((prev) => normalizeLotsWithFallbackNumbers([...(prev || []), savedLot]));
        if (onAuctionUpdate) {
          onAuctionUpdate(auction.id);
        }
        successMessage = 'Лот успешно создан';
      } else {
        savedLot = {
          ...lotData,
          id: Date.now(),
          isTemporary: true,
          public_offer_settings: normalizeLotPublicOfferSettings(lotData.public_offer_settings),
          public_offer_periods: formData?.auction_type === 'public_offer'
            ? normalizeLotPublicOfferPeriods(lotData.public_offer_periods, publicOfferStartBase)
            : [],
        };
        setLots((prev) => normalizeLotsWithFallbackNumbers([...(prev || []), savedLot]));
        successMessage = 'Лот добавлен (будет сохранен вместе с торгом)';
      }

      if (savedLot) {
        setEditingLot(savedLot);
        if (successMessage) {
          showToast(successMessage, 'success');
        }
      }

      if (exit) {
        forceCloseLotModal();
      }

      return savedLot;
    } catch (error) {
      const data = error.response?.data;
      let message = data?.detail || error.message;
      if (data && typeof data === 'object') {
        const firstEntry = Object.entries(data)[0];
        if (firstEntry) {
          const [, value] = firstEntry;
          message = Array.isArray(value) ? value.join(', ') : String(value || message);
        }
      }
      console.error('Error saving lot:', error);
      showToast(`Ошибка сохранения лота: ${message}`, 'error');
      return false;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(price);
  };

  // Функции для управления диалогом подтверждения
  function showConfirmDialog(title, message, onConfirm, confirmText = 'Удалить', cancelText = 'Отмена') {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  }

  function hideConfirmDialog() {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  }

  function handleConfirmAction() {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  }

  // Функции сортировки документов
  const handleDocSort = (field) => {
    if (docSortField === field) {
      setDocSortDirection(docSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDocSortField(field);
      setDocSortDirection('asc');
    }
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    let aValue, bValue;

    switch (docSortField) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'created_date':
        aValue = new Date(a.created_date);
        bValue = new Date(b.created_date);
        break;
      case 'updated_date':
        aValue = new Date(a.updated_date || a.created_date);
        bValue = new Date(b.updated_date || b.created_date);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return docSortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return docSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Функции для управления лотами (обновляют локальное состояние AuctionForm)
  const handleLotDelete = (lotId) => {
    const lot = lots.find(l => l.id === lotId);
    const lotName = lot ? lot.name : `лот ${lotId}`;

    showConfirmDialog(
      'Подтверждение удаления',
      `Вы уверены, что хотите удалить ${lotName}? Это действие нельзя отменить.`,
      async () => {
        try {
          if (lot?.isTemporary) {
            // Удаляем временный лот из локального состояния
            setLots(lots.filter(l => l.id !== lotId));
            showToast('Лот удален');
          } else {
            // Удаляем сохраненный лот с сервера
            await apiService.deleteLot(lotId);
            setLots(lots.filter(l => l.id !== lotId));
            // Обновляем список всех лотов для модального окна "Прикрепить существующий"
            await loadAllLots();
            // Обновляем данные торга в основном списке
            if (onAuctionUpdate && auction?.id) {
              onAuctionUpdate(auction.id);
            }
            showToast('Лот успешно удален');
          }
        } catch (error) {
          console.error('Error deleting lot:', error);
          showToast('Ошибка удаления лота', 'error');
        }
      }
    );
  };

  const handleLotDetach = (lotId) => {
    const lot = lots.find(l => l.id === lotId);
    const lotName = lot ? lot.name : `лот ${lotId}`;

    // Временные лоты нельзя отвязать - они еще не привязаны
    if (lot?.isTemporary) {
      showToast('Временные лоты можно только удалить', 'error');
      return;
    }

    showConfirmDialog(
      'Подтверждение отвязки',
      `Вы уверены, что хотите отвязать "${lotName}" от этих торгов?`,
      async () => {
        try {
          await apiService.detachLotFromAuction(lotId);
          // Обновляем локальное состояние лотов в AuctionForm
          setLots(lots.filter(lot => lot.id !== lotId));
          await loadAllLots(); // Обновляем список всех лотов
          // Обновляем данные торга в основном списке
          if (onAuctionUpdate && auction?.id) {
            onAuctionUpdate(auction.id);
          }
          showToast('Лот успешно отвязан от торгов');
        } catch (error) {
          console.error('Error detaching lot:', error);
          showToast('Ошибка отвязки лота', 'error');
        }
      },
      'Отвязать'
    );
  };

  const showPriceOfferForm = formData.auction_type !== 'public_offer';

  return (
    <div className="form-container">
      <div className="form-header">
        <h3>
          {auction ? (isViewMode ? getAuctionTitleLabel(auction) : 'Редактирование торгов') : 'Создание новых торгов'}
        </h3>
        <div className="form-header-actions">
          {auction && isViewMode && (
            <button type="button" className="btn btn-primary" onClick={onEdit}>
              Редактировать
            </button>
          )}
        </div>
      </div>
      <form className="auction-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minWidth: 0 }}>
            <div className="form-group">
              <label>Должник:</label>
              <div className="select-with-actions">
                <SearchableSelect
                  value={formData.debtor}
                  onChange={(value) => updateFormData('debtor', value)}
                  options={debtors}
                  placeholder="Выберите должника"
                  searchPlaceholder="Поиск должника..."
                  displayKey="display_name"
                  valueKey="id"
                  disabled={isViewMode}
                  required={true}
                  showValidation={showValidation}
                  onAddNew={onCreateDebtor}
                  addNewLabel="Добавить нового должника"
                />
                {formData.debtor && !isViewMode && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm edit-btn"
                    onClick={() => onEditDebtor(formData.debtor)}
                    title="Редактировать должника"
                  >
                    ✏️
                  </button>
                )}
              </div>
            </div>

            {showPriceOfferForm ? (
              <div className="auction-type-settings-grid auction-type-settings-grid--with-price-offer" style={{ marginTop: 8 }}>
                <div className="auction-type-settings-label">Форма торгов:</div>
                <div className="auction-type-settings-label">Тип торгов:</div>
                <div className="auction-type-settings-label">Форма подачи предложений:</div>
                <div className="auction-type-settings-label auction-type-settings-label--empty" aria-hidden="true">.</div>

                <select
                  value={formData.auction_form}
                  onChange={(e) => updateFormData('auction_form', e.target.value)}
                  disabled={isViewMode}
                  className="auction-type-settings-control"
                >
                  <option value="open">Открытая</option>
                  <option value="closed">Закрытая</option>
                </select>
                <select
                  value={formData.auction_type}
                  onChange={(e) => updateFormData('auction_type', e.target.value)}
                  disabled={isViewMode}
                  className="auction-type-settings-control"
                >
                  <option value="auction">Аукцион</option>
                  <option value="contest">Конкурс</option>
                  <option value="public_offer">Публичное предложение</option>
                </select>
                <select
                  value={formData.price_offer_form}
                  onChange={(e) => updateFormData('price_offer_form', e.target.value)}
                  disabled={isViewMode}
                  className="auction-type-settings-control auction-type-settings-control--price-offer"
                >
                  <option value="open">Открытая</option>
                  <option value="closed">Закрытая</option>
                </select>
                <div className="auction-repeat-trade-cell">
                  <label
                    className="auction-repeat-trade-checkbox"
                    style={{ cursor: isViewMode ? 'default' : 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={!!formData.is_repeat_trade}
                      disabled={isViewMode}
                      onChange={(e) => updateFormData('is_repeat_trade', e.target.checked)}
                    />
                    <span>Повторные торги</span>
                  </label>
                </div>
              </div>
            ) : (
              <div className="auction-type-settings-grid auction-type-settings-grid--no-price-offer" style={{ marginTop: 8 }}>
                <div className="auction-type-settings-label">Форма торгов:</div>
                <div className="auction-type-settings-label">Тип торгов:</div>
                <div className="auction-type-settings-label auction-type-settings-label--empty" aria-hidden="true">.</div>

                <select
                  value={formData.auction_form}
                  onChange={(e) => updateFormData('auction_form', e.target.value)}
                  disabled={isViewMode}
                  className="auction-type-settings-control"
                >
                  <option value="open">Открытая</option>
                  <option value="closed">Закрытая</option>
                </select>
                <select
                  value={formData.auction_type}
                  onChange={(e) => updateFormData('auction_type', e.target.value)}
                  disabled={isViewMode}
                  className="auction-type-settings-control"
                >
                  <option value="auction">Аукцион</option>
                  <option value="contest">Конкурс</option>
                  <option value="public_offer">Публичное предложение</option>
                </select>
                <div className="auction-repeat-trade-cell">
                  <label
                    className="auction-repeat-trade-checkbox"
                    style={{ cursor: isViewMode ? 'default' : 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={!!formData.is_repeat_trade}
                      disabled={isViewMode}
                      onChange={(e) => updateFormData('is_repeat_trade', e.target.checked)}
                    />
                    <span>Повторные торги</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="form-group" ref={organizerFieldRef}>
            <label>Организатор торгов:</label>
            {organizerSource === 'debtor_manager' ? (
              <div className="select-with-actions">
                <input
                  type="text"
                  value={debtorManagerName || ''}
                  placeholder={debtorManagerId ? '' : 'Не указан арбитражный управляющий у должника'}
                  readOnly
                  disabled
                  className="organizer-manager-input"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 6,
                    border: showValidation && !debtorManagerId ? '2px solid #e74c3c' : '1px solid #ddd',
                    background: '#f8f9fa',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                  }}
                />
                {(debtorManagerId || formData.organizer) && !isViewMode && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm edit-btn"
                    onClick={() => onEditOrganizer(debtorManagerId || formData.organizer)}
                    title="Редактировать арбитражного управляющего"
                  >
                    ✏️
                  </button>
                )}
              </div>
            ) : (
              <div className="select-with-actions">
                <SearchableSelect
                  value={formData.organizer}
                  onChange={(value) => updateFormData('organizer', value)}
                  options={directoryOrganizerOptions}
                  placeholder="Выберите организатора"
                  searchPlaceholder="Поиск организатора..."
                  displayKey="display_name"
                  valueKey="id"
                  formatOption={formatOrganizerOption}
                  disabled={isViewMode}
                  required={true}
                  showValidation={showValidation}
                  onAddNew={() => setShowAddOrganizerTypeModal(true)}
                  addNewLabel="Добавить организатора (ОТ/АУ)"
                />
                {formData.organizer && !isViewMode && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm edit-btn"
                    onClick={() => onEditOrganizer(formData.organizer)}
                    title="Редактировать организатора"
                  >
                    ✏️
                  </button>
                )}
              </div>
            )}

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 10,
                cursor: (!isViewMode && formData.debtor) ? 'pointer' : 'default',
                opacity: (!isViewMode && !formData.debtor) ? 0.6 : 1,
                userSelect: 'none',
              }}
              title={!isViewMode && !formData.debtor ? 'Сначала выберите должника' : ''}
            >
              <input
                type="checkbox"
                checked={organizerSource === 'debtor_manager'}
                disabled={isViewMode || !formData.debtor}
                style={{
                  width: 16,
                  minWidth: 16,
                  height: 16,
                  flex: '0 0 16px',
                  margin: 0,
                }}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    if (!formData.debtor) {
                      showToast('Сначала выберите должника', 'error');
                      return;
                    }
                    if (!debtorManagerId) {
                      showToast('У должника не указан арбитражный управляющий. Укажите его в карточке должника.', 'error');
                      return;
                    }
                    // Запоминаем текущий выбор из справочника, чтобы потом восстановить
                    if (organizerSource === 'directory' && formData.organizer) {
                      lastDirectoryOrganizerIdRef.current = formData.organizer;
                    }
                    setOrganizerSource('debtor_manager');
                    setFormData((prev) => ({ ...(prev || {}), organizer: debtorManagerId }));
                  } else {
                    setOrganizerSource('directory');
                    // восстанавливаем предыдущее значение из справочника (если было)
                    setFormData((prev) => ({ ...(prev || {}), organizer: lastDirectoryOrganizerIdRef.current || '' }));
                  }
                }}
              />
              <span style={{ whiteSpace: 'nowrap' }}>
                Арбитражный управляющий является организатором торгов
              </span>
            </label>
          </div>
        </div>

        {showAddOrganizerTypeModal && (
          <div className="modal-overlay" style={{ zIndex: 2500 }}>
            <div className="modal confirm-dialog" style={{ maxWidth: 520, width: 'min(520px, 92vw)' }}>
              <div className="modal-header">
                <h3>Добавить организатора</h3>
              </div>
              <div className="modal-body">
                <p className="confirm-message">Выберите тип добавляемой записи.</p>
                <div className="confirm-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setShowAddOrganizerTypeModal(false);
                      onCreateOrganizer?.('specialized_org');
                    }}
                  >
                    ОТ (организатор торгов)
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setShowAddOrganizerTypeModal(false);
                      onCreateOrganizer?.('arbitrage_manager');
                    }}
                  >
                    АУ (арбитражный управляющий)
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: '100%', gridColumn: '1 / -1' }}
                    onClick={() => setShowAddOrganizerTypeModal(false)}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="dates-calculation-block">
          <div className="dates-block-header">
            <h4>📅 Временные рамки торгов</h4>
            <p>Настройка дат проведения торгов с автоматическим расчетом периодов</p>
          </div>

          <div className="dates-content">
            {formData.auction_type === 'public_offer' ? (
              <div className="public-offer-by-lots">
                <div className="public-offer-periods-title">Периоды публичного предложения по лотам</div>
                <div className="public-offer-global-range-row">
                  <div className="form-group">
                    <label>Дата начала первого интервала:</label>
                    <CustomDateTimePicker
                      value={formData.auction_start_datetime || ''}
                      onChange={(e) => updateFormData('auction_start_datetime', e.target.value)}
                      onInputFocus={(e) => handleGlobalPublicOfferDateFieldFocus('auction_start_datetime', e.target.value)}
                      onInputBlur={(e) => handleGlobalPublicOfferDateFieldBlur('auction_start_datetime', e.target.value)}
                      showLabel={false}
                      inlineCalendarSize="md"
                      inputClassName={invalidGlobalPublicOfferFields.auction_start_datetime ? 'public-offer-input-invalid' : ''}
                      disabled={isViewMode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Дата начала приема заявок:</label>
                    <CustomDateTimePicker
                      value={formData.applications_start || ''}
                      showLabel={false}
                      inlineCalendarSize="md"
                      inputClassName={invalidGlobalPublicOfferFields.applications_start ? 'public-offer-input-invalid' : ''}
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>Дата окончания приема заявок:</label>
                    <CustomDateTimePicker
                      value={formData.applications_end || ''}
                      showLabel={false}
                      inlineCalendarSize="md"
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label>Окончание торгов:</label>
                    <CustomDateTimePicker
                      value={formData.results_datetime || ''}
                      onChange={(e) => updateFormData('results_datetime', e.target.value)}
                      showLabel={false}
                      inlineCalendarSize="md"
                      disabled={isViewMode}
                    />
                  </div>
                </div>
                {(lots || []).length === 0 && (
                  <div className="public-offer-empty-lots">
                    Сначала добавьте хотя бы один лот, затем заполните его таблицу периодов.
                  </div>
                )}
                {(lots || []).map((lot) => {
                  const settings = normalizeLotPublicOfferSettings(lot?.public_offer_settings);
                  const rows = normalizeLotPublicOfferPeriods(lot?.public_offer_periods, publicOfferStartBase);
                  const reductionTitle = getReductionColumnTitle(settings.reduction_type);
                  const reductionValueSuffix = getReductionValueSuffix(settings.reduction_type);
                  const depositValueSuffix = getDepositValueSuffix(settings.deposit_method);
                  const lotAutoKey = getPublicOfferLotKey(lot);
                  const autoConfigOpen = !!publicOfferAutoConfigOpen[lotAutoKey];
                  const lotStartPriceValue = Number.parseFloat(String(lot?.start_price ?? '').replace(',', '.'));
                  const lotStartPriceLabel = Number.isFinite(lotStartPriceValue) ? formatPrice(lotStartPriceValue) : '—';
                  return (
                    <div className="public-offer-lot-card" key={`public-offer-lot-${lot.id || lot.lot_number}`}>
                      <div className="public-offer-lot-card-header">
                        <div>
                          <strong>Лот №{lot.lot_number || '—'}: {lot.name || 'Без названия'}</strong>
                          <span className="public-offer-lot-start-price"> (начальная стоимость: {lotStartPriceLabel})</span>
                        </div>
                        {!isViewMode && (
                          <div className="public-offer-header-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setPublicOfferAutoConfigOpen((prev) => ({ ...prev, [lotAutoKey]: !autoConfigOpen }))}
                            >
                              {autoConfigOpen ? 'Скрыть автосоздание' : 'Автосоздание этапов'}
                            </button>
                          </div>
                        )}
                      </div>

                      {autoConfigOpen && (
                        <div className="public-offer-auto-panel">
                          <div className="public-offer-lot-settings-grid">
                            <div className="public-offer-row public-offer-row--top">
                              <label className="public-offer-field public-offer-field--interval">
                                Снижать цену каждые:
                                <div className="public-offer-inline-fields">
                                  <input
                                    type="text"
                                    value={settings.interval_value}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'interval_value', String(e.target.value || '').replace(/\D/g, ''))}
                                    disabled={isViewMode}
                                  />
                                  <select
                                    value={settings.interval_unit}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'interval_unit', e.target.value)}
                                    disabled={isViewMode}
                                  >
                                    <option value="days_working">дней раб.</option>
                                    <option value="days_calendar">дней календ.</option>
                                    <option value="hours">часов</option>
                                    <option value="minutes">минут</option>
                                  </select>
                                </div>
                              </label>
                              <label className="public-offer-field public-offer-field--reduction-type">
                                Тип снижения цены:
                                <select
                                  value={settings.reduction_type}
                                  onChange={(e) => {
                                    const nextType = e.target.value;
                                    updateLotPublicOfferSettings(lot.id, 'reduction_type', nextType);
                                    updateLotPublicOfferSettings(
                                      lot.id,
                                      'reduction_value',
                                      sanitizeReductionInput(settings.reduction_value, nextType, { allowEmpty: true })
                                    );
                                  }}
                                  disabled={isViewMode}
                                >
                                  {PUBLIC_OFFER_REDUCTION_TYPES.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="public-offer-field public-offer-field--reduction-value">
                                Величина снижения:
                                <div className="public-offer-value-with-unit">
                                  <input
                                    type="text"
                                    value={settings.reduction_value}
                                    onChange={(e) => updateLotPublicOfferSettings(
                                      lot.id,
                                      'reduction_value',
                                      sanitizeReductionInput(e.target.value, settings.reduction_type, { allowEmpty: true })
                                    )}
                                    onBlur={(e) => updateLotPublicOfferSettings(
                                      lot.id,
                                      'reduction_value',
                                      (() => {
                                        const normalized = sanitizeReductionInput(e.target.value, settings.reduction_type, { allowEmpty: true });
                                        return normalized ? formatFixedTwoDecimals(normalized) : '';
                                      })()
                                    )}
                                    disabled={isViewMode}
                                  />
                                  <span className="public-offer-value-unit">{reductionValueSuffix}</span>
                                </div>
                              </label>
                            </div>
                            <div className="public-offer-row public-offer-row--generation">
                              <label className="public-offer-field public-offer-field--generation-mode">
                                Создать интервалы:
                                <select
                                  value={settings.generation_mode}
                                  onChange={(e) => updateLotPublicOfferSettings(lot.id, 'generation_mode', e.target.value)}
                                  disabled={isViewMode}
                                >
                                  {PUBLIC_OFFER_GENERATION_MODES.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                  ))}
                                </select>
                              </label>
                              {settings.generation_mode === 'to_cutoff_price' && (
                                <label className="public-offer-field public-offer-field--generation-param">
                                  Цена отсечения (руб.):
                                  <input
                                    type="text"
                                    value={settings.cutoff_price}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'cutoff_price', sanitizeDecimalInput(e.target.value))}
                                    disabled={isViewMode}
                                  />
                                </label>
                              )}
                              {settings.generation_mode === 'by_count' && (
                                <label className="public-offer-field public-offer-field--generation-param">
                                  Количество интервалов:
                                  <input
                                    type="text"
                                    value={settings.intervals_count}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'intervals_count', String(e.target.value || '').replace(/\D/g, ''))}
                                    disabled={isViewMode}
                                  />
                                </label>
                              )}
                              <label className="public-offer-field public-offer-field--deposit-method">
                                  Способ расчета обеспечения:
                                  <select
                                    value={settings.deposit_method}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'deposit_method', e.target.value)}
                                    disabled={isViewMode}
                                  >
                                    {PUBLIC_OFFER_DEPOSIT_METHODS.map((item) => (
                                      <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                  </select>
                                </label>
                                <label className="public-offer-field public-offer-field--deposit-value">
                                  Размер обеспечения:
                                  <div className="public-offer-value-with-unit">
                                    <input
                                      type="text"
                                      value={settings.deposit_value}
                                      onChange={(e) => updateLotPublicOfferSettings(lot.id, 'deposit_value', sanitizeDecimalInput(e.target.value))}
                                      onBlur={(e) => {
                                        const normalized = sanitizeDecimalInput(e.target.value, { allowEmpty: true });
                                        if (!normalized) {
                                          updateLotPublicOfferSettings(lot.id, 'deposit_value', '');
                                          return;
                                        }
                                        const isPercentDeposit = settings.deposit_method !== 'fixed_rub';
                                        updateLotPublicOfferSettings(
                                          lot.id,
                                          'deposit_value',
                                          isPercentDeposit ? formatFixedTwoDecimals(normalized) : normalized
                                        );
                                      }}
                                      disabled={isViewMode}
                                    />
                                    <span className="public-offer-value-unit">{depositValueSuffix}</span>
                                  </div>
                                </label>
                            </div>
                            <div className="public-offer-row public-offer-row--applications-sync">
                              <div className="public-offer-sync-row">
                                <label className="public-offer-checkbox-label public-offer-sync-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={!!settings.applications_start_matches_interval}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'applications_start_matches_interval', !!e.target.checked)}
                                    disabled={isViewMode}
                                  />
                                  Дата начала приема заявок совпадает с началом интервала
                                </label>
                                {!settings.applications_start_matches_interval && (
                                  <div className="public-offer-sync-diff-row">
                                    <span className="public-offer-sync-diff-prefix">Отличается на</span>
                                    <input
                                      type="text"
                                      value={settings.applications_start_diff_value}
                                      onChange={(e) => updateLotPublicOfferSettings(
                                        lot.id,
                                        'applications_start_diff_value',
                                        String(e.target.value || '').replace(/\D/g, '')
                                      )}
                                      disabled={isViewMode}
                                    />
                                    <select
                                      value={settings.applications_start_diff_unit}
                                      onChange={(e) => updateLotPublicOfferSettings(lot.id, 'applications_start_diff_unit', e.target.value)}
                                      disabled={isViewMode}
                                    >
                                      <option value="days">дней</option>
                                      <option value="hours">часов</option>
                                      <option value="minutes">минут</option>
                                    </select>
                                  </div>
                                )}
                              </div>
                              <div className="public-offer-sync-row">
                                <label className="public-offer-checkbox-label public-offer-sync-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={!!settings.applications_end_matches_interval}
                                    onChange={(e) => updateLotPublicOfferSettings(lot.id, 'applications_end_matches_interval', !!e.target.checked)}
                                    disabled={isViewMode}
                                  />
                                  Дата окончания приема заявок совпадает с окончанием интервала
                                </label>
                                {!settings.applications_end_matches_interval && (
                                  <div className="public-offer-sync-diff-row">
                                    <span className="public-offer-sync-diff-prefix">Отличается на</span>
                                    <input
                                      type="text"
                                      value={settings.applications_end_diff_value}
                                      onChange={(e) => updateLotPublicOfferSettings(
                                        lot.id,
                                        'applications_end_diff_value',
                                        String(e.target.value || '').replace(/\D/g, '')
                                      )}
                                      disabled={isViewMode}
                                    />
                                    <select
                                      value={settings.applications_end_diff_unit}
                                      onChange={(e) => updateLotPublicOfferSettings(lot.id, 'applications_end_diff_unit', e.target.value)}
                                      disabled={isViewMode}
                                    >
                                      <option value="days">дней</option>
                                      <option value="hours">часов</option>
                                      <option value="minutes">минут</option>
                                    </select>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {!isViewMode && (
                            <div className="public-offer-auto-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => handleGenerateLotPublicOfferPeriods(lot.id, e.currentTarget)}
                              >
                                Сгенерировать этапы
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="public-offer-periods-table-wrap">
                        <table
                          className={`public-offer-periods-table public-offer-periods-table--compact ${isAdmin ? 'public-offer-periods-table--resizable' : ''}`}
                          style={{
                            width: `${Math.max(960, Math.round(publicOfferTableWidthPx || 0))}px`,
                            minWidth: `${Math.max(960, Math.round(publicOfferTableWidthPx || 0))}px`,
                          }}
                        >
                          <colgroup>
                            <col style={{ width: `${effectivePublicOfferColWidths.interval_start_datetime}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.applications_start_datetime}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.applications_end_datetime}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.interval_end_datetime}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.reduction_value}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.deposit_amount}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.interval_price}px` }} />
                            <col style={{ width: `${effectivePublicOfferColWidths.comment}px` }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>
                                <span className="public-offer-th-label">Дата начала интервала</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'interval_start_datetime')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">Дата начала приема заявок на интервале</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'applications_start_datetime')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">Дата окончания приема заявок на интервале</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'applications_end_datetime')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">Дата окончания интервала</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'interval_end_datetime')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">{reductionTitle}</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'reduction_value')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">Задаток на интервале, руб.</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'deposit_amount')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">Цена на интервале, руб.</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'interval_price')} />}
                              </th>
                              <th>
                                <span className="public-offer-th-label">Комментарий</span>
                                {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizePublicOfferCol?.(e, 'comment')} />}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, idx) => (
                              <tr key={`lot-${lot.id}-row-${idx}`}>
                                <td><CustomDateTimePicker showLabel={false} inputClassName={invalidPublicOfferFields[getPublicOfferFieldKey(lot.id, idx, 'interval_start_datetime')] ? 'public-offer-input-invalid' : ''} value={row.interval_start_datetime || ''} onInputFocus={(e) => handleLotPublicOfferDateFieldFocus(lot.id, idx, 'interval_start_datetime', e.target.value)} onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'interval_start_datetime', e.target.value)} onInputBlur={(e) => handleLotPublicOfferDateFieldBlur(lot.id, idx, 'interval_start_datetime', e.target.value, e.currentTarget)} disabled={isViewMode} /></td>
                                <td><CustomDateTimePicker showLabel={false} inputClassName={invalidPublicOfferFields[getPublicOfferFieldKey(lot.id, idx, 'applications_start_datetime')] ? 'public-offer-input-invalid' : ''} value={row.applications_start_datetime || ''} onInputFocus={(e) => handleLotPublicOfferDateFieldFocus(lot.id, idx, 'applications_start_datetime', e.target.value)} onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'applications_start_datetime', e.target.value)} onInputBlur={(e) => handleLotPublicOfferDateFieldBlur(lot.id, idx, 'applications_start_datetime', e.target.value, e.currentTarget)} disabled={isViewMode || settings.applications_start_matches_interval} /></td>
                                <td><CustomDateTimePicker showLabel={false} inputClassName={invalidPublicOfferFields[getPublicOfferFieldKey(lot.id, idx, 'applications_end_datetime')] ? 'public-offer-input-invalid' : ''} value={row.applications_end_datetime || ''} onInputFocus={(e) => handleLotPublicOfferDateFieldFocus(lot.id, idx, 'applications_end_datetime', e.target.value)} onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'applications_end_datetime', e.target.value)} onInputBlur={(e) => handleLotPublicOfferDateFieldBlur(lot.id, idx, 'applications_end_datetime', e.target.value, e.currentTarget)} disabled={isViewMode || settings.applications_end_matches_interval} /></td>
                                <td><CustomDateTimePicker showLabel={false} inputClassName={invalidPublicOfferFields[getPublicOfferFieldKey(lot.id, idx, 'interval_end_datetime')] ? 'public-offer-input-invalid' : ''} value={row.interval_end_datetime || ''} onInputFocus={(e) => handleLotPublicOfferDateFieldFocus(lot.id, idx, 'interval_end_datetime', e.target.value)} onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'interval_end_datetime', e.target.value)} onInputBlur={(e) => handleLotPublicOfferDateFieldBlur(lot.id, idx, 'interval_end_datetime', e.target.value, e.currentTarget)} disabled={isViewMode} /></td>
                                <td>
                                  <div className="public-offer-amount-with-suffix">
                                    <input
                                      className="public-offer-amount-input"
                                      type="text"
                                      value={row.reduction_value ?? ''}
                                      onFocus={(e) => { publicOfferReductionFocusValuesRef.current[`${lot.id}:${idx}:reduction_value`] = e.target.value || ''; }}
                                      onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'reduction_value', e.target.value)}
                                      onBlur={(e) => handleReductionValueBlur(lot.id, idx, e.target.value, e.currentTarget)}
                                      disabled={isViewMode || idx === 0}
                                    />
                                    <span className="public-offer-amount-suffix">{getReductionValueSuffix(settings?.reduction_type)}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="public-offer-amount-with-suffix">
                                    <input
                                      className="public-offer-amount-input"
                                      type="text"
                                      value={formatGroupedAmountInput(row.deposit_amount)}
                                      onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'deposit_amount', e.target.value)}
                                      disabled={isViewMode}
                                    />
                                    <span className="public-offer-amount-suffix">₽</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="public-offer-amount-with-suffix">
                                    <input
                                      className="public-offer-amount-input"
                                      type="text"
                                      value={formatGroupedAmountInput(row.interval_price)}
                                      onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'interval_price', e.target.value)}
                                      disabled={isViewMode}
                                    />
                                    <span className="public-offer-amount-suffix">₽</span>
                                  </div>
                                </td>
                                <td><input type="text" value={row.comment || ''} onChange={(e) => updateLotPublicOfferPeriodField(lot.id, idx, 'comment', e.target.value)} disabled={isViewMode} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {!isViewMode && (
                        <div className="public-offer-periods-actions">
                          <button type="button" className="period-add-btn" onClick={() => addLotPublicOfferPeriod(lot.id)}>
                            +
                          </button>
                          <button
                            type="button"
                            className="period-remove-btn"
                            onClick={() => removeLotPublicOfferPeriod(lot.id, Math.max(0, rows.length - 1))}
                            disabled={rows.length <= 1}
                          >
                            -
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <CustomDateRangePicker
                startDate={formData.applications_start}
                endDate={formData.applications_end}
                onStartDateChange={(e) => updateFormData('applications_start', e.target.value)}
                onEndDateChange={(e) => updateFormData('applications_end', e.target.value)}
                startLabel="Начало приема заявок"
                endLabel="Окончание приема заявок"
                required={false}
                showInputs={true}
                layout="horizontal"
                compactCalculator={true}
                showFlowArrows={true}
                defaultDayType="working"
                disabled={isViewMode}
              />
            )}
            <div className="auction-reminder-row">
              <button
                type="button"
                className="auction-reminder-btn"
                disabled={!formData.applications_start}
                onClick={() => openReminderForEvent('applications_start')}
              >
                Напомнить о начале приема заявок
              </button>
              {formData.auction_type === 'public_offer' ? (
                <button
                  type="button"
                  className="auction-reminder-btn"
                  disabled={!formData.results_datetime}
                  onClick={() => openReminderForEvent('results_datetime')}
                >
                  Напомнить об окончании торгов
                </button>
              ) : (
                <button
                  type="button"
                  className="auction-reminder-btn"
                  disabled={!formData.applications_end}
                  onClick={() => openReminderForEvent('applications_end')}
                >
                  Напомнить об окончании приема заявок
                </button>
              )}
            </div>

            {/* Дата торгов для открытой формы */}
            {formData.price_offer_form === 'open' && formData.auction_type !== 'public_offer' && (
              <div className="auction-date-row">
                <CustomDateTimePicker
                  value={formData.auction_date}
                  onChange={(e) => updateFormData('auction_date', e.target.value)}
                  label="Дата и время проведения торгов"
                  required={false}
                  min={formData.applications_end}
                  baseDate={formData.applications_end}
                  baseLabel="от окончания приема заявок"
                  calculatorPosition="above"
                  showFlowArrows={true}
                  compactCalculator={true}
                  defaultDayType="working"
                  disabled={isViewMode}
                />
                <div className="auction-reminder-row">
                  <button
                    type="button"
                    className="auction-reminder-btn"
                    disabled={!formData.auction_date}
                    onClick={() => openReminderForEvent('auction_date')}
                  >
                    Напомнить о дате проведения торгов
                  </button>
                </div>
              </div>
            )}

            {/* Подведение итогов для закрытой формы или окончание торгов для публичного предложения */}
            {(formData.auction_type !== 'public_offer' && formData.price_offer_form === 'closed') && (
              <div className="results-date-row">
                <CustomDateTimePicker
                  value={formData.results_datetime}
                  onChange={(e) => updateFormData('results_datetime', e.target.value)}
                  label={formData.auction_type === 'public_offer' ? 'Окончание торгов' : 'Подведение итогов'}
                  required={false}
                  min={formData.applications_end || formData.applications_start}
                  baseDate={formData.applications_end || formData.applications_start}
                  baseLabel={formData.applications_end ? "от окончания приема заявок" : "от начала приема заявок"}
                  calculatorPosition="above"
                  showFlowArrows={true}
                  compactCalculator={true}
                  defaultDayType="working"
                  disabled={isViewMode}
                />
                <div className="auction-reminder-row">
                  <button
                    type="button"
                    className="auction-reminder-btn"
                    disabled={!formData.results_datetime}
                    onClick={() => openReminderForEvent('results_datetime')}
                  >
                    Напомнить {formData.auction_type === 'public_offer' ? 'об окончании торгов' : 'о подведении итогов'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Статус:</label>
            <select
              value={formData.status}
              onChange={(e) => updateFormData('status', e.target.value)}
              disabled={isViewMode}
            >
              <option value="draft">Черновик</option>
              <option value="pending">Ожидает</option>
              <option value="applications">Прием заявок</option>
              <option value="applications_closed">Прием заявок закрыт</option>
              <option value="in_progress">В процессе</option>
              <option value="completed">Завершены</option>
              <option value="cancelled">Отменены</option>
              <option value="failed">Не состоялись</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{color: '#6c757d', fontSize: '0.85rem'}}>💡 Информация:</label>
            <div style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#6c757d',
              lineHeight: '1.4'
            }}>
              Статус определяет текущую стадию торгов и влияет на доступные операции с лотами
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Номер сообщения в ЕФРСБ:</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={15}
              className={invalidEfrsbField ? 'efrsb-input-invalid' : ''}
              value={formData.efrsb_message_id || ''}
              onChange={(e) => updateFormData('efrsb_message_id', e.target.value)}
              placeholder="6-15 цифр"
              disabled={isViewMode}
              ref={!isViewMode ? efrsbMessageInputRef : undefined}
            />
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6c757d' }}>
              * заполняйте только для экспорта торгов в ЭТП. Если экспорт не нужен, поле можно оставить пустым. Заполняется автоматически при добавлении сообщения на Федресурс.
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>ЭТП (электронная торговая площадка):</label>
            <SearchableSelect
              value={formData.etp}
              onChange={(value) => updateFormData('etp', value)}
              options={etps || []}
              placeholder="Выберите ЭТП"
              searchPlaceholder="Поиск ЭТП..."
              displayKey="name"
              valueKey="id"
              disabled={isViewMode}
              formatOption={formatEtpDisplay}
              formatSelected={formatEtpDisplay}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Счет для задатка:</label>
            <div className="select-with-actions">
              <SearchableSelect
                value={formData.deposit_account}
                onChange={(value) => updateFormData('deposit_account', value)}
                options={bankAccountsDepositOptions}
                placeholder="Выберите счет для задатка"
                searchPlaceholder="Поиск счета..."
                displayKey="name"
                valueKey="id"
                disabled={isViewMode}
                formatOption={formatBankAccountDisplay}
                formatSelected={formatBankAccountDisplay}
                fallbackText={formatBankAccountDisplay(selectedDepositAccount)}
                invalid={!isViewMode && hasDepositAccountOwnershipMismatch}
                onAddNew={() => onCreateAccount({
                  purpose: 'deposit',
                  debtorId: formData.debtor,
                  organizerId: formData.organizer,
                  etpId: formData.etp,
                })}
                addNewLabel="Добавить новый счет"
              />
              {!isViewMode && canEditDepositAccount && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm edit-btn"
                  disabled={!canEditDepositAccount}
                  onClick={() => {
                    if (!canEditDepositAccount) return;
                    onEditAccount(formData.deposit_account, 'deposit', {
                      debtorId: formData.debtor,
                      organizerId: formData.organizer,
                      etpId: formData.etp,
                    });
                  }}
                  title="Редактировать счет для задатка"
                >
                  ✏️
                </button>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6c757d' }}>
              * доступны счета с назначением «Задаток» и владельцем: Должник, Организатор или Площадка (для площадки — по выбранной ЭТП).
            </div>
            {!isViewMode && isDepositPlatformEtpMismatch && (
              <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#dc3545' }}>
                Выбран счет площадки с другой ЭТП. Выберите другой счет или измените ЭТП в карточке счета.
              </div>
            )}
            {!isViewMode && isDepositDebtorMismatch && (
              <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#dc3545' }}>
                Выбран счет должника из другой карточки. Выберите другой счет или измените должника в карточке счета.
              </div>
            )}
            {!isViewMode && isDepositOrganizerMismatch && (
              <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#dc3545' }}>
                Выбран счет организатора из другой карточки. Выберите другой счет или измените организатора в карточке счета.
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Счет для оплаты по договору:</label>
            <div className="select-with-actions">
              <SearchableSelect
                value={formData.payment_account}
                onChange={(value) => updateFormData('payment_account', value)}
                options={bankAccountsPaymentOptions}
                placeholder="Выберите счет для оплаты"
                searchPlaceholder="Поиск счета..."
                displayKey="name"
                valueKey="id"
                disabled={isViewMode}
                formatOption={(account) => `${account.bank_name} - ${account.account_number}`}
                onAddNew={() => onCreateAccount({
                  purpose: 'payment',
                  debtorId: formData.debtor,
                  organizerId: formData.organizer,
                  etpId: formData.etp,
                })}
                addNewLabel="Добавить новый счет"
              />
              {!isViewMode && canEditPaymentAccount && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm edit-btn"
                  disabled={!canEditPaymentAccount}
                  onClick={() => {
                    if (!canEditPaymentAccount) return;
                    onEditAccount(formData.payment_account, 'payment', {
                      debtorId: formData.debtor,
                    });
                  }}
                  title="Редактировать счет для оплаты"
                >
                  ✏️
                </button>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6c757d' }}>
              * доступны счета с назначением «Оплата по договору» и владельцем: Должник.
            </div>
          </div>
        </div>

        {/* Шаг торгов для аукциона с открытой формой */}
        {formData.auction_type === 'auction' && formData.price_offer_form === 'open' && (
          <div className="form-group">
            <label>Шаг аукциона (%):</label>
            <input
              type="text"
              inputMode="decimal"
              value={formData.auction_step}
              onChange={(e) => updateFormData('auction_step', e.target.value)}
              disabled={isViewMode}
              placeholder="От 5 до 10"
            />
            {!isViewMode && auctionStepWarning && (
              <div className="error-text" style={{ marginTop: 6 }}>
                {auctionStepWarning}
              </div>
            )}
          </div>
        )}

        {/* Шаг конкурса для открытой формы */}
        {formData.auction_type === 'contest' && formData.price_offer_form === 'open' && (
          <div className="form-group">
            <label>Шаг конкурса (%):</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={formData.contest_step}
              onChange={(e) => updateFormData('contest_step', e.target.value)}
              disabled={isViewMode}
            />
          </div>
        )}

        {/* Порядок снижения цены для публичного предложения */}
        {formData.auction_type === 'public_offer' && (
          <div className="form-group">
            <label>Порядок снижения цены:</label>
            <textarea
              value={formData.price_decrease_rules}
              onChange={(e) => updateFormData('price_decrease_rules', e.target.value)}
              rows={3}
              disabled={isViewMode}
              placeholder="Опишите периоды и сроки снижения цены..."
            />
          </div>
        )}

        {/* Условия конкурса для всех видов конкурса */}
        {formData.auction_type === 'contest' && (
          <div className="form-group">
            <label>Условия проведения конкурса:</label>
            <textarea
              value={formData.contest_conditions}
              onChange={(e) => updateFormData('contest_conditions', e.target.value)}
              rows={3}
              disabled={isViewMode}
            />
          </div>
        )}

        {/* Поля для задатка */}
        <div className="deposit-section">
          <h4>💰 Задаток</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Размер задатка (%):</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.deposit_amount}
                onChange={(e) => updateFormData('deposit_amount', e.target.value)}
                disabled={isViewMode}
                placeholder="Например: 5"
              />
              {formData.auction_type === 'public_offer' && (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#6c757d' }}>
                  * используется для сообщения о торгах.
                </div>
              )}
              {!isViewMode && depositAmountWarning && (
                <div className="error-text" style={{ marginTop: 6 }}>
                  {depositAmountWarning}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Срок внесения задатка:</label>
              <textarea
                value={formData.deposit_deadline}
                onChange={(e) => updateFormData('deposit_deadline', e.target.value)}
                rows={2}
                disabled={isViewMode}
                placeholder="Например: до 17:00 дня, предшествующего дню торгов"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Телефон для ознакомления:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={inspectionPhoneCustom}
                  onChange={(e) => {
                    if (isViewMode) return;
                    const checked = e.target.checked;
                    setInspectionPhoneCustom(checked);
                    if (!checked && organizerPhone) {
                      setFormData((prev) => {
                        const next = { ...(prev || {}), inspection_phone: organizerPhone };
                        formDataRef.current = next;
                        return next;
                      });
                    }
                  }}
                  disabled={isViewMode}
                  style={{ width: 18, height: 18, cursor: isViewMode ? 'default' : 'pointer' }}
                />
                <span style={{ color: isViewMode ? '#6c757d' : '#2c3e50' }}>
                  Телефон ознакомления не совпадает с телефоном ОТ
                </span>
              </div>
              <input
                type="text"
                value={formData.inspection_phone}
                onChange={handleInspectionPhoneChange}
                disabled={isViewMode || !inspectionPhoneCustom}
                placeholder="Укажите телефон для ознакомления"
                ref={!isViewMode ? inspectionPhoneRef : undefined}
                inputMode="tel"
                autoComplete="tel"
              />
            </div>
            <div className="form-group">
              <label>Email для ознакомления:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={inspectionEmailCustom}
                  onChange={(e) => {
                    if (isViewMode) return;
                    const checked = e.target.checked;
                    setInspectionEmailCustom(checked);
                    if (!checked && organizerEmail) {
                      setFormData((prev) => {
                        const next = { ...(prev || {}), inspection_email: organizerEmail };
                        formDataRef.current = next;
                        return next;
                      });
                    }
                  }}
                  disabled={isViewMode}
                  style={{ width: 18, height: 18, cursor: isViewMode ? 'default' : 'pointer' }}
                />
                <span style={{ color: isViewMode ? '#6c757d' : '#2c3e50' }}>
                  Email ознакомления не совпадает с email ОТ
                </span>
              </div>
              <input
                type="email"
                value={formData.inspection_email || ''}
                onChange={handleInspectionEmailChange}
                disabled={isViewMode || !inspectionEmailCustom}
                placeholder="Укажите email для ознакомления"
                autoComplete="email"
              />
            </div>
          </div>
        </div>

      </form>

      {/* Раздел лотов */}
      <div className="lots-section">
          <div className="section-header">
            <h4>{isViewMode ? 'Лоты торгов' : 'Лоты для торгов'}</h4>
            {!isViewMode && (
              <div className="lot-buttons">
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    const nextNumber = String(getNextAvailableLotNumber());
                    setEditingLot(null);
                    setLotModalMode('create');
                    setLotFormInitialNumber(nextNumber);
                    setLotFormData((prev) => ({
                      ...(prev || {}),
                      lot_number: nextNumber,
                    }));
                    setLotHasUnsavedChanges(false);
                    setShowLotForm(true);
                  }}
                >
                  Создать лот
                </button>
                {!!isAdmin && (
                  <button
                    className="btn btn-info btn-sm"
                    onClick={async () => {
                      await loadAllLots(); // Обновляем список всех лотов
                      setShowAttachLotModal(true);
                    }}
                  >
                    Прикрепить существующий
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Таблица лотов */}
          {lots.length > 0 ? (
            <div className="lots-table-container">
              <table className={`lots-table ${isViewMode ? '' : 'has-actions'} ${isAdmin ? 'lots-table--resizable' : ''}`}>
                <colgroup>
                  <col style={{ width: lotColWidths?.index }} />
                  <col style={{ width: lotColWidths?.name }} />
                  <col style={{ width: lotColWidths?.description }} />
                  <col style={{ width: lotColWidths?.start_price }} />
                  <col style={{ width: lotColWidths?.category }} />
                  {!isViewMode && <col style={{ width: lotColWidths?.actions }} />}
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <span className="lots-th-label">№</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'index')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Наименование</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'name')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Описание</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'description')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Начальная цена</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'start_price')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Категория</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'category')} />}
                    </th>
                    {!isViewMode && (
                      <th>
                        <span className="lots-th-label">Действия</span>
                        {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'actions')} />}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {normalizeLotsWithFallbackNumbers(lots).map((lot, index) => (
                    <tr
                      key={lot.id}
                      onClick={isViewMode ? () => handleViewLot(lot, index + 1) : undefined}
                      style={isViewMode ? { cursor: 'pointer' } : undefined}
                      title={isViewMode ? 'Нажмите для просмотра лота' : undefined}
                    >
                      <td className="lot-number-cell">
                        <span className="lot-name lot-number-value">{lot.lot_number || (index + 1)}</span>
                      </td>
                      <td className="lot-name">
                        {lot.name}
                        {lot.isTemporary && (
                          <span
                            className="temp-indicator"
                            title="Временный лот - будет сохранен вместе с торгом"
                          >
                            📝
                          </span>
                        )}
                      </td>
                      <td className="lot-description" title={lot.description || ''}>
                        {lot.description && lot.description.length > 50
                          ? `${lot.description.substring(0, 50)}...`
                          : (lot.description || 'Без описания')}
                      </td>
                      <td className="lot-price">{formatPrice(lot.start_price)}</td>
                      <td className="lot-category">
                        <span className={`category-badge ${lot.category}`}>
                          {lot.category === 'real_estate' ? 'Недвижимость' :
                           lot.category === 'transport' ? 'Транспорт' :
                           lot.category === 'equipment' ? 'Оборудование' :
                           lot.category === 'debt' ? 'Дебиторка' :
                           'Прочее'}
                        </span>
                      </td>
                      {!isViewMode && (
                        <td className="lot-actions">
                          <div className="card-actions lot-actions-inner">
                            <button
                              className="card-action-btn card-action-btn--edit"
                              onClick={() => handleEditLot(lot, index + 1)}
                              title="Редактировать"
                              type="button"
                            >
                              ✎
                            </button>
                            {isAdmin && (
                              <button
                                className="card-action-btn card-action-btn--unlink"
                                onClick={() => handleLotDetach(lot.id)}
                                title={lot.isTemporary ? "Временные лоты нельзя отвязать" : "Отвязать от торгов"}
                                disabled={lot.isTemporary}
                                type="button"
                              >
                                🔗
                              </button>
                            )}
                            <button
                              className="card-action-btn card-action-btn--delete"
                              onClick={() => handleLotDelete(lot.id)}
                              title="Удалить"
                              type="button"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-lots">
              <p>Лоты для данных торгов не добавлены</p>
              <p>Нажмите "Добавить лот" для создания первого лота</p>
            </div>
          )}

        </div>

      {/* Модальное окно для прикрепления существующих лотов */}
      {showAttachLotModal && (
        <AttachLotModal
          allLots={allLots}
          attachedLots={lots}
          allAuctions={allAuctions}
          currentAuctionId={auction?.id || null}
          isAdmin={isAdmin}
          lotColWidths={lotColWidths}
          onStartResizeLotCol={onStartResizeLotCol}
          onAttach={(lotId) => {
            return onAttachExistingLot(lotId, auction, lots, setLots, loadAuctionLots, showToast, setShowAttachLotModal, allLots);
          }}
          onClose={() => setShowAttachLotModal(false)}
        />
      )}

      {/* Модальное окно для создания/редактирования лота */}
      {showLotForm && (
        <UniversalModal
          isOpen={showLotForm}
          onClose={handleCancelLotModal}
          title={
            lotModalMode === 'view'
              ? 'Лот'
              : editingLot ? 'Редактирование лота' : 'Добавление нового лота'
          }
          isViewMode={lotModalMode === 'view'}
          onEdit={lotModalMode === 'view' && !isViewMode ? () => setLotModalMode('edit') : null}
        >
          <SharedLotForm
            lot={editingLot}
            formData={lotFormData}
            setFormData={setLotFormData}
            initialLotNumber={lotFormInitialNumber}
            onSave={handleSaveLot}
            onCancel={handleCancelLotModal}
            isViewMode={lotModalMode === 'view'}
            onFormChange={setLotHasUnsavedChanges}
            isLotAttached={() => true}
            getAuctionInfo={() =>
              auction?.id ? `Привязан к торгу "${getAuctionTitleLabel(auction)}"` : 'Временный (будет сохранен вместе с торгом)'
            }
            vatLocked={
              (selectedDebtor?.type === 'legal' && selectedDebtor?.procedure === 'competition') ||
              ((selectedDebtor?.type === 'individual' || selectedDebtor?.type === 'entrepreneur') &&
                selectedDebtor?.procedure === 'citizen_property_realization')
            }
          />
        </UniversalModal>
      )}

      {/* Уведомление о недоступности разделов документов и экспорта */}
      {sectionsLocked && (
        <div className="documents-activation-notice" role="status" aria-live="polite">
          Заполните обязательные параметры торгов, чтобы активировать разделы Документы торгов и Федресурс / ЭТП.
        </div>
      )}

      {/* Раздел документов */}
        <div className={`documents-section ${sectionsLocked ? 'section-disabled' : ''}`}>
          <div className="section-header">
            <h4>Документы торгов</h4>
            <button
              className="btn btn-success btn-sm"
              onClick={handleOpenDocumentModal}
              disabled={sectionsLocked}
              title={sectionsLocked ? 'Заполните обязательные параметры торгов' : ''}
            >
              Создать документ
            </button>
          </div>
          {/* Список документов */}
          {documents.length > 0 ? (
            <table className="documents-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}></th>
                  <th
                    className={`sortable ${docSortField === 'title' ? `sort-${docSortDirection}` : ''}`}
                    onClick={() => handleDocSort('title')}
                  >
                    Название
                  </th>
                  <th
                    className={`sortable ${docSortField === 'created_date' ? `sort-${docSortDirection}` : ''}`}
                    onClick={() => handleDocSort('created_date')}
                  >
                    Создан
                  </th>
                  <th
                    className={`sortable ${docSortField === 'updated_date' ? `sort-${docSortDirection}` : ''}`}
                    onClick={() => handleDocSort('updated_date')}
                  >
                    Изменен
                  </th>
                  <th className="document-table-actions">Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedDocuments.map((doc) => (
                  <tr
                    key={doc.id}
                    onClick={() => handleViewDocument(doc.id)}
                  >
                    <td>
                      <div className="word-icon-compact">
                        <div className="word-icon-header">
                          <div className="word-ribbon"></div>
                          <div className="word-ribbon"></div>
                          <div className="word-ribbon"></div>
                        </div>
                        <div className="word-icon-body">
                          <div className="word-letter">W</div>
                        </div>
                        <div className="word-icon-corner"></div>
                      </div>
                    </td>
                    <td>
                      <div className="document-table-title">{doc.title}</div>
                    </td>
                    <td>
                      <div className="document-table-date">
                        {new Date(doc.created_date).toLocaleDateString('ru-RU', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="document-table-date">
                        {doc.updated_date ? new Date(doc.updated_date).toLocaleDateString('ru-RU', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </div>
                    </td>
                    <td className="document-table-actions">
                      <div className="card-actions documents-table-actions">
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadWord(doc.id, doc.title);
                          }}
                          title="Скачать в формате Word"
                        >
                          ⬇
                        </button>
                        {!isViewMode && (
                          <button
                            type="button"
                            className="card-action-btn card-action-btn--delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDocument(doc.id);
                            }}
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-documents">
              <p>Документы не добавлены</p>
            </div>
          )}
        </div>

      <div className={`etp-export-history-card ${sectionsLocked ? 'section-disabled' : ''}`}>
          <div className="etp-export-history-header">
            <strong>Сообщение о проведении торгов на Федресурсе</strong>
            <div className="etp-export-controls etp-export-controls--fedresurs">
              <input
                type="text"
                className="etp-export-input etp-export-input--auth etp-export-input--login"
                placeholder="Логин Федресурса"
                value={fedresursLoginInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setFedresursLoginInput(nextValue);
                  if (auction?.id) {
                    writeFedresursLoginByAuction(auction.id, nextValue);
                  }
                }}
                disabled={sectionsLocked || isViewMode}
              />
              <input
                type="password"
                className="etp-export-input etp-export-input--auth"
                placeholder="Пароль Федресурса"
                value={fedresursPromptPassword}
                onChange={(e) => setFedresursPromptPassword(e.target.value)}
                disabled={sectionsLocked || isViewMode}
              />
              <button
                type="button"
                className="btn btn-sm fedresurs-message-btn etp-export-action-btn"
                ref={fedresursAddMessageBtnRef}
                onClick={handleStartFedresursExport}
                disabled={sectionsLocked || isViewMode || fedresursExportStarting || hasRunningFedresursExport}
              >
                {fedresursExportStarting ? 'Постановка...' : 'Добавить сообщение'}
              </button>
            </div>
          </div>
          {(fedresursExportStarting || hasRunningFedresursExport) && (
            <div className="fedresurs-progress-box">
              <div className="fedresurs-progress-header">
                <span>
                  {fedresursExportStarting && !hasRunningFedresursExport
                    ? 'Постановка задания в очередь...'
                    : `${String(activeFedresursJob?.status || '').toLowerCase() === 'queued' ? 'Ожидает очереди' : 'Формирование выполняется'}${activeFedresursJob?.created_at ? ` (создано ${formatLocalDateTime(activeFedresursJob.created_at)})` : ''}. Обновление каждые 2 сек.`}
                </span>
                <span>{Math.round(activeFedresursProgress)}%</span>
              </div>
              <div className="fedresurs-progress-track" aria-hidden="true">
                <div className="fedresurs-progress-bar" style={{ width: `${activeFedresursProgress}%` }} />
              </div>
              <div className="etp-export-muted">
                {activeFedresursStep?.step_name ? `Текущий шаг: ${activeFedresursStep.step_name}.` : 'Шаги появятся после старта обработки.'}
                {activeFedresursStep?.details ? ` ${activeFedresursStep.details}` : ''}
              </div>
            </div>
          )}
          {fedresursExportsLoading ? (
            <div className="etp-export-muted">Загрузка истории добавления...</div>
          ) : fedresursExports.length === 0 ? (
            <div className="etp-export-muted">Запусков добавления пока нет.</div>
          ) : (
            <div className="etp-export-table-wrapper">
              <table className="etp-export-table etp-export-table--resizable">
                <colgroup>
                  <col style={{ width: `${integrationsJournalColWidths.started_at}px` }} />
                  <col style={{ width: `${integrationsJournalColWidths.status}px` }} />
                  <col style={{ width: `${integrationsJournalColWidths.link}px` }} />
                  <col style={{ width: `${integrationsJournalColWidths.error}px` }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Запущено{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'started_at')} />}</th>
                    <th>Статус{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'status')} />}</th>
                    <th>Ссылка{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'link')} />}</th>
                    <th>Ошибка{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'error')} />}</th>
                  </tr>
                </thead>
                <tbody>
                  {fedresursExports.slice(0, 5).map((job) => (
                    <tr key={job.id}>
                      <td>{formatLocalDateTime(job.created_at) || '-'}</td>
                      <td>{getFedresursExportStatusText(job.status)}</td>
                      <td>
                        {job.result_url ? (
                          <a href={job.result_url} target="_blank" rel="noreferrer">Открыть</a>
                        ) : '-'}
                      </td>
                      <td>{job.error_short || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      <div className={`etp-export-history-card ${sectionsLocked ? 'section-disabled' : ''}`}>
          <div className="etp-export-history-header">
            <strong>Заявка на проведение торгов в ЭТП</strong>
            <div className="etp-export-controls etp-export-controls--etp">
              {canSelectEtpHost ? (
                <div
                  className="etp-export-input etp-export-input--host"
                  style={{ padding: 0, border: 'none', background: 'transparent', minWidth: 0, position: 'relative' }}
                  ref={etpHostMenuRef}
                >
                  <button
                    type="button"
                    className="etp-export-input"
                    disabled={sectionsLocked || isViewMode}
                    onClick={() => setEtpHostMenuOpen((prev) => !prev)}
                    style={{
                      textAlign: 'left',
                      cursor: (sectionsLocked || isViewMode) ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      lineHeight: '1.2',
                      height: 34,
                      background: isViewMode ? '#e9ecef' : '#fff',
                    }}
                    title={etpHostInput || 'Выберите хост ЭТП'}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {etpHostInput || 'Выберите хост ЭТП'}
                    </span>
                    <span style={{ opacity: 0.65, flex: '0 0 auto' }}>▾</span>
                  </button>
                  {etpHostMenuOpen && !sectionsLocked && !isViewMode && (
                    <div
                      style={{
                        position: 'absolute',
                        zIndex: 40,
                        left: 0,
                        right: 'auto',
                        top: 'calc(100% + 4px)',
                        maxHeight: 260,
                        overflowY: 'auto',
                        width: 'max-content',
                        maxWidth: 'min(86vw, 920px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        border: '1px solid #ced4da',
                        borderRadius: 6,
                        background: '#fff',
                        boxShadow: '0 8px 20px rgba(16, 24, 40, 0.12)',
                      }}
                    >
                      {etpHostOptions.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          onClick={() => {
                            setEtpHostInput(option.value);
                            setFormData((prev) => ({ ...(prev || {}), etp_host: option.value }));
                            setEtpHostMenuOpen(false);
                          }}
                          style={{
                            width: 'auto',
                            textAlign: 'left',
                            padding: '8px 10px',
                            border: 'none',
                            borderBottom: '1px solid #f1f3f5',
                            background: option.value === etpHostInput ? '#f1f7ff' : '#fff',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.9rem',
                          }}
                          title={option.label}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  className="etp-export-input etp-export-input--host"
                  placeholder="Хост ЭТП"
                  value={etpHostInput}
                  disabled
                />
              )}
              <input
                type="text"
                className="etp-export-input etp-export-input--auth etp-export-input--login"
                placeholder="Логин ЭТП"
                value={etpLoginInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setEtpLoginInput(nextValue);
                  if (auction?.id) {
                    writeEtpLoginByAuction(auction.id, nextValue);
                  }
                }}
                disabled={sectionsLocked || isViewMode}
              />
              <input
                type="password"
                className="etp-export-input etp-export-input--auth"
                placeholder="Пароль ЭТП"
                value={etpPromptPassword}
                onChange={(e) => setEtpPromptPassword(e.target.value)}
                disabled={sectionsLocked || isViewMode}
              />
              <button
                type="button"
                className="btn btn-sm etp-export-start-btn etp-export-action-btn"
                onClick={handleStartEtpExport}
                disabled={sectionsLocked || isViewMode || etpExportStarting}
              >
                {etpExportStarting ? 'Запуск...' : 'Экспорт в ЭТП'}
              </button>
            </div>
          </div>
          {hasRunningEtpExport && (
            <div className="fedresurs-progress-box">
              <div className="fedresurs-progress-header">
                <span>Прогресс экспорта в ЭТП</span>
                <strong>{activeEtpProgress}%</strong>
              </div>
              <div className="fedresurs-progress-track" aria-hidden="true">
                <div className="fedresurs-progress-bar" style={{ width: `${activeEtpProgress}%` }} />
              </div>
              <div className="etp-export-muted">
                {activeEtpStep?.step_name ? `Текущий шаг: ${activeEtpStep.step_name}.` : 'Шаги появятся после старта обработки.'}
              </div>
            </div>
          )}
          {etpExportsLoading ? (
            <div className="etp-export-muted">Загрузка истории экспорта...</div>
          ) : etpExports.length === 0 ? (
            <div className="etp-export-muted">Запусков экспорта пока нет.</div>
          ) : (
            <div className="etp-export-table-wrapper">
              <table className="etp-export-table etp-export-table--resizable">
                <colgroup>
                  <col style={{ width: `${integrationsJournalColWidths.started_at}px` }} />
                  <col style={{ width: `${integrationsJournalColWidths.status}px` }} />
                  <col style={{ width: `${integrationsJournalColWidths.link}px` }} />
                  <col style={{ width: `${integrationsJournalColWidths.error}px` }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Запущено{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'started_at')} />}</th>
                    <th>Статус{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'status')} />}</th>
                    <th>Ссылка{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'link')} />}</th>
                    <th>Ошибка{isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeIntegrationsJournalCol?.(e, 'error')} />}</th>
                  </tr>
                </thead>
                <tbody>
                  {etpExports.slice(0, 5).map((job) => (
                    <tr key={job.id}>
                      <td>{formatLocalDateTime(job.created_at) || '-'}</td>
                      <td>{getEtpExportStatusText(job.status)}</td>
                      <td>
                        {job.result_url ? (
                          <a href={job.result_url} target="_blank" rel="noreferrer">Открыть</a>
                        ) : '-'}
                      </td>
                      <td>{job.error_short || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      <div className="form-actions">
        {isViewMode ? (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Закрыть
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => performSubmit({ exit: false })}
              disabled={isSaving}
            >
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => performSubmit({ exit: true })}
              disabled={isSaving}
            >
              {isSaving ? 'Сохранение...' : 'Сохранить и выйти'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSaving}>
              Отменить
            </button>
          </>
        )}
      </div>

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      {inlineChoice.isOpen && createPortal(
        <div
          className="inline-choice-popover"
          style={{ top: `${inlineChoice.top}px`, left: `${inlineChoice.left}px` }}
        >
          <div className="inline-choice-message">{inlineChoice.message}</div>
          <div className="inline-choice-actions">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => {
                const cancelHandler = inlineChoiceRef.current?.onCancel;
                hideInlineChoice();
                if (typeof cancelHandler === 'function') {
                  setTimeout(() => {
                    cancelHandler();
                  }, 0);
                }
              }}
            >
              {inlineChoice.cancelText || 'Отмена'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                const confirmHandler = inlineChoiceRef.current?.onConfirm;
                hideInlineChoice();
                if (typeof confirmHandler === 'function') {
                  setTimeout(() => {
                    confirmHandler();
                  }, 0);
                }
              }}
            >
              {inlineChoice.confirmText || 'ОК'}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Модальное окно создания документа */}
      {showDocumentModal && createPortal(
        <DocumentCreateModal
          templates={templates}
          auctionId={(documentAuctionContext || auction || {}).id}
          auction={documentAuctionContext || auction}
          debtors={debtors}
          organizers={organizers}
          lots={lots}
          etps={etps}
          documents={documents}
          showToast={showToast}
          onClose={() => {
            setShowDocumentModal(false);
            setDocumentAuctionContext(null);
          }}
          onSuccess={() => {
            setShowDocumentModal(false);
            setDocumentAuctionContext(null);
            loadAuctionDocuments();
            // Обновляем статистику в основном списке торгов
            const updatedAuctionId = (documentAuctionContext || auction || {}).id;
            if (onAuctionUpdate && updatedAuctionId) {
              onAuctionUpdate(updatedAuctionId);
            }
            showToast('Документ успешно создан', 'success');
          }}
        />,
        document.body
      )}

      {/* Модальное окно редактирования/просмотра документа */}
      {showDocumentViewer && viewingDocument && (
        <DocumentEditor
          document={{
            id: viewingDocument.id,
            name: viewingDocument.title,
            content: viewingDocument.content
          }}
          templateName={viewingDocument.template_name}
          onSave={async (documentId, documentData, { exit = false } = {}) => {
            await handleSaveDocument(documentId, documentData, { exit });
            if (exit) {
              setShowDocumentViewer(false);
              setViewingDocument(null);
            }
          }}
          onCancel={() => {
            setShowDocumentViewer(false);
            setViewingDocument(null);
          }}
        />
      )}

      <ReminderFormModal
        isOpen={reminderModalOpen}
        onClose={() => setReminderModalOpen(false)}
        onSubmit={handleReminderSubmit}
        auctions={auction ? [auction] : []}
        initialData={reminderInitial}
        allowAuctionSelect={false}
        allowEventSelect={false}
      />
      <Toast toast={toast} />

    </div>
  );
};

const DocumentCreateModal = ({ templates, auctionId, auction, debtors, organizers, lots, etps = [], documents, showToast, onClose, onSuccess }) => {
  const MARGINS_TAG_REGEX = /<div[^>]*data-a4-margins=(['"])([\s\S]*?)\1[^>]*><\/div>/i;
  const DEFAULT_EDITOR_MARGINS = { top: 76, right: 57, bottom: 76, left: 113 };
  const stripMarginsTag = (html) => String(html || '').replace(MARGINS_TAG_REGEX, '');
  const ensureMarginsTag = (html, margins) => `<div data-a4-margins='${JSON.stringify(margins || DEFAULT_EDITOR_MARGINS)}'></div>${stripMarginsTag(html)}`;

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [editorHeaderContent, setEditorHeaderContent] = useState('');
  const [editorFooterContent, setEditorFooterContent] = useState('');
  const [editorHeaderGapPx, setEditorHeaderGapPx] = useState(12);
  const [editorFooterGapPx, setEditorFooterGapPx] = useState(12);
  const [createdDocumentId, setCreatedDocumentId] = useState(null);
  const [showPreviewMode, setShowPreviewMode] = useState(false);
  const [showUserTemplates, setShowUserTemplates] = useState(false);
  const [showKommersantPartyModal, setShowKommersantPartyModal] = useState(false);
  const [kommersantPartyKind, setKommersantPartyKind] = useState('');
  const [showDepositEtpNoticeModal, setShowDepositEtpNoticeModal] = useState(false);

  // Обработчик Escape для закрытия модального окна (только когда не показан редактор)
  useEffect(() => {
    const handleEscape = (e) => {
      const key = String(e?.key || '').toLowerCase();
      const codeName = String(e?.code || '').toLowerCase();
      const code = Number(e?.keyCode || e?.which || 0);
      const isEscape = key === 'escape' || key === 'esc' || codeName === 'escape' || code === 27;
      if (isEscape && !showEditor) {
        if (showKommersantPartyModal) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          setShowKommersantPartyModal(false);
          return;
        }
        if (showDepositEtpNoticeModal) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          setShowDepositEtpNoticeModal(false);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape, true);
    window.document.addEventListener('keydown', handleEscape, true);
    return () => {
      window.removeEventListener('keydown', handleEscape, true);
      window.document.removeEventListener('keydown', handleEscape, true);
    };
  }, [onClose, showEditor, showDepositEtpNoticeModal, showKommersantPartyModal]);

  const standardTemplates = (templates || []).filter((t) => t?.is_standard);
  const userTemplates = (templates || []).filter((t) => !t?.is_standard);
  const currentDebtor = (debtors || []).find((d) => d?.id === auction?.debtor);
  const isLegalDebtor = String(currentDebtor?.type || '').toLowerCase() === 'legal';
  const KOMMERSANT_LEGAL_TEMPLATE_NAME = 'Заявка в газету «Коммерсант»';
  const LEGACY_KOMMERSANT_LEGAL_TEMPLATE_NAME = 'Сообщение о банкротстве ЮЛ в Коммерсантъ';
  const SALE_CONTRACT_TEMPLATE_NAME = 'Проект договора купли-продажи';
  const ASSIGNMENT_CONTRACT_TEMPLATE_NAME = 'Проект договора уступки';
  const LEGACY_ASSIGNMENT_CONTRACT_TEMPLATE_NAME = 'Договор уступки';
  const normalizedLots = Array.isArray(lots) ? lots : [];
  const hasDebtLots = normalizedLots.some((lot) => String(lot?.category || '').toLowerCase() === 'debt');
  const hasNonDebtLots = normalizedLots.some((lot) => String(lot?.category || '').toLowerCase() !== 'debt');
  const selectedTemplateObject = (templates || []).find((t) => String(t?.id) === String(selectedTemplate));
  const isKommersantTemplateSelected = selectedTemplateObject?.name === KOMMERSANT_LEGAL_TEMPLATE_NAME
    || selectedTemplateObject?.name === LEGACY_KOMMERSANT_LEGAL_TEMPLATE_NAME;
  const isStandardDepositAgreementTemplateSelected = selectedTemplateObject?.is_standard
    && String(selectedTemplateObject?.name || '') === 'Договор о задатке';

  const selectTemplate = (template) => {
    if (!template) return;
    const templateId = String(template.id);
    setSelectedTemplate(templateId);
    setShowKommersantPartyModal(false);
    const autoTitle = generateDocumentTitle(template, auction, documents);
    setDocumentTitle(autoTitle);
  };

  const standardCards = [
    { key: 'Сообщение о торгах', title: 'Сообщение о торгах', icon: 'notice' },
    { key: 'Договор о задатке', title: 'Договор о задатке', icon: 'deposit' },
    { key: 'Агентский договор на организацию и проведение торгов', title: 'Агентский договор', icon: 'agent' },
    { key: 'Проект договора купли-продажи', title: 'Проект договора купли-продажи', icon: 'sale' },
    { key: ASSIGNMENT_CONTRACT_TEMPLATE_NAME, title: 'Проект договора уступки', icon: 'assignment' },
    { key: KOMMERSANT_LEGAL_TEMPLATE_NAME, title: 'Заявка в газету «Коммерсант»', icon: 'kommersant' },
  ].map((card) => {
    const matchedTemplate = standardTemplates.find((t) => {
      const name = String(t?.name || '');
      if (name === card.key) return true;
      if (card.key === ASSIGNMENT_CONTRACT_TEMPLATE_NAME && name === LEGACY_ASSIGNMENT_CONTRACT_TEMPLATE_NAME) return true;
      if (card.key === KOMMERSANT_LEGAL_TEMPLATE_NAME && name === LEGACY_KOMMERSANT_LEGAL_TEMPLATE_NAME) return true;
      return false;
    }) || null;
    let blockedByRule = false;
    let disabledHint = 'Шаблон не найден';
    let badgeText = matchedTemplate?.template_type_name || card.key;

    if (card.key === KOMMERSANT_LEGAL_TEMPLATE_NAME && !isLegalDebtor) {
      blockedByRule = true;
      badgeText = 'Сообщение о торгах по форме заявки в газету «Коммерсант»';
      disabledHint = `${KOMMERSANT_LEGAL_TEMPLATE_NAME} доступен только для должника-юридического лица`;
    } else if (card.key === SALE_CONTRACT_TEMPLATE_NAME && !hasNonDebtLots) {
      blockedByRule = true;
      disabledHint = `${SALE_CONTRACT_TEMPLATE_NAME} доступен только при наличии хотя бы одного лота, не относящегося к дебиторке`;
    } else if ((card.key === ASSIGNMENT_CONTRACT_TEMPLATE_NAME || card.key === LEGACY_ASSIGNMENT_CONTRACT_TEMPLATE_NAME) && !hasDebtLots) {
      blockedByRule = true;
      disabledHint = `${ASSIGNMENT_CONTRACT_TEMPLATE_NAME} доступен только при наличии лота категории «Дебиторка»`;
    }

    return {
      ...card,
      template: blockedByRule ? null : matchedTemplate,
      badgeText,
      disabledHint,
    };
  });

  useEffect(() => {
    if (!selectedTemplate) return;
    const selected = (templates || []).find((t) => String(t?.id) === String(selectedTemplate));
    if (!selected) return;
    const selectedName = String(selected?.name || '');
    const blockedByRules =
      ((selectedName === KOMMERSANT_LEGAL_TEMPLATE_NAME || selectedName === LEGACY_KOMMERSANT_LEGAL_TEMPLATE_NAME) && !isLegalDebtor) ||
      (selectedName === SALE_CONTRACT_TEMPLATE_NAME && !hasNonDebtLots) ||
      ((selectedName === ASSIGNMENT_CONTRACT_TEMPLATE_NAME || selectedName === LEGACY_ASSIGNMENT_CONTRACT_TEMPLATE_NAME) && !hasDebtLots);

    if (blockedByRules) {
      setSelectedTemplate('');
      setDocumentTitle('');
    }
  }, [selectedTemplate, templates, isLegalDebtor, hasNonDebtLots, hasDebtLots]);

  useEffect(() => {
    if (!isKommersantTemplateSelected && kommersantPartyKind) {
      setKommersantPartyKind('');
    }
  }, [isKommersantTemplateSelected, kommersantPartyKind]);

  const needsTemplatePickerVariables = isKommersantTemplateSelected || isStandardDepositAgreementTemplateSelected;
  const baseVariablesData = useMemo(
    () => (needsTemplatePickerVariables ? getDocumentVariablesData(auction, debtors, organizers, lots || [], etps) : null),
    [needsTemplatePickerVariables, auction, debtors, organizers, lots, etps]
  );

  const kommersantPartyOptions = useMemo(() => {
    const debtorName = isKommersantTemplateSelected ? String(baseVariablesData?.debtor_name || '').trim() : '';
    const organizerName = isKommersantTemplateSelected ? String(baseVariablesData?.organizer_name || '').trim() : '';
    const managerName = isKommersantTemplateSelected ? String(baseVariablesData?.debtor_manager_name || '').trim() : '';

    return [
      { key: 'debtor', label: 'Должник', name: debtorName, disabled: !debtorName },
      { key: 'organizer', label: 'Организатор', name: organizerName, disabled: !organizerName },
      { key: 'manager', label: 'Арбитражный управляющий', name: managerName, disabled: !managerName },
    ];
  }, [baseVariablesData, isKommersantTemplateSelected]);

  const getKommersantPartyVariables = useCallback((kind) => {
    const selected = kommersantPartyOptions.find((option) => option.key === kind);
    const name = String(selected?.name || '').trim();
    if (!name) {
      return {
        kommersant_party_kind: '',
        kommersant_party_name: '',
        kommersant_party_name_r: '',
        kommersant_party_name_d: '',
        kommersant_party_name_v: '',
        kommersant_party_name_t: '',
        kommersant_party_name_p: '',
      };
    }
    const cases = createAllCases(name);
    return {
      kommersant_party_kind: kind,
      kommersant_party_name: cases.i || name,
      kommersant_party_name_r: cases.r || '',
      kommersant_party_name_d: cases.d || '',
      kommersant_party_name_v: cases.v || '',
      kommersant_party_name_t: cases.t || '',
      kommersant_party_name_p: cases.p || '',
    };
  }, [kommersantPartyOptions]);

  const getMergedVariablesData = useCallback((lotsData = lots || [], partyKindOverride = null) => {
    const base = getDocumentVariablesData(auction, debtors, organizers, lotsData, etps);
    if (!isKommersantTemplateSelected) {
      return base;
    }
    const effectivePartyKind = partyKindOverride ?? kommersantPartyKind;
    return {
      ...base,
      ...getKommersantPartyVariables(effectivePartyKind),
    };
  }, [
    auction,
    debtors,
    organizers,
    lots,
    etps,
    isKommersantTemplateSelected,
    getKommersantPartyVariables,
    kommersantPartyKind,
  ]);

  const resolveTemplateIncludes = useCallback(async (html, stack = new Set()) => {
    if (!html) return html;
    const includeRe = /\{\{\s*include_template\s*:\s*([^}]+?)\s*\}\}/gi;
    const ids = new Set();
    const esc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let match;
    while ((match = includeRe.exec(html)) !== null) {
      const raw = String(match[1] || '').trim();
      if (/^\d+$/.test(raw)) ids.add(raw);
    }
    if (ids.size === 0) return html;

    let resolved = html;
    for (const id of ids) {
      const inlineRe = new RegExp(`\\{\\{\\s*include_template\\s*:\\s*${esc(id)}\\s*\\}\\}`, 'gi');
      const paragraphRe = new RegExp(
        `<p\\b[^>]*>(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*(?:<(?:span|strong|b|em|i|u|mark)\\b[^>]*>(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*)*${inlineRe.source}(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*(?:<\\/(?:span|strong|b|em|i|u|mark)>(?:\\s|&nbsp;|\\u00A0|<br\\s*\\/?>)*)*<\\/p>`,
        'gi'
      );
      if (stack.has(id)) {
        resolved = resolved.replace(paragraphRe, '');
        resolved = resolved.replace(inlineRe, '');
        continue;
      }
      try {
        const resp = await apiService.getDocumentTemplate(id);
        const tplContent = resp.data?.content || '';
        const nested = await resolveTemplateIncludes(tplContent, new Set([...stack, id]));
        resolved = resolved.replace(paragraphRe, nested);
        resolved = resolved.replace(inlineRe, nested);
      } catch (err) {
        console.error('Include template load error:', err);
      }
    }
    return resolved;
  }, []);

  const isDepositAccountOwnerPlatform = isStandardDepositAgreementTemplateSelected
    && String(baseVariablesData?.deposit_account_owner_type_code || '').toLowerCase() === 'platform';

  const renderTemplateIcon = (type) => {
    switch (type) {
      case 'notice':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 11v2l11 4V7L3 11zm14-6v14h2V5h-2zm-6 13a2 2 0 0 1-4 0v-1h4v1z" />
          </svg>
        );
      case 'deposit':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 1l8 4v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-4zm0 4l-4 2v4c0 3.2 2.1 6.1 4 7 1.9-.9 4-3.8 4-7V7l-4-2z" />
          </svg>
        );
      case 'agent':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 12l2 2 3-3 5 5 2-2-7-7-5 5zm10-7h4v4h-2V7h-2V5zM3 15v4h4v-2H5v-2H3z" />
          </svg>
        );
      case 'sale':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 2h8l4 4v16H6V2zm8 2H8v16h8V6h-2V4zM9 9h6v2H9V9zm0 4h6v2H9v-2z" />
          </svg>
        );
      case 'assignment':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 4h10a2 2 0 0 1 2 2v7h-2V6H7v12h5v2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm6 9h8v2h-8v-2zm0 4h8v2h-8v-2zm0-8h8v2h-8V9z" />
          </svg>
        );
      case 'kommersant':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 3h16v3H4V3zm0 5h16v13H4V8zm2 2v9h12v-9H6zm2 2h8v2H8v-2zm0 3h8v2H8v-2z" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 2h8l4 4v16H6V2zm8 2H8v16h8V6h-2V4zM9 9h6v2H9V9zm0 4h6v2H9v-2z" />
          </svg>
        );
    }
  };

  // Функция для проверки заполненности важных полей
  const checkMissingFields = () => {
    const missing = [];
    const debtor = debtors?.find(d => d.id === auction?.debtor);
    const organizer = organizers?.find(o => o.id === auction?.organizer);
    const organizerAddress = organizer?.address || organizer?.mailing_address || '';

    // Проверяем основные поля торгов
    if (!auction?.applications_start) missing.push('Начало приема заявок');
    if (auction?.auction_type !== 'public_offer' && !auction?.applications_end) {
      missing.push('Окончание приема заявок');
    }
    const needsResultsDate = auction?.price_offer_form === 'closed' || auction?.auction_type === 'public_offer';
    if (needsResultsDate && !auction?.results_datetime) {
      missing.push(auction?.auction_type === 'public_offer' ? 'Окончание торгов' : 'Подведение итогов');
    }

    // Проверяем данные должника
    if (!debtor?.name) missing.push('Наименование должника');
    if (!debtor?.inn) missing.push('ИНН должника');
    if (!debtor?.address) missing.push('Адрес должника');

    // Проверяем данные организатора
    if (!organizer?.name) missing.push('Наименование организатора');
    if (!organizer?.inn) missing.push('ИНН организатора');
    if (!organizerAddress) missing.push('Адрес организатора');

    // Проверяем наличие лотов
    if (!lots || lots.length === 0) missing.push('Лоты торгов');

    return missing;
  };

  const proceedCreate = async ({ force = false, skipDepositEtpNotice = false } = {}) => {
    if (!selectedTemplate || !documentTitle) {
      showToast('Пожалуйста, выберите шаблон и введите название документа', 'error');
      return;
    }

    if (!skipDepositEtpNotice && isStandardDepositAgreementTemplateSelected && isDepositAccountOwnerPlatform) {
      setShowDepositEtpNoticeModal(true);
      return;
    }

    if (!force) {
      // Проверяем заполненность полей
      const missing = checkMissingFields();
      if (missing.length > 0) {
        setMissingFields(missing);
        setShowWarning(true);
        return;
      }
    }

    // Показываем редактор
    await showEditorDocument();
  };

  const handleCreate = async () => {
    await proceedCreate();
  };

  const handleForceCreate = async () => {
    try {
      setShowWarning(false);
      // Показываем редактор
      await proceedCreate({ force: true });
    } catch (error) {
      console.error('Error in handleForceCreate:', error);
      showToast('Ошибка при обработке запроса', 'error');
    }
  };

  const showEditorDocument = async ({ skipPartyPrompt = false, partyKindOverride = null } = {}) => {
    if (!selectedTemplate) {
      showToast('Пожалуйста, выберите шаблон', 'error');
      return false;
    }

    if (!templates || templates.length === 0) {
      showToast('Шаблоны не загружены. Попробуйте обновить страницу.', 'error');
      return false;
    }

    if (isKommersantTemplateSelected && !skipPartyPrompt) {
      setShowKommersantPartyModal(true);
      return false;
    }

    setLoading(true);
    try {
      // Логируем для диагностики

      // Получаем шаблон с более надежным поиском
      const template = templates.find(t => String(t.id) === String(selectedTemplate));
      if (!template) {
        console.error('Template not found:', selectedTemplate, 'in', templates.map(t => t.id));
        showToast(`Шаблон не найден. Выбранный ID: ${selectedTemplate}`, 'error');
        return false;
      }


      // Если у шаблона нет содержимого, получаем его отдельно через API
      const parsedTemplate = extractTemplateMeta(template.content || '');
      let templateContent = parsedTemplate.content || template.content;
      let templateHeaderContent = template.header_content || parsedTemplate.header_content || '';
      let templateFooterContent = template.footer_content || parsedTemplate.footer_content || '';
      const parsedContentMargins = extractMarginsAndBody(templateContent || '', DEFAULT_EDITOR_MARGINS)?.margins || DEFAULT_EDITOR_MARGINS;
      const hasTemplateMargins = ['margin_top_px', 'margin_right_px', 'margin_bottom_px', 'margin_left_px']
        .every((key) => Number.isFinite(Number(template?.[key])) && Number(template?.[key]) >= 0);
      let templateMarginsPx = {
        top: Number.isFinite(Number(template?.margin_top_px)) ? Math.max(0, Number(template.margin_top_px)) : Math.max(0, Number(parsedContentMargins?.top) || DEFAULT_EDITOR_MARGINS.top),
        right: Number.isFinite(Number(template?.margin_right_px)) ? Math.max(0, Number(template.margin_right_px)) : Math.max(0, Number(parsedContentMargins?.right) || DEFAULT_EDITOR_MARGINS.right),
        bottom: Number.isFinite(Number(template?.margin_bottom_px)) ? Math.max(0, Number(template.margin_bottom_px)) : Math.max(0, Number(parsedContentMargins?.bottom) || DEFAULT_EDITOR_MARGINS.bottom),
        left: Number.isFinite(Number(template?.margin_left_px)) ? Math.max(0, Number(template.margin_left_px)) : Math.max(0, Number(parsedContentMargins?.left) || DEFAULT_EDITOR_MARGINS.left),
      };
      let templateHeaderGapPx = Number.isFinite(Number(parsedTemplate.header_gap_px))
        ? Math.max(0, Number(parsedTemplate.header_gap_px))
        : 12;
      let templateFooterGapPx = Number.isFinite(Number(parsedTemplate.footer_gap_px))
        ? Math.max(0, Number(parsedTemplate.footer_gap_px))
        : 12;
      const needFullTemplate =
        !templateContent
        || !hasTemplateMargins
        || !Object.prototype.hasOwnProperty.call(template, 'header_content')
        || !Object.prototype.hasOwnProperty.call(template, 'footer_content');
      if (needFullTemplate) {
        try {
          const fullTemplateResponse = await apiService.getDocumentTemplate(template.id);
          const parsedFullTemplate = extractTemplateMeta(fullTemplateResponse.data?.content || '');
          templateContent = parsedFullTemplate.content || fullTemplateResponse.data?.content;
          templateHeaderContent = fullTemplateResponse.data?.header_content || parsedFullTemplate.header_content || templateHeaderContent;
          templateFooterContent = fullTemplateResponse.data?.footer_content || parsedFullTemplate.footer_content || templateFooterContent;
          templateMarginsPx = {
            top: Number.isFinite(Number(fullTemplateResponse.data?.margin_top_px)) ? Math.max(0, Number(fullTemplateResponse.data.margin_top_px)) : templateMarginsPx.top,
            right: Number.isFinite(Number(fullTemplateResponse.data?.margin_right_px)) ? Math.max(0, Number(fullTemplateResponse.data.margin_right_px)) : templateMarginsPx.right,
            bottom: Number.isFinite(Number(fullTemplateResponse.data?.margin_bottom_px)) ? Math.max(0, Number(fullTemplateResponse.data.margin_bottom_px)) : templateMarginsPx.bottom,
            left: Number.isFinite(Number(fullTemplateResponse.data?.margin_left_px)) ? Math.max(0, Number(fullTemplateResponse.data.margin_left_px)) : templateMarginsPx.left,
          };
          templateHeaderGapPx = Number.isFinite(Number(parsedFullTemplate.header_gap_px))
            ? Math.max(0, Number(parsedFullTemplate.header_gap_px))
            : templateHeaderGapPx;
          templateFooterGapPx = Number.isFinite(Number(parsedFullTemplate.footer_gap_px))
            ? Math.max(0, Number(parsedFullTemplate.footer_gap_px))
            : templateFooterGapPx;
        } catch (fetchError) {
          console.error('Error fetching template content:', fetchError);
          showToast('Ошибка при загрузке содержимого шаблона', 'error');
          return false;
        }
      }

      if (!templateContent) {
        console.error('Template still has no content after fetch:', template);
        showToast('У выбранного шаблона отсутствует содержимое', 'error');
        return false;
      }

      let lotsForVariables = lots || [];
      if (auction?.id) {
        try {
          const lotsResponse = await apiService.getAuctionLots(auction.id);
          lotsForVariables = lotsResponse?.data || [];
        } catch (lotsError) {
          console.error('Error loading lots for document variables:', lotsError);
        }
      }

      const resolvedTemplateContent = await resolveTemplateIncludes(templateContent);

      // Подставляем переменные в содержимое шаблона
      const processedContent = substituteTemplateVariables(resolvedTemplateContent, {
        auction,
        debtors,
        organizers,
        lots: lotsForVariables,
        etps,
        variablesData: getMergedVariablesData(lotsForVariables, partyKindOverride),
      });


      // Устанавливаем обработанное содержимое в редактор
      setEditorContent(ensureMarginsTag(processedContent, templateMarginsPx));
      setEditorHeaderContent(templateHeaderContent);
      setEditorFooterContent(templateFooterContent);
      setEditorHeaderGapPx(templateHeaderGapPx);
      setEditorFooterGapPx(templateFooterGapPx);
      setCreatedDocumentId(null);
      setShowEditor(true);
      return true;
    } catch (error) {
      console.error('Error preparing preview:', error);
      console.error('Error stack:', error.stack);
      showToast('Ошибка при подготовке предпросмотра: ' + error.message, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSelectKommersantParty = async (kind) => {
    const selectedOption = kommersantPartyOptions.find((option) => option.key === kind);
    if (!selectedOption || selectedOption.disabled) {
      showToast('Нет данных для выбранного варианта в карточке торгов', 'error');
      return;
    }
    setKommersantPartyKind(kind);
    setShowKommersantPartyModal(false);
    await showEditorDocument({ skipPartyPrompt: true, partyKindOverride: kind });
  };

  // Функция для предпросмотра с подсвеченными переменными
  const generatePreviewWithHighlight = () => {
    if (!editorContent) return '';

    // Подставляем переменные
    const processedContent = substituteTemplateVariables(editorContent, {
      auction,
      debtors,
      organizers,
      lots,
      etps,
      variablesData: getMergedVariablesData(lots || []),
    });

    // Находим все не подставленные переменные (которые остались в формате {{variable}})
    const unfilledVariables = processedContent.match(/\{\{[^}]+\}\}/g) || [];

    // Подсвечиваем не подставленные переменные желтым
    let highlightedContent = processedContent;
    unfilledVariables.forEach(variable => {
      const highlightedVariable = `<mark style="background-color: yellow; padding: 2px 4px; border-radius: 3px;">${variable}</mark>`;
      highlightedContent = highlightedContent.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), highlightedVariable);
    });

    return highlightedContent;
  };

  const handleSaveDocument = async (overrides = {}) => {
    setLoading(true);
    try {
      const nextTitle = String(overrides?.title ?? documentTitle ?? '').trim();
      const nextContent = overrides?.content ?? editorContent;
      const shouldExit = !!overrides?.exit;

      // Проверяем уникальность названия документа перед сохранением
      const existingTitles = documents
        .filter((doc) => !createdDocumentId || String(doc.id) !== String(createdDocumentId))
        .map(doc => doc.title || doc.name)
        .filter(Boolean);
      if (existingTitles.includes(nextTitle)) {
        showToast('Документ с таким названием уже существует. Пожалуйста, измените название.', 'error');
        setLoading(false);
        return;
      }

      console.log('Saving document with content:', nextContent?.substring(0, 200), '...');
      console.log('Has variables in content:', nextContent?.includes('{{'));

      let lotsForVariables = lots || [];
      if (auction?.id) {
        try {
          const lotsResponse = await apiService.getAuctionLots(auction.id);
          lotsForVariables = lotsResponse?.data || [];
        } catch (lotsError) {
          console.error('Error loading lots for document variables:', lotsError);
        }
      }

      const mergedVariablesData = getMergedVariablesData(lotsForVariables);

      if (createdDocumentId) {
        await apiService.patchDocument(createdDocumentId, {
          title: nextTitle,
          content: nextContent,
          variables_data: mergedVariablesData,
        });
      } else {
        const response = await apiService.createDocumentFromTemplate(selectedTemplate, {
          title: nextTitle,
          auction_id: auctionId,
          content: nextContent, // Сохраняем содержимое редактора с подставленными значениями
          variables_data: mergedVariablesData
        });
        if (response?.data?.id) {
          setCreatedDocumentId(response.data.id);
        }
      }

      setDocumentTitle(nextTitle);
      setEditorContent(nextContent);

      if (shouldExit) {
        onSuccess();
      } else {
        showToast('Документ сохранен', 'success');
      }
    } catch (error) {
      console.error('Error creating document:', error);
      showToast('Ошибка при создании документа', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportDraftDocx = async (overrides = {}) => {
    try {
      const sourceContent = String(overrides?.content ?? editorContent ?? '').trim();
      if (!sourceContent) {
        showToast('Нет содержимого для экспорта', 'error');
        return;
      }
      const { margins, bodyHTML } = extractMarginsAndBody(sourceContent);
      const htmlContent = bodyHTML || sourceContent;
      const processedHeaderContent = substituteTemplateVariables(editorHeaderContent || '', {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData: getMergedVariablesData(lots || []),
      });
      const processedFooterContent = substituteTemplateVariables(editorFooterContent || '', {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData: getMergedVariablesData(lots || []),
      });
      const safeTitle = String(overrides?.title ?? documentTitle ?? 'Документ').trim() || 'Документ';
      const fileName = `${safeTitle}`.replace(/[\\/:*?"<>|]/g, '_');
      await exportToDocx(htmlContent, fileName, margins || {}, {
        headerContent: processedHeaderContent,
        footerContent: processedFooterContent,
        headerGapPx: editorHeaderGapPx,
        footerGapPx: editorFooterGapPx,
      });
      showToast('Документ экспортирован в Word', 'success');
    } catch (error) {
      console.error('Error exporting draft to docx:', error);
      showToast('Ошибка при экспорте в Word', 'error');
    }
  };

  return (
    <div className="modal-overlay document-create-modal-overlay" onClick={onClose}>
      <div className={`modal document-create-modal${showEditor ? ' is-editor-fullscreen' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {showEditor ? 'Редактор документа' : 'Создание документа'}
            {showEditor && (
              <div style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                {showPreviewMode ? 'Режим предпросмотра' : 'Режим редактирования'}
              </div>
            )}
          </h3>
          {showEditor && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                className={`btn ${showPreviewMode ? 'btn-secondary' : 'btn-info'}`}
                onClick={() => setShowPreviewMode(!showPreviewMode)}
                style={{ fontSize: '0.8rem', padding: '4px 8px' }}
              >
                {showPreviewMode ? 'Редактирование' : 'Предпросмотр'}
              </button>
            </div>
          )}
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className={`modal-body${showEditor ? ' modal-body-editor' : ''}`}>
          {showKommersantPartyModal && !showEditor && (
            <div style={{ border: '1px solid #d9dee8', borderRadius: '8px', padding: '16px', background: '#fff' }}>
              <h4>Отчетные бухгалтерские документы за публикацию оформить на:</h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '8px',
                  marginTop: '12px'
                }}
              >
                {kommersantPartyOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`btn ${kommersantPartyKind === option.key ? 'btn-primary' : 'btn-outline-primary'}`}
                    disabled={option.disabled}
                    onClick={() => handleSelectKommersantParty(option.key)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '4px',
                      justifyContent: 'center',
                      textAlign: 'left',
                      whiteSpace: 'normal',
                      padding: '10px 12px',
                      minHeight: '64px',
                      border: kommersantPartyKind === option.key ? '1px solid #0d6efd' : '1px solid #9fbdf5',
                      background: kommersantPartyKind === option.key ? '#0d6efd' : '#ffffff',
                      color: kommersantPartyKind === option.key ? '#ffffff' : '#0d3b8f'
                    }}
                  >
                    <strong>{option.label}</strong>
                    <div
                      style={{
                        fontSize: '0.85rem',
                        color: kommersantPartyKind === option.key ? 'rgba(255,255,255,0.92)' : '#4f6284'
                      }}
                    >
                      {option.name || 'Не заполнено в карточке торгов'}
                    </div>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#5c6f91' }}>
                Выберите один вариант, редактор откроется автоматически.
              </div>
            </div>
          )}

          {showEditor && (
            <div className="document-editor-container">
              {showPreviewMode ? (
                <div className="document-preview-mode">
                  <h4>Предпросмотр документа:</h4>
                  <div
                    className="document-preview-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(generatePreviewWithHighlight().replace(/\n/g, '<br>')),
                    }}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      padding: '15px',
                      minHeight: '400px',
                      backgroundColor: '#fff',
                      lineHeight: '1.6',
                      maxHeight: '60vh',
                      overflowY: 'auto'
                    }}
                  />
                  <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
                    <strong>Примечание:</strong> Поля, выделенные желтым цветом, не были автоматически заполнены из параметров торгов.
                  </div>
                </div>
              ) : (
                <DocumentEditor
                  document={{
                    id: null,
                    name: documentTitle || 'Новый документ',
                    content: editorContent
                  }}
                  templateName={templates.find(t => String(t.id) === String(selectedTemplate))?.name}
                  onExportWord={(documentData) => handleExportDraftDocx(documentData)}
                  onSave={async (documentId, documentData, { exit = false } = {}) => {
                    // Обновляем содержимое в редакторе
                    setEditorContent(documentData.content);
                    setDocumentTitle(documentData.title);

                    // Сохраняем документ в базу данных
                    await handleSaveDocument({
                      title: documentData.title,
                      content: documentData.content,
                      exit,
                    });
                  }}
                  onCancel={() => {
                    setShowEditor(false);
                    setEditorContent('');
                    setEditorHeaderContent('');
                    setEditorFooterContent('');
                    setEditorHeaderGapPx(12);
                    setEditorFooterGapPx(12);
                    setCreatedDocumentId(null);
                    setShowPreviewMode(false);
                  }}
                />
              )}
            </div>
          )}

          {showWarning && !showEditor && !showKommersantPartyModal && (
            <div className="warning-message">
              <h4>Предупреждение</h4>
              <p>В карточке торгов не заполнены следующие поля:</p>
              <ul>
                {missingFields.map((field, index) => (
                  <li key={index}>{field}</li>
                ))}
              </ul>
              <p>Возможно, не все переменные будут автоматически подставлены в документ.</p>
              <div className="warning-actions">
                <button className="btn btn-warning" onClick={handleForceCreate} disabled={loading}>
                  {loading ? 'Подготовка...' : 'Продолжить создание документа'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowWarning(false)}>
                  Отмена
                </button>
              </div>
            </div>
          )}

          {showDepositEtpNoticeModal && !showEditor && !showKommersantPartyModal && (
            <div className="warning-message">
              <h4>Внимание</h4>
              <p>Обратите внимание, что в карточке торга используется р/с электронной площадки. Форму договора о задатке рекоммендуется запросить у оператора ЭТП.</p>
              <div className="warning-actions">
                <button
                  className="btn btn-warning"
                  onClick={async () => {
                    setShowDepositEtpNoticeModal(false);
                    await proceedCreate({ skipDepositEtpNotice: true });
                  }}
                  disabled={loading}
                >
                  {loading ? 'Подготовка...' : 'Продолжить'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowDepositEtpNoticeModal(false)}>
                  Отмена
                </button>
              </div>
            </div>
          )}

          {!showWarning && !showDepositEtpNoticeModal && !showEditor && !showKommersantPartyModal && (
            <>
              <div className="form-group">
                <label>Выберите шаблон:</label>
                <div className="document-template-grid">
                  {standardCards.map((card) => {
                    const template = card.template;
                    const isSelected = template && String(template.id) === String(selectedTemplate);
                    return (
                      <button
                        key={card.key}
                        type="button"
                        className={`document-template-card ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => selectTemplate(template)}
                        disabled={!template}
                        title={template ? template.name : card.disabledHint}
                      >
                        <div className="document-template-icon">{renderTemplateIcon(card.icon)}</div>
                        <div className="document-template-title">{card.title}</div>
                        {(template?.template_type_name || card.badgeText) && (
                          <div className="document-template-badge">{template?.template_type_name || card.badgeText}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="document-template-divider">
                  <span>или</span>
                </div>
                <button
                  type="button"
                  className="document-template-custom-btn"
                  onClick={() => setShowUserTemplates((prev) => !prev)}
                >
                  Пользовательские шаблоны
                </button>
                {showUserTemplates && (
                  <div className="document-template-custom-list">
                    {userTemplates.length === 0 ? (
                      <div className="document-template-empty">Пользовательские шаблоны не найдены</div>
                    ) : (
                      userTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          className={`document-template-custom-item ${String(template.id) === String(selectedTemplate) ? 'is-selected' : ''}`}
                          onClick={() => selectTemplate(template)}
                        >
                          <span>{template.name}</span>
                          {template.template_type_name && (
                            <span className="document-template-custom-type">{template.template_type_name}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Название документа:</label>
                <input
                  type="text"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="Например: Уведомление о торгах №123"
                  className="form-control"
                />
              </div>
            </>
          )}
        </div>
        {!showWarning && !showDepositEtpNoticeModal && !showKommersantPartyModal && (
          <div className="modal-footer">
            {showEditor ? (
              <>
                <button
                  className="btn btn-success"
                  onClick={() => handleSaveDocument({ exit: false })}
                  disabled={loading}
                >
                  {loading ? 'Сохранение...' : 'Сохранить документ'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditor(false);
                    setEditorContent('');
                    setEditorHeaderContent('');
                    setEditorFooterContent('');
                    setEditorHeaderGapPx(12);
                    setEditorFooterGapPx(12);
                    setCreatedDocumentId(null);
                    setShowPreviewMode(false);
                  }}
                >
                  Назад
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  onClick={handleCreate}
                  disabled={loading}
                >
                  {loading ? 'Подготовка...' : 'Создать документ'}
                </button>
                <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DocumentsListModal = ({ documents, onClose, onViewDocument, auction, debtors, organizers, lots, etps, onDocumentDeleted }) => {
  const [viewingDocument, setViewingDocument] = useState(null);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const showConfirmDialog = (title, message, onConfirm) => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm
    });
  };

  const handleDeleteDocument = async (docId) => {
    showConfirmDialog(
      'Удаление документа',
      'Вы действительно хотите удалить этот документ?',
      async () => {
        try {
          await apiService.deleteDocument(docId);
          showToast('Документ успешно удален', 'success');
          if (onDocumentDeleted) {
            onDocumentDeleted();
          }
        } catch (error) {
          console.error('Error deleting document:', error);
          showToast('Ошибка при удалении документа', 'error');
        }
      }
    );
  };


  // Функция скачивания документа в формате Word
  const handleDownloadWord = async (docId, docTitle) => {
    try {
      const documentResponse = await apiService.getDocument(docId);
      const parsedDoc = extractTemplateMeta(documentResponse.data?.content || '');
      const documentContent = parsedDoc.content || '';
      const headerContent = documentResponse.data?.header_content || '';
      const footerContent = documentResponse.data?.footer_content || '';

      // Подставляем переменные из данных торгов
      const variablesData = getDocumentVariablesData(auction, debtors, organizers, lots, etps);
      const processedContent = substituteTemplateVariables(documentContent, {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData
      });
      const processedHeaderContent = substituteTemplateVariables(headerContent || parsedDoc.header_content || '', {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData,
      });
      const processedFooterContent = substituteTemplateVariables(footerContent || parsedDoc.footer_content || '', {
        auction,
        debtors,
        organizers,
        lots,
        etps,
        variablesData,
      });

      const { margins, bodyHTML } = extractMarginsAndBody(processedContent);
      const htmlContent = bodyHTML || processedContent;

      // Формируем имя файла только из названия документа
      const fileName = `${docTitle}.docx`;

      await exportToDocx(htmlContent, fileName.replace(/\.docx$/i, ''), margins || {}, {
        headerContent: processedHeaderContent,
        footerContent: processedFooterContent,
        headerGapPx: parsedDoc.header_gap_px,
        footerGapPx: parsedDoc.footer_gap_px,
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      showToast('Ошибка при скачивании документа', 'error');
    }
  };

  // Обработчик Escape для закрытия модального окна
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !viewingDocument) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    // Добавляем обработчик только если нет открытого viewer
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, viewingDocument]);

  const handleViewDocument = async (document) => {
    try {
      // Загружаем полный документ с содержимым
      const fullDocumentResponse = await apiService.getDocument(document.id);
      setViewingDocument(fullDocumentResponse.data);
    } catch (error) {
      console.error('Error loading full document:', error);
    }
  };

  const handleCloseViewer = () => {
    setViewingDocument(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'template_name':
        aValue = a.template_name.toLowerCase();
        bValue = b.template_name.toLowerCase();
        break;
      case 'created_date':
        aValue = new Date(a.created_date);
        bValue = new Date(b.created_date);
        break;
      case 'updated_date':
        aValue = new Date(a.updated_date);
        bValue = new Date(b.updated_date);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatDate = (dateString) => {
    return formatLocalDateTime(dateString);
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal documents-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Документы торгов</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {documents.length === 0 ? (
            <div className="empty-state">
              <p>У этих торгов пока нет документов</p>
            </div>
          ) : (
            <table className="documents-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}></th>
                  <th
                    className={`sortable ${sortField === 'title' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('title')}
                  >
                    Название
                  </th>
                  <th
                    className={`sortable ${sortField === 'template_name' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('template_name')}
                  >
                    Шаблон
                  </th>
                  <th
                    className={`sortable ${sortField === 'created_date' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('created_date')}
                  >
                    Создан
                  </th>
                  <th
                    className={`sortable ${sortField === 'updated_date' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('updated_date')}
                  >
                    Изменен
                  </th>
                  <th className="document-table-actions">Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedDocuments.map((document) => (
                  <tr
                    key={document.id}
                    onClick={() => handleViewDocument(document)}
                  >
                    <td>
                      <div className="word-icon-compact">
                        <div className="word-icon-header">
                          <div className="word-ribbon"></div>
                          <div className="word-ribbon"></div>
                          <div className="word-ribbon"></div>
                        </div>
                        <div className="word-icon-body">
                          <div className="word-letter">W</div>
                        </div>
                        <div className="word-icon-corner"></div>
                      </div>
                    </td>
                    <td>
                      <div className="document-table-info">
                        <div className="document-table-title">{document.title}</div>
                      </div>
                    </td>
                    <td>
                      <div className="document-table-template">{document.template_name}</div>
                    </td>
                    <td>
                      <div className="document-table-date">{formatDate(document.created_date)}</div>
                    </td>
                    <td>
                      <div className="document-table-date">{formatDate(document.updated_date)}</div>
                    </td>
                    <td className="document-table-actions">
                      <div className="card-actions documents-table-actions">
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadWord(document.id, document.title);
                          }}
                          title="Скачать в формате Word"
                        >
                          ⬇
                        </button>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(document.id);
                          }}
                          title="Удалить документ"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Модальное окно просмотра документа внутри списка */}
      {viewingDocument && (
        <div className="modal-overlay" onClick={handleCloseViewer}>
          <div className="modal document-viewer-wide-modal" onClick={(e) => e.stopPropagation()}>
            <DocumentEditor
              document={{
                id: viewingDocument.id,
                name: viewingDocument.title,
                content: viewingDocument.content
              }}
              templateName={viewingDocument.template_name}
              noOverlay={true}
              onSave={async (documentId, documentData) => {
                try {
                  await apiService.patchDocument(documentId, documentData);
                  // Перезагружаем список документов
                  const response = await apiService.getDocument(documentId);
                  setViewingDocument(response.data);
                  showToast('Документ успешно сохранен', 'success');
                } catch (error) {
                  console.error('Error saving document:', error);
                  showToast('Ошибка при сохранении документа', 'error');
                }
              }}
              onCancel={handleCloseViewer}
            />
          </div>
        </div>
      )}

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <UnsavedChangesPopover
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title || 'Подтверждение'}
          message={confirmDialog.message}
          cancelText="Отмена"
          confirmText="Удалить"
          onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
          onConfirm={() => {
            confirmDialog.onConfirm();
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          }}
          zIndex={12000}
        />
      )}
      <Toast toast={toast} />
    </div>
  );
};

const LotsModal = ({ auctionId, lots, showLotForm, setShowLotForm, onAddLot, onClose }) => {
  // Стабильная функция для закрытия формы лота
  const handleCloseLotForm = useCallback(() => {
    setShowLotForm(false);
  }, [setShowLotForm]);

  // Обработчик Escape для закрытия модального окна
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
      }
    };

    window.document.addEventListener('keydown', handleEscape, true);
    return () => {
      window.document.removeEventListener('keydown', handleEscape, true);
    };
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Лоты для торгов #{auctionId}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="lots-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowLotForm(!showLotForm)}
            >
              {showLotForm ? 'Отменить' : 'Добавить лот'}
            </button>
          </div>

          {showLotForm && (
            <LotFormWrapper
              onSubmit={onAddLot}
              onCancel={handleCloseLotForm}
              showLotForm={showLotForm}
            />
          )}

          <div className="lots-list">
            {lots.length === 0 ? (
              <p>Лоты не добавлены</p>
            ) : (
              lots.map(lot => (
                <div key={lot.id} className="lot-item">
                  <h4>{lot.name}</h4>
                  <p>{lot.description}</p>
                  <div className="lot-price">
                    <strong>Начальная цена: {lot.start_price?.toLocaleString('ru-RU')} ₽</strong>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Глобальный обработчик НЕ управляется React вообще
window.__lotFormEscapeHandler = null;
window.__lotFormCallbacks = {};

const LotFormWrapper = ({ onSubmit, onCancel, showLotForm }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Регистрируем callback'и глобально
  useEffect(() => {
    window.__lotFormCallbacks.onCancel = onCancel;
    window.__lotFormCallbacks.setShowConfirmDialog = setShowConfirmDialog;
  });

  // Создаем ЕДИНСТВЕННЫЙ глобальный обработчик при монтировании
  useEffect(() => {
    if (window.__lotFormEscapeHandler) {
      return;
    }

    window.__lotFormEscapeHandler = (e) => {
      if (e.key === 'Escape') {
        // Проверяем, что форма лота действительно открыта
        const lotForm = document.querySelector('.lot-form');
        if (!lotForm) return; // Форма не открыта

        // Проверяем, открыт ли уже диалог подтверждения
        const confirmDialog = document.querySelector('.confirm-dialog, .unsaved-changes-popover');
        if (confirmDialog) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          if (window.__lotFormCallbacks.setShowConfirmDialog) {
            window.__lotFormCallbacks.setShowConfirmDialog(false);
          }
          return false;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const nameInput = lotForm.querySelector('input[type="text"]');
        const descriptionInput = lotForm.querySelector('textarea');
        const priceInput = lotForm.querySelector('input[type="number"]');

        const hasData = (nameInput && nameInput.value.trim()) ||
                       (descriptionInput && descriptionInput.value.trim()) ||
                       (priceInput && priceInput.value.trim());


        if (hasData && window.__lotFormCallbacks.setShowConfirmDialog) {
          window.__lotFormCallbacks.setShowConfirmDialog(true);
        } else if (window.__lotFormCallbacks.onCancel) {
          window.__lotFormCallbacks.onCancel();
        }
        return false;
      }
    };

    document.addEventListener('keydown', window.__lotFormEscapeHandler, { capture: true });

    // НЕ УДАЛЯЕМ в cleanup - оставляем глобально!
    return () => {
    };
  }, []); // ПУСТОЙ массив

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    if (window.__lotFormCallbacks.onCancel) {
      window.__lotFormCallbacks.onCancel();
    }
  };

  return (
    <div>
      <LotForm onSubmit={onSubmit} onCancel={onCancel} />

      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        message="У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно? Все изменения будут потеряны."
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={10000}
      />
    </div>
  );
};

const LotForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_price: ''
  });

  const normalizeStartPriceInput = (value) => {
    const raw = String(value ?? '').replace(',', '.');
    if (!raw) return '';
    const cleaned = raw.replace(/[^\d.]/g, '');
    const hasDot = cleaned.includes('.');
    const [wholeRaw = '', ...fractionParts] = cleaned.split('.');
    const whole = wholeRaw.replace(/^0+(?=\d)/, '');
    const fraction = fractionParts.join('').slice(0, 2);
    if (hasDot) return `${whole || '0'}.${fraction}`;
    return whole;
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: '', description: '', start_price: '' });
  };

  const handleCancel = () => {
    onCancel(); // Просто вызываем onCancel - проверка данных теперь в wrapper
  };

  return (
    <div className="lot-form">
      <h4>Добавление лота</h4>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Наименование лота:</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </div>
        <div className="form-group">
          <label>Описание:</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={4}
            required
          />
        </div>
        <div className="form-group">
          <label>Начальная цена (₽):</label>
          <input
            type="number"
            step="0.01"
            value={formData.start_price}
            onChange={(e) => setFormData({...formData, start_price: normalizeStartPriceInput(e.target.value)})}
            required
          />
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-sm">
            Добавить лот
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCancel}>
            Отменить
          </button>
        </div>
      </form>
    </div>
  );
};

const AttachLotModal = ({ allLots, attachedLots, allAuctions, currentAuctionId, onAttach, onClose, isAdmin = false, lotColWidths, onStartResizeLotCol }) => {

  // Обработка Escape с приоритетом
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
        return false;
      }
    };

    // Используем захватывающую фазу с максимальным приоритетом
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onClose]);

  // Функция для получения информации о торгах по ID
  const getAuctionInfo = (auctionId) => {
    const auction = (allAuctions || []).find(a => a.id === auctionId);
    return auction ? getAuctionTitleLabel(auction) : `Торги #${auctionId}`;
  };

  // Функция для извлечения ID торгов из auction_info
  const getAuctionIdFromInfo = (auctionInfo) => {
    if (!auctionInfo) return null;
    // Извлекаем ID из строки вида "Торги 3 - ..."
    const match = auctionInfo.match(/Торги (\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  const getLotAuctionId = (lot) => {
    const direct = lot?.auction;
    if (direct !== null && direct !== undefined && String(direct).trim() !== '') {
      const parsed = Number.parseInt(String(direct), 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return getAuctionIdFromInfo(lot?.auction_info);
  };

  // Функция для проверки доступности лота (свободен ли он для прикрепления)
  const isLotAvailable = (lot) => {
    const auctionId = getLotAuctionId(lot);
    return !auctionId; // Лот доступен только если он не привязан ни к каким торгам
  };

  // Функция для проверки, был ли лот ранее привязан к текущему торгу но потом отвязан
  const wasDetachedFromCurrent = (lot) => {
    // Если в auction_info есть информация о том что лот был отвязан от текущих торгов
    return lot.auction_info &&
           (lot.auction_info.includes('<method-wrapper') ||
            lot.auction_info === 'null' ||
            lot.auction_info.trim() === '');
  };

  // Функция для проверки, привязан ли лот к текущим торгам
  const isAttachedToCurrentAuction = (lot) => {
    return (attachedLots || []).some(attachedLot => attachedLot.id === lot.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(price);
  };

  return (
    <div className="modal-overlay">
      <div className="modal attach-modal" style={{ width: '90%', maxWidth: '1200px' }}>
        <div className="modal-header">
          <h3>Прикрепить существующий лот</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {(allLots || []).length === 0 ? (
            <div className="empty-state">
              <p>Нет лотов в системе</p>
              <small>Сначала создайте лоты</small>
            </div>
          ) : (
            <div className="lots-table-container" style={{ width: '100%', overflow: 'auto' }}>
              <table className={`lots-table ${isAdmin ? 'lots-table--resizable' : ''}`} style={{ width: '100%', minWidth: '1000px', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: lotColWidths?.name }} />
                  <col style={{ width: lotColWidths?.description }} />
                  <col style={{ width: lotColWidths?.start_price }} />
                  <col style={{ width: lotColWidths?.category }} />
                  <col style={{ width: lotColWidths?.status }} />
                  <col style={{ width: lotColWidths?.actions }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <span className="lots-th-label">Наименование</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'name')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Описание</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'description')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Начальная цена</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'start_price')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Категория</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'category')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Статус</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'status')} />}
                    </th>
                    <th>
                      <span className="lots-th-label">Действия</span>
                      {isAdmin && <span className="col-resizer" role="presentation" onMouseDown={(e) => onStartResizeLotCol(e, 'actions')} />}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(allLots || []).map((lot) => {
                    // VERSION 2.0 CHECK
                    const available = isLotAvailable(lot);
                    const attachedToCurrent = isAttachedToCurrentAuction(lot);
                    const isUnavailable = !available && !attachedToCurrent; // Привязан к другим торгам


                    return (
                      <tr key={lot.id} className={isUnavailable ? 'lot-row-unavailable' : ''}>
                        <td className="lot-name">{lot.name}</td>
                        <td className="lot-description" title={lot.description || ''}>
                          {lot.description && lot.description.length > 50
                            ? `${lot.description.substring(0, 50)}...`
                            : (lot.description || 'Без описания')}
                        </td>
                        <td className="lot-price">{formatPrice(lot.start_price)}</td>
                        <td className="lot-category">
                          <span className={`category-badge ${lot.category}`}>
                            {lot.category === 'real_estate' ? 'Недвижимость' :
                             lot.category === 'transport' ? 'Транспорт' :
                             lot.category === 'equipment' ? 'Оборудование' :
                             lot.category === 'debt' ? 'Дебиторка' :
                             'Прочее'}
                          </span>
                        </td>
                        <td className="lot-status">
                          {attachedToCurrent ? (
                            <span className="status-current">Прикреплен к текущим торгам</span>
                          ) : getLotAuctionId(lot) ? (
                            <span className="status-attached">
                              Привязан к {getAuctionInfo(getLotAuctionId(lot))}
                            </span>
                          ) : (
                            <span className="status-free">Свободен</span>
                          )}
                        </td>
                        <td className="lot-actions" style={{ minWidth: '100px', padding: '8px', textAlign: 'center' }}>
                          <div className="lot-actions-inner">
                            {(() => {
                              if (attachedToCurrent) {
                                return (
                                  <span style={{ color: 'green', fontSize: '16px' }} title="Уже прикреплен к текущим торгам">
                                    ✓
                                  </span>
                                );
                              } else if (available || wasDetachedFromCurrent(lot)) {
                                return (
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => {
                                      onAttach(lot.id);
                                    }}
                                    title="Прикрепить лот"
                                    style={{ minWidth: '30px', padding: '2px 6px' }}
                                    type="button"
                                  >
                                    ✓
                                  </button>
                                );
                              } else {
                                return (
                                  <span className="action-disabled" style={{ color: 'gray', fontSize: '12px' }}>
                                    Привязан к другим торгам
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент диалога подтверждения с обработчиком Escape
const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Полностью блокируем событие
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Закрываем диалог
        onCancel();
        return false;
      }
    };

    // Используем захватывающую фазу с максимальным приоритетом
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={5000}
    />
  );
};

export default AuctionsList;
